import { DEFERRED_SOURCE_CHANGES_HEADERS, SOURCE_CHANGES_REFRESH_UNMOUNTED, buildSourceChangeCompletionToast, isAcceptableSourceChangeRefreshResult, isSourceChangeFinalPreview, sourceChangeMutationRefreshPeriods, sourceChangeMutationScopes } from "../utils/sourceChangeMutationCompletion.js";
import { completeLinkedOperationCapture, createOperationFeedbackCapture, sourceChangeCaptureResourceContext } from "../utils/operationFeedbackCapture.js";
import { canRefreshTruckSourceChange, formatTruckSourceChangesMenuPath, selectAutomaticTruckSourceChanges, truckSourceChangeFieldSection } from "../utils/truckSourceChangePresentation.js";
import { normalizeApplicationFeedback, applicationFeedbackMessage, isBusinessApplicationBlock } from "../utils/sourceChangeApplicationFeedback.js";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { useLocation, useNavigate, useNavigation, useRevalidator } from "react-router";
import useAccess from "./useAccess.jsx";
import usePlanningMutation from "./usePlanningMutation.js";
import useResultsWorkspaceInteractions from "./useResultsWorkspaceInteractions.js";
import useTripSelection from "./useTripSelection.js";
import { useUserUiPreferences } from "./useUserUiPreferences.js";
import { fetchWithSupabaseAuth } from "../lib/supabaseClient.js";
import { buildStatusNoticeAlertCaptureContext } from "../lib/dispatchAlertCaptureContext.js";
import { ACCESS_PERMISSION_KEYS, canManageDispatcherFreeze as accessCanManageDispatcherFreeze, canManageRouteAlternatives as accessCanManageRouteAlternatives } from "../lib/accessControl.js";
import { invalidatePageDataCacheEntries, writePageDataCacheEntry } from "./usePageData.js";
import { invalidateShellDataCache } from "./useShellData.js";
import { readDispatchDateFromSearch, writeLastDispatchContext } from "../utils/dispatchDateUrls.js";
import { clearResultsDetailViewSearchParam } from "../utils/resultsDetailViewNavigation.js";
import {
  normalizeSourceChangeResolution,
  normalizeSourceStaleCoverage,
  normalizeLoadingConstraintChanges,
  normalizeTruckRefreshRecovery,
  normalizeTruckChanges,
  readInitialDispatchSnapshots,
  readInitialPortalPublications,
  readInitialSourceChanges,
  readSourceChangesMutationResponse,
} from "../utils/resultsDispatchBootstrap.js";
import { setAssignmentOpenState } from "../utils/resultsFleetData.js";
import { dispatchPlanningStateChange, isPlanningStateChangeInScope, subscribePlanningStateChange } from "../utils/planningCrossViewSync.js";
import { syncResultsAfterPlanningMutation } from "../utils/resultsWorkspaceData.js";
import { clearAppStatusNoticeBySource, dispatchAppStatusNotice } from "../utils/appStatusNotice.js";
import {
  normalizeDispatchProtectAfterReassignment,
  normalizeDispatchReassignmentMode,
} from "../utils/dispatchReassignmentMode.js";
import { useToast } from "../components/ResultsPrimitives.jsx";
import { SOURCE_CHANGES_PROGRESS_STEPS, createToastProgress, createToastProgressReporter } from "../utils/toastProgress.js";
import {
  PORTAL_PUBLICATION_STATUSES,
  buildPortalPublicationInvalidationToast,
  fetchPortalPublications,
  normalizePortalPublicationDispatchPeriod,
  normalizePortalPublicationInvalidatedTruckIds,
  normalizePortalPublications,
} from "../lib/portalPublicationsApi.js";
import {
  isProtectedOrderAutoRefreshWaitingForFirstChecks,
  selectProtectedOrderAutoRefreshCandidates,
} from "../utils/protectedOrderAutoRefresh.js";
import {
  buildRemoveModifiedSourceChangeRequest,
  executeRemoveModifiedSourceChangeRequest,
} from "../utils/sourceChangeRemovalSelection.js";
import {
  executeExactSelectionSourceChangeRequest,
  markSourceChangesRefreshPerformed,
  wasSourceChangesRefreshPerformed,
  wasSourceChangesRetryAttempted,
} from "../utils/sourceChangeExactSelection.js";
import { executeLoadingConstraintSourceChangeRequest, readLoadingConstraintRecovery } from "../utils/loadingConstraintSourceChanges.js";
import {
  createSourceChangeMetadataSyncCoordinator,
  createSourceChangesRefreshCoordinator,
  SOURCE_CHANGES_REFRESH_SUPERSEDED,
} from "../utils/sourceChangesRefreshCoordinator.js";
import {
  buildSourceChangesPerformanceHeaders,
  classifySourceChangesPlanningEvent,
  createPerformanceCorrelationId,
  isSourceChangesActionPreviewFresh,
  resolveSourceChangesAggregateCount,
  sourceChangesAggregateCache,
} from "../utils/sourceChangesRefreshPolicy.js";
import {
  tryBeginPlanningUpdateActivity,
  PLANNING_UPDATE_BUSY_MESSAGE,
  cancelDeferredPlanningBackgroundWork,
  deferPlanningBackgroundWorkUntilIdle,
  getPlanningMutationActivityMessage,
  subscribePlanningMutationActivity,
} from "../utils/planningMutationActivity.js";
import { acquireSourceChangeMetadataSyncActivity } from "../utils/sourceChangeMetadataSyncActivity.js";
import { addressRefreshToast, pendingAddressOrderIds } from "../utils/sourceChangeAddressRefresh.js";

const RESULTS_LATEST_API_URL = "/api/results/latest";
const PROTECTED_ORDER_AUTO_REFRESH_ATTEMPT_STORAGE_PREFIX = "grain:protected-order-auto-refresh:";
const SOURCE_CHANGE_METADATA_SYNC_MAX_ATTEMPTS = 3;
const SOURCE_CHANGE_METADATA_SYNC_MAX_POST_ATTEMPTS = 2;
const SOURCE_CHANGE_MUTATION_PERFORMANCE_ACTIONS = Object.freeze({
  "import-new": "source_changes_import_new",
  "import-suspendable-errors": "source_changes_import_suspendable_errors",
  "remove-date-changed": "source_changes_remove_date_changed",
  "remove-modified": "source_changes_remove_modified",
  "repair-ghost-orders": "source_changes_remove_deleted",
  "restore-executions": "source_changes_restore_executions",
});

function normalizeInlineText(value) {
  return typeof value === "string" ? value.trim() : "";
}

function readRequestedSourceChangeRefreshPeriods(detail) {
  const rawPeriods = detail?.refreshSourceChangePeriods
    || detail?.refresh_source_change_periods;
  if (!Array.isArray(rawPeriods)) {
    return [];
  }
  return [...new Set(rawPeriods
    .map((period) => normalizeInlineText(period).toLowerCase())
    .filter((period) => period === "day" || period === "evening"))]
    .sort();
}

const SOURCE_CHANGE_TRUCK_REFRESH_OUTCOMES = new Set(["applied", "partial", "blocked"]);
// Changements faits dans le Gantt dont les cartes camion créées ou modifiées sont actualisées
// automatiquement, avec les sections de Mise à jour que chacun peut toucher.
const GANTT_FLEET_EDIT_SECTIONS = new Map([
  ["update_truck_driver_name", new Set(["driver"])],
  ["update_truck_tractor", new Set(["truck", "driver"])],
]);
// Une seule tentative automatique par révision de carte : un refus reste dans Mise à jour.
const automaticTruckChangeKey = (dispatchDate, dispatchPeriod, item) => `${dispatchDate}|${dispatchPeriod}|${item.truckId}|${item.changeRevision}`;
// Toast transmis par un changement du Gantt, terminé tel quel quand aucune actualisation camion ne le reprend.
const finishHandedOffToast = (updateToast, toast) => {
  if (toast?.id) updateToast(toast.id, { type: "success", message: toast.message, duration: 5000 });
  completeLinkedOperationCapture(toast?.capture, { toastTitle: "Changement enregistré", toastMessage: "Aucune actualisation camion lancée." });
};

function normalizeUniqueSourceChangeIds(value) {
  if (!Array.isArray(value)) {
    return null;
  }
  const normalized = value.map(normalizeInlineText);
  if (normalized.some((item) => !item) || new Set(normalized).size !== normalized.length) {
    return null;
  }
  return normalized.sort((left, right) => left.localeCompare(right, "fr", { sensitivity: "base" }));
}

function attachBlockedTruckRecoveriesToPayload(payload, blockedTrucks = []) {
  const sourceChanges = payload?.source_changes;
  const truckChanges = sourceChanges?.truck_changes;
  const details = truckChanges?.details;
  if (!Array.isArray(details) || !Array.isArray(blockedTrucks) || blockedTrucks.length === 0) {
    return payload;
  }
  const recoveryByTruckRevision = new Map(blockedTrucks
    .filter((item) => item?.recovery)
    .map((item) => [
      `${normalizeInlineText(item.truckId)}|${normalizeInlineText(item.changeRevision)}`,
      item.recovery,
    ]));
  if (recoveryByTruckRevision.size === 0) {
    return payload;
  }
  return {
    ...payload,
    source_changes: {
      ...sourceChanges,
      truck_changes: {
        ...truckChanges,
        details: details.map((detail) => {
          const key = `${normalizeInlineText(detail?.truck_id || detail?.truckId)}|${normalizeInlineText(
            detail?.change_revision || detail?.changeRevision,
          )}`;
          const recovery = recoveryByTruckRevision.get(key);
          return recovery ? { ...detail, recovery } : detail;
        }),
      },
    },
  };
}

function readSourceChangeTruckRefreshContract(payload, requestedTruckIds = []) {
  const source = payload && typeof payload === "object" && !Array.isArray(payload)
    ? payload
    : null;
  const outcome = normalizeInlineText(source?.outcome).toLowerCase();
  const mutationCounts = source?.mutation_counts;
  const requested = mutationCounts?.requested;
  const applied = mutationCounts?.applied;
  const blocked = mutationCounts?.blocked;
  const invalidatedTruckIds = source?.publication_invalidated_truck_ids;
  const refreshedTruckIds = source?.refreshed_truck_ids;
  const blockedTrucks = source?.blocked_trucks;
  const sourceChangesRefreshRequired = source?.source_changes_refresh_required === true;
  const normalizedRequestedTruckIds = Array.isArray(requestedTruckIds)
    ? requestedTruckIds.map(normalizeInlineText).filter(Boolean)
    : [];
  const selectedTruckIds = new Set(normalizedRequestedTruckIds);
  const normalizedRefreshedTruckIds = Array.isArray(refreshedTruckIds)
    ? refreshedTruckIds.map(normalizeInlineText)
    : null;
  const normalizedBlockedTrucks = Array.isArray(blockedTrucks)
    ? blockedTrucks.map((item) => {
        if (!item || typeof item !== "object" || Array.isArray(item)) {
          return null;
        }
        const recovery = normalizeTruckRefreshRecovery(item.recovery);
        const applicationFeedback = normalizeApplicationFeedback(
          item.application_feedback || item.applicationFeedback,
        );
        return {
          truckId: normalizeInlineText(item.truck_id || item.truckId),
          changeRevision: normalizeInlineText(item.change_revision || item.changeRevision),
          reasonCode: normalizeInlineText(item.reason_code || item.reasonCode),
          ...(recovery ? { recovery } : {}),
          ...(applicationFeedback ? { applicationFeedback } : {}),
          recoveryIsValid: item.recovery == null || recovery !== undefined,
        };
      })
    : null;
  const normalizedBlockedTruckIds = normalizedBlockedTrucks
    ? normalizedBlockedTrucks.map((item) => normalizeInlineText(item?.truckId))
    : null;
  const blockedTruckDetailsAreValid = Array.isArray(blockedTrucks)
    && normalizedBlockedTrucks.every((item) => (
      item?.truckId
      && item.changeRevision
      && item.reasonCode
      && item.recoveryIsValid === true
    ));
  const coveredTruckIds = new Set([
    ...(normalizedRefreshedTruckIds || []),
    ...(normalizedBlockedTruckIds || []),
  ]);
  const hasValidOutcomeCounts = (
    (outcome === "applied" && applied === requested && blocked === 0)
    || (outcome === "partial" && applied > 0 && blocked > 0)
    || (outcome === "blocked" && applied === 0 && blocked === requested)
  );
  if (
    !source
    || !SOURCE_CHANGE_TRUCK_REFRESH_OUTCOMES.has(outcome)
    || !mutationCounts
    || typeof mutationCounts !== "object"
    || !Number.isInteger(requested)
    || !Number.isInteger(applied)
    || !Number.isInteger(blocked)
    || requested < 1
    || applied < 0
    || blocked < 0
    || requested !== normalizedRequestedTruckIds.length
    || applied + blocked !== requested
    || !hasValidOutcomeCounts
    || !Array.isArray(invalidatedTruckIds)
    || invalidatedTruckIds.length !== 0
    || !normalizedRefreshedTruckIds
    || normalizedRefreshedTruckIds.some((truckId) => !truckId || !selectedTruckIds.has(truckId))
    || new Set(normalizedRefreshedTruckIds).size !== normalizedRefreshedTruckIds.length
    || normalizedRefreshedTruckIds.length !== applied
    || !normalizedBlockedTruckIds
    || !blockedTruckDetailsAreValid
    || normalizedBlockedTruckIds.some((truckId) => (
      !selectedTruckIds.has(truckId) || normalizedRefreshedTruckIds.includes(truckId)
    ))
    || new Set(normalizedBlockedTruckIds).size !== normalizedBlockedTruckIds.length
    || normalizedBlockedTruckIds.length !== blocked
    || coveredTruckIds.size !== selectedTruckIds.size

  ) {
    throw new Error("La réponse de mise à jour des camions est invalide. Le planning va être réconcilié.");
  }
  return {
    applied,
    blocked,
    blockedTrucks: normalizedBlockedTrucks.map(({ recoveryIsValid: _recoveryIsValid, ...item }) => item),
    outcome,
    refreshedTruckIds: normalizedRefreshedTruckIds,
    requested,
    sourceChangesRefreshRequired,
  };
}

function readSourceChangeTruckRecoveryContract(payload, requestedTruckId = "") {
  const truckId = normalizeInlineText(requestedTruckId);
  const refreshContract = readSourceChangeTruckRefreshContract(payload, [truckId]);
  const suspendedOrderIds = normalizeUniqueSourceChangeIds(payload?.suspended_order_ids);
  const suspendedDeliveryIds = normalizeUniqueSourceChangeIds(payload?.suspended_delivery_ids);
  if (
    !truckId
    || !suspendedOrderIds
    || !suspendedDeliveryIds
    || (
      refreshContract.outcome === "applied"
      && (suspendedOrderIds.length === 0 || suspendedDeliveryIds.length === 0)
    )
  ) {
    throw new Error("La réponse de récupération du camion est invalide. Le planning va être réconcilié.");
  }
  return {
    ...refreshContract,
    suspendedDeliveryIds,
    suspendedOrderIds,
  };
}

function readSourceChangeTruckRejectionContract(payload, requestedTruckId = "") {
  const source = payload && typeof payload === "object" && !Array.isArray(payload)
    ? payload
    : null;
  const truckId = normalizeInlineText(requestedTruckId);
  const rejectedTruckId = normalizeInlineText(source?.rejected_truck_id);
  const rejected = source?.mutation_counts?.rejected;
  const invalidatedTruckIds = source?.publication_invalidated_truck_ids;
  if (
    !source
    || normalizeInlineText(source.outcome).toLowerCase() !== "rejected"
    || !truckId
    || rejectedTruckId !== truckId
    || rejected !== 1
    || !Array.isArray(invalidatedTruckIds)
    || invalidatedTruckIds.length !== 0

  ) {
    throw new Error("La réponse de rejet du camion est invalide. L’aperçu va être réconcilié.");
  }
  return { rejected: 1, rejectedTruckId };
}

function buildSourceChangePlanningResultPayload(resultPayload, planningDiagnostics) {
  if (!resultPayload || typeof resultPayload !== "object" || Array.isArray(resultPayload)) {
    return resultPayload;
  }
  if (!planningDiagnostics || typeof planningDiagnostics !== "object" || Array.isArray(planningDiagnostics)) {
    return resultPayload;
  }

  return {
    ...resultPayload,
    _planning_diagnostics: planningDiagnostics,
    diagnostics: planningDiagnostics,
    last_reassignment_diagnostics: planningDiagnostics,
  };
}

function resolveNonRenderableTripActionError(error, { action = "repair", orderId = "" } = {}) {
  const reasonCode = normalizeInlineText(
    error?.code
      || error?.payload?.reason_code
      || error?.payload?.detail?.reason_code,
  ).toLowerCase();
  if (["stale_conflict", "runtime_revision_conflict"].includes(reasonCode)) {
    return {
      message: "La tournée a changé. Aperçu actualisé.",
      refresh: true,
      title: "Aperçu actualisé",
      type: "info",
    };
  }
  if (reasonCode === "published_truck") {
    return {
      message: "Tournée publiée. Dépubliez-la avant de la modifier.",
      refresh: true,
      title: "Tournée publiée",
      type: "warning",
    };
  }
  if (reasonCode === "dispatch_day_closed") {
    return {
      message: "Cette journée est fermée. Rien n’a été modifié.",
      refresh: true,
      title: "Journée fermée",
      type: "warning",
    };
  }
  if (reasonCode === "conflict_not_targetable") {
    return {
      message: orderId
        ? `Données incomplètes. Corrigez ${orderId} dans Data Manager, puis réessayez.`
        : "Données incomplètes. Corrigez la commande dans Data Manager, puis réessayez.",
      refresh: true,
      title: "Commande non identifiable",
      type: "error",
    };
  }
  if (reasonCode === "repair_blocked") {
    return {
      message: "Réparation automatique impossible. Rien n’a été modifié.",
      refresh: true,
      title: "Réparation impossible",
      type: "error",
    };
  }
  if ((Number(error?.status) || 0) === 0 || Number(error?.status) >= 500) {
    return {
      message: "Service indisponible. Rien n’a été modifié. Réessayez.",
      refresh: true,
      title: action === "suspend" ? "Suspension impossible" : "Réparation impossible",
      type: "error",
    };
  }
  return {
    message: error instanceof Error && error.message
      ? error.message
      : action === "suspend"
        ? "Impossible de placer la commande dans Suspendues. Rien n’a été modifié."
        : "Réparation automatique impossible. Rien n’a été modifié.",
    refresh: false,
    title: action === "suspend" ? "Suspension impossible" : "Réparation impossible",
    type: "error",
  };
}

function publishSourceChangesStatusNotice({ message = "", planningDiagnostics = null, status, title = "" }) {
  const isRunning = status === "running";
  dispatchAppStatusNotice({
    eyebrow: "M-à-J commandes",
    ...(isRunning ? { startedAt: new Date().toISOString() } : { finishedAt: new Date().toISOString() }),
    message,
    planningDiagnostics,
    source: "planning",
    status,
    statusLabel: isRunning ? "Traitement en cours" : status === "completed" ? "Terminé" : status === "partial" ? "Partiel" : "Action requise",
    title,
  });
}

function formatRemoveModifiedSuccessMessage(totalCount, alreadySuspendedCount) {
  const normalizedTotalCount = Number.isFinite(Number(totalCount))
    ? Math.max(0, Math.round(Number(totalCount)))
    : 0;
  const normalizedAlreadySuspendedCount = Number.isFinite(Number(alreadySuspendedCount))
    ? Math.min(normalizedTotalCount, Math.max(0, Math.round(Number(alreadySuspendedCount))))
    : 0;
  if (normalizedAlreadySuspendedCount === 0) {
    return `${normalizedTotalCount} commande(s) mise(s) en Suspendues.`;
  }

  const transferredCount = normalizedTotalCount - normalizedAlreadySuspendedCount;
  const messages = [];
  if (transferredCount === 1) {
    messages.push("1 commande transférée vers Suspendues");
  } else if (transferredCount > 1) {
    messages.push(`${transferredCount} commandes transférées vers Suspendues`);
  }
  if (normalizedAlreadySuspendedCount === 1) {
    messages.push("1 commande déjà suspendue actualisée sur place");
  } else {
    messages.push(`${normalizedAlreadySuspendedCount} commandes déjà suspendues actualisées sur place`);
  }
  return `${messages.join(" et ")}.`;
}

function isProtectedOrderAutoRefreshDispatchOpen(resultsData = null, workspaceState = null, dispatchDate = "") {
  const safeResultsData = resultsData && typeof resultsData === "object" ? resultsData : {};
  const safeDispatchDate = normalizeInlineText(dispatchDate);
  const planningSummary = workspaceState?.planningSummary && typeof workspaceState.planningSummary === "object"
    ? workspaceState.planningSummary
    : safeResultsData?.planning_summary && typeof safeResultsData.planning_summary === "object"
      ? safeResultsData.planning_summary
      : {};
  const dispatchDayRecord = (Array.isArray(safeResultsData.dispatch_days) ? safeResultsData.dispatch_days : [])
    .find((item) => normalizeInlineText(item?.dispatch_date) === safeDispatchDate);
  const dispatchDayStates = [
    dispatchDayRecord?.dispatch_day_state,
    planningSummary.dispatch_day_state,
  ].map((value) => normalizeInlineText(value).toLowerCase());

  return dispatchDayRecord?.is_closed !== true
    && planningSummary.is_dispatch_day_closed !== true
    && !dispatchDayStates.some((value) => value === "closed" || value === "archived");
}

function isProtectedOrderAutoRefreshTruckPublished(portalPublications = null, truckId = "") {
  const safeTruckId = normalizeInlineText(truckId);
  return Boolean(safeTruckId) && (Array.isArray(portalPublications?.routes) ? portalPublications.routes : [])
    .some((route) => (
      normalizeInlineText(route?.truckId || route?.truck_id) === safeTruckId
      && normalizeInlineText(route?.status).toLowerCase() === PORTAL_PUBLICATION_STATUSES.published
    ));
}

function hasStoredProtectedOrderAutoRefreshAttempt(attemptKey = "") {
  const safeAttemptKey = normalizeInlineText(attemptKey);
  if (!safeAttemptKey || typeof window === "undefined") {
    return false;
  }
  try {
    return window.sessionStorage.getItem(`${PROTECTED_ORDER_AUTO_REFRESH_ATTEMPT_STORAGE_PREFIX}${safeAttemptKey}`) === "1";
  } catch {
    return false;
  }
}

function storeProtectedOrderAutoRefreshAttempt(attemptKey = "") {
  const safeAttemptKey = normalizeInlineText(attemptKey);
  if (!safeAttemptKey || typeof window === "undefined") {
    return;
  }
  try {
    window.sessionStorage.setItem(`${PROTECTED_ORDER_AUTO_REFRESH_ATTEMPT_STORAGE_PREFIX}${safeAttemptKey}`, "1");
  } catch {
    // Le Set mémoire reste la garantie one-shot lorsque le stockage navigateur est indisponible.
  }
}

const SOURCE_CHANGES_SNAPSHOT_UNAVAILABLE_CODE = "source_changes_snapshot_unavailable";
const EVENING_PLANNING_UNAVAILABLE_MESSAGE = "Aucun planning Soir pour cette journée.";
const SOURCE_CHANGES_SNAPSHOT_UNAVAILABLE_MESSAGE = (
  "Aucun résultat d'optimisation n'est disponible pour comparer les sources."
);

function isSourceChangesSnapshotUnavailableError(error) {
  if (!error || Number(error.status) !== 404) {
    return false;
  }
  const detail = error?.payload?.detail && typeof error.payload.detail === "object"
    ? error.payload.detail
    : null;
  const code = normalizeInlineText(
    error.code
    || detail?.reason_code
    || detail?.code
    || error?.payload?.reason_code
    || error?.payload?.code,
  );
  if (code === SOURCE_CHANGES_SNAPSHOT_UNAVAILABLE_CODE) {
    return true;
  }
  return normalizeInlineText(error.message) === SOURCE_CHANGES_SNAPSHOT_UNAVAILABLE_MESSAGE;
}

function buildEmptySourceChangesState(dispatchDate = "", dispatchPeriod = "day") {
  const normalizedDispatchDate = normalizeInlineText(dispatchDate);
  const normalizedDispatchPeriod = normalizePortalPublicationDispatchPeriod(dispatchPeriod);
  const lastVerifiedPendingCount = sourceChangesAggregateCache.read({
    dispatchDate: normalizedDispatchDate,
    dispatchPeriod: normalizedDispatchPeriod,
  });
  return {
    dispatchDate: normalizedDispatchDate,
    dispatchPeriod: normalizedDispatchPeriod,
    counts: { new: 0, modified_same_date: 0, loading_constraint_changes: 0, date_changed_remove: 0, missing_dispatch_orders: 0, removed_end_of_day_loads: 0, protected_execution: 0, blocking_protected_execution: 0, inconsistent_execution_removals: 0, integrity_conflicts: 0, metadata_sync: 0, total: 0, unresolved_total: 0 },
    details: { new: [], modified_same_date: [], loading_constraint_changes: [], date_changed_remove: [], missing_dispatch_orders: [], removed_end_of_day_loads: [], protected_execution: [], inconsistent_execution_removals: [], integrity_conflicts: [] },
    changeDomains: [],
    endOfDayLoadIntegrity: { status: "ok", counts: { checked: 0, blocking: 0 }, preview_revision: "", issues: [] },
    errorMessage: "",
    hasVerified: false,
    metadataSync: { details: [], orderIds: [], previewRevision: "" },
    loadingConstraintChanges: normalizeLoadingConstraintChanges(null),
    mutationPreviewRevision: "",
    previewRevision: "",
    resolution: normalizeSourceChangeResolution(null),
    runtimeRevision: "",
    staleCoverage: normalizeSourceStaleCoverage(null),
    suspendableErrors: { counts: { total: 0 }, details: [] },
    truckOutputPolicy: null,
    truckChanges: normalizeTruckChanges(null),
    unavailableMessage: "",
    isLoading: false,
    isMutating: false,
    isVerifying: false,
    lastVerifiedPendingCount,
    needsPlanningContextVerification: false,
    needsVerification: lastVerifiedPendingCount != null,
    planningContextVerificationErrorMessage: "",
    planningContextVerificationVersion: 0,
  };
}

function hasPendingSourceChangeMetadataSync(sourceChanges = null) {
  return Array.isArray(sourceChanges?.metadataSync?.orderIds)
    && sourceChanges.metadataSync.orderIds.length > 0;
}

function buildDispatchDateQueryUrl(pathname, dispatchDate, dispatchPeriod = "") {
  const safeDispatchDate = normalizeInlineText(dispatchDate);
  const safeDispatchPeriod = normalizeInlineText(dispatchPeriod);
  const searchParams = new URLSearchParams();
  if (safeDispatchDate) {
    searchParams.set("dispatch_date", safeDispatchDate);
  }
  if (safeDispatchPeriod) {
    searchParams.set("dispatch_period", normalizePortalPublicationDispatchPeriod(safeDispatchPeriod));
  }
  const queryString = searchParams.toString();
  return queryString ? `${pathname}?${queryString}` : pathname;
}

function buildEmptyDispatchRestoreSourceChangePreview() {
  return {
    counts: { new: 0, modified_same_date: 0, loading_constraint_changes: 0, date_changed_remove: 0, missing_dispatch_orders: 0, removed_end_of_day_loads: 0, protected_execution: 0, blocking_protected_execution: 0, inconsistent_execution_removals: 0, total: 0, unresolved_total: 0 },
    details: { new: [], modified_same_date: [], loading_constraint_changes: [], date_changed_remove: [], missing_dispatch_orders: [], removed_end_of_day_loads: [], protected_execution: [], inconsistent_execution_removals: [] },
  };
}

function buildEmptyDispatchRestoreExecutionPreservation() {
  return {
    blocking: false,
    counts: { preservable: 0, blocked: 0, total: 0 },
    details: { preservable: [], blocked: [] },
  };
}

function buildDispatchRestorePreviewFromPayload(payload) {
  const sourceChangePreview = payload?.source_changes && typeof payload.source_changes === "object"
    ? payload.source_changes
    : buildEmptyDispatchRestoreSourceChangePreview();
  return {
    ...sourceChangePreview,
    address_revalidation: payload?.address_revalidation && typeof payload.address_revalidation === "object"
      ? payload.address_revalidation
      : null,
    execution_preservation: payload?.execution_preservation && typeof payload.execution_preservation === "object"
      ? payload.execution_preservation
      : buildEmptyDispatchRestoreExecutionPreservation(),
    previewLoaded: true,
    related_dispatches: payload?.related_dispatches && typeof payload.related_dispatches === "object"
      ? payload.related_dispatches
      : null,
  };
}

function buildDispatchRestorePreviewError() {
  return {
    ...buildEmptyDispatchRestoreSourceChangePreview(),
    previewError: true,
    previewLoaded: true,
  };
}

async function fetchDispatchSnapshotRestorePreview(snapshotId, dispatchDate) {
  const response = await fetchWithSupabaseAuth(
    buildDispatchDateQueryUrl(`/api/planning/snapshots/${encodeURIComponent(snapshotId)}/restore-preview`, dispatchDate),
  );
  const payload = await parseJsonResponse(response, "Impossible de prévisualiser la restauration.");
  return buildDispatchRestorePreviewFromPayload(payload);
}

function readWorkspaceDispatchDate(workspaceStore) {
  const state = typeof workspaceStore?.getState === "function" ? workspaceStore.getState() : null;
  if (!state || typeof state !== "object") {
    return "";
  }

  return normalizeInlineText(state?.resultsData?.dispatch_date)
    || normalizeInlineText(state?.planningSummary?.dispatch_date);
}

function resolveSourceChangesDispatchDate(locationSearch, workspaceStore, initialDispatchDate = "") {
  return normalizeInlineText(readDispatchDateFromSearch(locationSearch))
    || normalizeInlineText(initialDispatchDate)
    || readWorkspaceDispatchDate(workspaceStore);
}

async function parseJsonResponse(response, fallbackMessage, capture = null) {
  let jsonReadFailed = false;
  const payload = await response.json().catch((error) => {
    jsonReadFailed = true;
    capture?.recordMutationJson("invalid", error);
    return {};
  });
  if (!jsonReadFailed) capture?.recordMutationJson("parsed", null, payload);
  if (!response.ok) {
    const detail = payload?.detail && typeof payload.detail === "object" && !Array.isArray(payload.detail)
      ? payload.detail
      : null;
    const message = [
      payload?.error,
      payload?.message,
      typeof payload?.detail === "string" ? payload.detail : "",
      detail?.error,
      detail?.message,
    ].map(normalizeInlineText).find(Boolean) || fallbackMessage;
    const error = new Error(message);
    error.name = "ApiResponseError";
    error.status = Number(response.status) || 0;
    error.code = normalizeInlineText(
      payload?.code
      || detail?.code
      || detail?.reason_code
      || payload?.reason_code,
    );
    error.payload = payload;
    throw error;
  }
  return payload;
}

function hasNonEmptyEndOfDayLoadOrderIds(detail, fieldName) {
  return Array.isArray(detail?.[fieldName])
    && detail[fieldName].some((orderId) => normalizeInlineText(orderId));
}

function isMixedOrphanEndOfDayLoadDetail(detail) {
  const reason = normalizeInlineText(detail?.ghost_reason).toLowerCase();
  return reason === "source_mixed" || (
    hasNonEmptyEndOfDayLoadOrderIds(detail, "missing_order_ids")
    && hasNonEmptyEndOfDayLoadOrderIds(detail, "date_changed_order_ids")
  );
}

function isRemovableOrphanEndOfDayLoadDetail(detail) {
  if (isMixedOrphanEndOfDayLoadDetail(detail)) {
    return false;
  }
  const reason = normalizeInlineText(detail?.ghost_reason).toLowerCase();
  return reason === "source_missing" || reason === "source_inactive";
}

function isDateChangedEndOfDayLoadDetail(detail) {
  return !isMixedOrphanEndOfDayLoadDetail(detail)
    && normalizeInlineText(detail?.ghost_reason).toLowerCase() === "source_date_changed";
}

function buildRemovedEndOfDayLoadRemovalPayload(
  detail,
  dispatchDate,
  previewRevision = "",
  sourceChangeSection = "delete",
) {
  const markerKey = normalizeInlineText(detail?.marker_key);
  const activeDispatchDate = normalizeInlineText(dispatchDate);
  const sourceDispatchDate = normalizeInlineText(detail?.source_dispatch_date || detail?.marker_dispatch_date || dispatchDate);
  const markerDispatchDate = normalizeInlineText(detail?.marker_dispatch_date || sourceDispatchDate);
  const deliveryIds = Array.isArray(detail?.delivery_ids)
    ? detail.delivery_ids.map(normalizeInlineText).filter(Boolean)
    : [];
  const orderIds = Array.isArray(detail?.order_ids)
    ? detail.order_ids.map(normalizeInlineText).filter(Boolean)
    : [];
  return {
    allow_missing_source_orders: true,
    allow_source_ghost: true,
    delivery_ids: deliveryIds,
    dispatch_date: activeDispatchDate,
    marker_dispatch_date: markerDispatchDate || sourceDispatchDate || dispatchDate,
    marker_key: markerKey,
    order_ids: orderIds,
    preview_revision: normalizeInlineText(previewRevision),
    source_change_section: normalizeInlineText(sourceChangeSection) || "delete",
    source_dispatch_date: sourceDispatchDate || markerDispatchDate || dispatchDate,
    target_dispatch_date: activeDispatchDate,
    target_period: normalizeInlineText(detail?.target_period) || undefined,
    delivery_mode: normalizeInlineText(detail?.delivery_mode) || undefined,
    target_truck_id: normalizeInlineText(detail?.target_truck_id) || undefined,
  };
}

function resolveSourceChangePortalPublicationEffects(responsePayloads = []) {
  const invalidatedTruckIds = [];
  let portalPublications = null;

  (Array.isArray(responsePayloads) ? responsePayloads : []).forEach((payload) => {
    if (!payload || typeof payload !== "object") {
      return;
    }
    const resultPayload = payload?.result_payload && typeof payload.result_payload === "object"
      ? payload.result_payload
      : null;
    const nextPortalPublications = payload?.portal_publications
      || resultPayload?.portal_publications
      || null;
    if (nextPortalPublications && typeof nextPortalPublications === "object") {
      portalPublications = nextPortalPublications;
    }
    invalidatedTruckIds.push(
      ...normalizePortalPublicationInvalidatedTruckIds(payload?.publication_invalidated_truck_ids),
      ...normalizePortalPublicationInvalidatedTruckIds(resultPayload?.publication_invalidated_truck_ids),
    );
  });

  return {
    invalidatedTruckIds: normalizePortalPublicationInvalidatedTruckIds(invalidatedTruckIds),
    portalPublications,
  };
}

export default function useResultsWorkspaceController({
  detailViewState,
  initialResultsData = null,
  openErrorsPanel = null,
  workspaceState,
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const navigation = useNavigation();
  const revalidator = useRevalidator();
  const isDispatchReloading = navigation.state !== "idle" || revalidator.state !== "idle";
  const dispatchReloadingRef = useRef(isDispatchReloading);
  dispatchReloadingRef.current = isDispatchReloading;
  const { showToast, updateToast, dismissToast } = useToast();
  const {
    closeReassignmentPopover,
    handleClientSelection,
    interactionsRef,
    mapStateRef,
    renderAssignmentCards,
    renderCurrentSelection,
    syncWorkspaceUi,
    toggleReassignmentPopover,
    workspaceStore,
    workspaceElements,
  } = workspaceState;
  const { access, hasPermission, isReady: isAccessReady } = useAccess();
  const {
    dispatchReassignmentMode: reassignmentRebuildMode,
    protectAfterReassignment,
    setDispatchReassignmentMode,
    setProtectAfterReassignment,
  } = useUserUiPreferences();
  const isAccessLoading = !isAccessReady;
  const canCloseDispatchDay = isAccessReady && hasPermission(ACCESS_PERMISSION_KEYS.dispatchDayClose);
  const canInvalidateDispatchDay = isAccessReady && hasPermission(ACCESS_PERMISSION_KEYS.dispatchDayInvalidate);
  const canResetDispatchRevalidation = isAccessReady
    && hasPermission(ACCESS_PERMISSION_KEYS.dispatchDayResetRevalidation);
  const canReopenDispatchDay = isAccessReady && hasPermission(ACCESS_PERMISSION_KEYS.dispatchDayReopen);
  const canResetHereRouteCache = isAccessReady && hasPermission(ACCESS_PERMISSION_KEYS.technicalRouteCacheReset);
  const canResetPlanning = isAccessReady && hasPermission(ACCESS_PERMISSION_KEYS.technicalPlanningReset);
  const canDeleteDispatchSnapshot = isAccessReady && hasPermission(ACCESS_PERMISSION_KEYS.technicalDispatchSnapshotDelete);
  const canRunOptimization = isAccessReady && hasPermission(ACCESS_PERMISSION_KEYS.dispatchRun);
  const canPublishDriverPortal = isAccessReady && hasPermission(ACCESS_PERMISSION_KEYS.dispatchDayPublishPortal);
  const canMutatePlanning = isAccessReady && hasPermission(ACCESS_PERMISSION_KEYS.dispatchAssignmentWrite);
  const canSaveDispatchSnapshot = isAccessReady && hasPermission(ACCESS_PERMISSION_KEYS.dispatchSnapshotWrite);
  const canRestoreDispatchSnapshot = isAccessReady && hasPermission(ACCESS_PERMISSION_KEYS.dispatchSnapshotRestore);
  const canApplyDispatchSourceChanges = isAccessReady && hasPermission(ACCESS_PERMISSION_KEYS.dispatchSourceChangesWrite);
  const canManageDispatcherFreeze = canMutatePlanning && accessCanManageDispatcherFreeze(access);
  const canManageRouteAlternatives = isAccessReady && canMutatePlanning && accessCanManageRouteAlternatives(access);

  const handleReassignmentRebuildModeChange = useCallback((nextMode) => {
    const normalizedMode = normalizeDispatchReassignmentMode(nextMode);
    try {
      const operation = setDispatchReassignmentMode(normalizedMode, { immediate: true });
      if (operation && typeof operation.catch === "function") {
        void operation.catch(() => {});
      }
    } catch {
      // Le fournisseur global rétablira la dernière préférence synchronisée.
    }
  }, [setDispatchReassignmentMode]);

  const handleProtectAfterReassignmentChange = useCallback((nextValue) => {
    const normalizedValue = normalizeDispatchProtectAfterReassignment(nextValue);
    try {
      const operation = setProtectAfterReassignment(normalizedValue, { immediate: true });
      if (operation && typeof operation.catch === "function") {
        void operation.catch(() => {});
      }
    } catch {
      // Le fournisseur global rétablira la dernière préférence synchronisée.
    }
  }, [setProtectAfterReassignment]);

  const initialResultsDispatchDate = normalizeInlineText(initialResultsData?.dispatch_date);
  const activePortalDispatchDate = normalizeInlineText(readDispatchDateFromSearch(location.search))
    || initialResultsDispatchDate
    || readWorkspaceDispatchDate(workspaceStore);
  const activePortalDispatchPeriod = normalizePortalPublicationDispatchPeriod(
    new URLSearchParams(location.search || "").get("dispatch_period")
      || initialResultsData?.ui_dispatch_period
      || initialResultsData?.dispatch_period,
  );
  const activePortalScopeKey = `${activePortalDispatchDate}|${activePortalDispatchPeriod}`;
  const initialPortalPublicationsSource = initialResultsData?.portal_publications ?? null;
  const initialDispatchSnapshotsSource = initialResultsData?.dispatch_snapshots ?? null;
  const initialSourceChangesSource = initialResultsData?.source_changes ?? null;
  const initialPortalPublications = readInitialPortalPublications(initialResultsData, {
    dispatchDate: activePortalDispatchDate,
    dispatchPeriod: activePortalDispatchPeriod,
  });
  const initialDispatchSnapshots = readInitialDispatchSnapshots(initialResultsData, {
    dispatchDate: activePortalDispatchDate,
    dispatchPeriod: activePortalDispatchPeriod,
  });
  const initialSourceChanges = readInitialSourceChanges(initialResultsData, {
    dispatchDate: activePortalDispatchDate,
    dispatchPeriod: activePortalDispatchPeriod,
  });
  const hasInitialSourceChangeMetadataSync = hasPendingSourceChangeMetadataSync(initialSourceChanges);
  const [sourceChanges, setSourceChanges] = useState(() => {
    const emptyState = buildEmptySourceChangesState(
      activePortalDispatchDate,
      activePortalDispatchPeriod,
    );
    // Bloc absent de la réponse (ouverture différée) : la vérification séparée part aussitôt.
    const isBootstrapVerificationPending = !initialSourceChanges && canApplyDispatchSourceChanges;
    const initialState = {
      ...emptyState,
      ...(!hasInitialSourceChangeMetadataSync && initialSourceChanges ? initialSourceChanges : {}),
      hasVerified: Boolean(initialSourceChanges) && !hasInitialSourceChangeMetadataSync,
      isVerifying: hasInitialSourceChangeMetadataSync || isBootstrapVerificationPending,
      needsVerification: hasInitialSourceChangeMetadataSync
        || isBootstrapVerificationPending
        || (!initialSourceChanges && emptyState.lastVerifiedPendingCount != null),
    };
    const initialPendingCount = initialState.hasVerified
      ? resolveSourceChangesAggregateCount(initialState)
      : initialState.lastVerifiedPendingCount;
    if (initialState.hasVerified && initialPendingCount != null) {
      sourceChangesAggregateCache.write({
        dispatchDate: activePortalDispatchDate,
        dispatchPeriod: activePortalDispatchPeriod,
      }, initialPendingCount);
    }
    return {
      ...initialState,
      lastVerifiedPendingCount: initialPendingCount,
    };
  });
  const [sourceChangesOpenRequestedPeriod, setSourceChangesOpenRequestedPeriod] = useState("");
  const [daySourceChanges, setDaySourceChanges] = useState(() => (
    buildEmptySourceChangesState(activePortalDispatchDate, "day")
  ));
  const [eveningSourceChanges, setEveningSourceChanges] = useState(() => (
    buildEmptySourceChangesState(activePortalDispatchDate, "evening")
  ));
  const isSourceChangesBusy = sourceChanges.isMutating === true || sourceChanges.isVerifying === true || isDispatchReloading;
  const [dispatchSnapshots, setDispatchSnapshots] = useState(() => ({
    dispatchDate: activePortalDispatchDate,
    items: initialDispatchSnapshots?.items || [],
    isLoading: false,
    isSaving: false,
    previewingSnapshotId: "",
    restoringSnapshotId: "",
    deletingSnapshotId: "",
    restorePreviewById: {},
  }));
  const activePortalScopeRef = useRef({
    dispatchDate: activePortalDispatchDate,
    dispatchPeriod: activePortalDispatchPeriod,
  });
  const [portalPublications, setPortalPublications] = useState(() => ({
    ...(initialPortalPublications || normalizePortalPublications(null, {
      dispatchDate: activePortalDispatchDate,
      dispatchPeriod: activePortalDispatchPeriod,
    })),
    errorMessage: "",
    isLoaded: Boolean(initialPortalPublications),
    // Bloc absent de la réponse : son chargement séparé part aussitôt.
    isLoading: !initialPortalPublications && canPublishDriverPortal,
  }));
  const dispatchSnapshotsSyncKeyRef = useRef("");
  const dispatchSnapshotsRequestIdRef = useRef(0);
  const portalPublicationsRequestIdRef = useRef(0);
  const portalPublicationsInFlightRef = useRef(null);
  const sourceChangesRequestIdRef = useRef(0);
  const daySourceChangesRequestIdRef = useRef(0);
  const eveningSourceChangesRequestIdRef = useRef(0);
  const sourceChangesSyncKeyRef = useRef("");
  const sourceChangesStateRef = useRef(sourceChanges);
  const daySourceChangesStateRef = useRef(daySourceChanges);
  const eveningSourceChangesStateRef = useRef(eveningSourceChanges);
  const sourceChangesMutationInFlightRef = useRef(false);
  const sourceChangesUpdateFinishRef = useRef(null);
  const sourceChangeResultInstallationsRef = useRef(new WeakMap());
  const deferredSourceChangesVerificationRef = useRef(null);
  const openSourceChangesVerificationInFlightRef = useRef(null);
  const verifySourceChangesOnOpenRef = useRef(null);
  const refreshTruckChangesCreatedByEditRef = useRef(null);
  const automaticTruckChangeKeysRef = useRef(new Set());
  const attemptedAddressRefreshKeysRef = useRef(new Set());
  const sourceChangeMetadataSyncInFlightRef = useRef(null);
  const sourceChangeAcknowledgementInFlightRef = useRef(false);
  const renderCurrentSelectionRef = useRef(renderCurrentSelection);
  const sourceChangesMountedRef = useRef(true);
  useEffect(() => {
    sourceChangesMountedRef.current = true;
    return () => {
      sourceChangesMountedRef.current = false;
      sourceChangesMutationInFlightRef.current = false;
      sourceChangeAcknowledgementInFlightRef.current = false;
      sourceChangesUpdateFinishRef.current?.();
      sourceChangesUpdateFinishRef.current = null;
    };
  }, []);
  const metadataSyncActivityMessage = useSyncExternalStore(
    subscribePlanningMutationActivity,
    getPlanningMutationActivityMessage,
    () => "",
  );
  useLayoutEffect(() => {
    renderCurrentSelectionRef.current?.();
  }, [metadataSyncActivityMessage]);
  const sourceChangesRefreshCoordinatorRef = useRef(null);
  const visibilityWorkspaceRefreshKeyRef = useRef(null);
  const visibilityWorkspaceRefreshRef = useRef(null);
  if (!sourceChangesRefreshCoordinatorRef.current) {
    sourceChangesRefreshCoordinatorRef.current = createSourceChangesRefreshCoordinator();
  }
  if (!sourceChangeMetadataSyncInFlightRef.current) {
    sourceChangeMetadataSyncInFlightRef.current = createSourceChangeMetadataSyncCoordinator();
  }
  if (!visibilityWorkspaceRefreshKeyRef.current) {
    visibilityWorkspaceRefreshKeyRef.current = Symbol("results-workspace-visibility-refresh");
  }
  sourceChangesStateRef.current = sourceChanges;
  daySourceChangesStateRef.current = daySourceChanges;
  eveningSourceChangesStateRef.current = eveningSourceChanges;
  const setSourceChangesMutationBusy = useCallback((isMutating) => {
    if (isMutating) {
      if (!sourceChangesMountedRef.current || sourceChangesUpdateFinishRef.current) return false;
      const finish = tryBeginPlanningUpdateActivity();
      if (!finish) {
        showToast({ type: "warning", title: PLANNING_UPDATE_BUSY_MESSAGE,
          message: "Attendez la fin avant de lancer une autre action de Mise à jour." });
        return false;
      }
      sourceChangesUpdateFinishRef.current = finish;
    }
    sourceChangesMutationInFlightRef.current = isMutating === true;
    if (!isMutating) {
      sourceChangesUpdateFinishRef.current?.();
      sourceChangesUpdateFinishRef.current = null;
    }
    if (!sourceChangesMountedRef.current) return;
    setSourceChanges((currentState) => ({ ...currentState, isMutating: isMutating === true }));
    setDaySourceChanges((currentState) => ({ ...currentState, isMutating: isMutating === true }));
    setEveningSourceChanges((currentState) => ({ ...currentState, isMutating: isMutating === true }));
    return true;
  }, [showToast]);
  const installSourceChangeMutationResult = useCallback((payload, {
    dispatchDate, dispatchPeriod, performanceContext = {}, capture = null,
  }) => {
    const previous = payload && sourceChangeResultInstallationsRef.current.get(payload);
    if (previous) return previous;
    const installation = { needsRecovery: false, viewInstalled: false };
    if (payload && typeof payload === "object") sourceChangeResultInstallationsRef.current.set(payload, installation);
    capture?.reportProgress("display");
    const result = payload?.result_payload;
    // A complete POST preview describes this saved result. A later GET may
    // describe a concurrent result and must never acknowledge it on its behalf.
    const responsePreviewRevision = payload?.source_changes_refresh_required !== true
      && isSourceChangeFinalPreview(payload?.source_changes, { dispatchDate, dispatchPeriod })
      ? payload.source_changes.runtime_revision
      : "";
    const runtimeRevision = normalizeInlineText(result?.runtime_revision || payload?.runtime_revision || responsePreviewRevision);
    installation.runtimeRevision = runtimeRevision;
    const validResult = result && typeof result === "object" && !Array.isArray(result)
      && payload?.result_refresh_required !== true
      && (!result.dispatch_date || result.dispatch_date === dispatchDate)
      && (!result.dispatch_period || result.dispatch_period === dispatchPeriod);
    if (!validResult) {
      installation.needsRecovery = true;
    } else {
      const syncedResultPayload = buildSourceChangePlanningResultPayload(
        runtimeRevision ? { ...result, runtime_revision: runtimeRevision } : result,
        payload?._planning_diagnostics,
      );
      const activeScope = activePortalScopeRef.current;
      if (sourceChangesMountedRef.current && activeScope.dispatchDate === dispatchDate
        && activeScope.dispatchPeriod === dispatchPeriod) {
        try {
          const state = workspaceStore.getState();
          if (!state || typeof renderCurrentSelectionRef.current !== "function") throw new Error("Vue dispatch indisponible.");
          const { nextAssignment, nextTrip } = syncResultsAfterPlanningMutation(state, syncedResultPayload, {});
          renderCurrentSelectionRef.current(nextAssignment, nextTrip, {
            synchronous: performanceContext.action === "source_changes_refresh_trucks",
          });
          installation.viewInstalled = true;
        } catch (error) {
          installation.needsRecovery = true;
          console.warn("Action enregistrée, mais la vue dispatch reste à récupérer.", error);
        }
      }
      try {
        invalidatePageDataCacheEntries([RESULTS_LATEST_API_URL]);
        writePageDataCacheEntry(buildDispatchDateQueryUrl(RESULTS_LATEST_API_URL, dispatchDate, dispatchPeriod), syncedResultPayload);
        invalidateShellDataCache();
      } catch (error) {
        installation.needsRecovery = true;
        console.warn("Action enregistrée, mais le cache dispatch reste à récupérer.", error);
      }
    }
    dispatchPlanningStateChange("dispatch", {
      actionName: (performanceContext?.action || "source_changes_update").replace(/^source_changes_/, "source-changes:").replaceAll("_", "-"),
      dispatchDate, dispatchPeriod, performanceChainId: performanceContext?.chainId,
      skipLocalSourceChangesRefresh: true,
      // A cache write never acknowledges the active view.
      ...(installation.viewInstalled && runtimeRevision ? { runtimeRevision } : {}),
    });
    if (installation.needsRecovery && !["refused", "awaiting_confirmation"].includes(capture?.getStatus())) capture?.setStatus("partial");
    capture?.reportProgress("display", installation.needsRecovery ? "warning" : "completed", {
      result_code: installation.needsRecovery ? "installation_failed" : installation.viewInstalled ? "result_installed" : "scope_no_longer_visible",
    });
    return installation;
  }, [workspaceStore]);
  const protectedOrderAutoRefreshAttemptedKeysRef = useRef(new Set());
  const protectedOrderAutoRefreshMountedRef = useRef(true);
  const protectedOrderAutoRefreshRunningRef = useRef(false);
  const protectedOrderAutoRefreshPlanningMutationRef = useRef(null);
  const protectedOrderAutoRefreshPortalPublicationsRef = useRef(portalPublications);
  const protectedOrderAutoRefreshSourceChangesBusyRef = useRef(isSourceChangesBusy);
  const protectedOrderAutoRefreshSettledScopesRef = useRef({ portalPublications: "", sourceChanges: "" });
  const [isDocumentVisible, setIsDocumentVisible] = useState(
    () => typeof document === "undefined" || document.visibilityState === "visible",
  );
  const portalBootstrapSkipRef = useRef(initialPortalPublications ? {
    scopeKey: activePortalScopeKey,
    source: initialPortalPublicationsSource,
  } : null);
  const dispatchSnapshotsBootstrapSkipRef = useRef(initialDispatchSnapshots ? {
    dispatchDate: activePortalDispatchDate,
    source: initialDispatchSnapshotsSource,
  } : null);
  // Pending metadata or addresses are applied by the opening refresh.
  const sourceChangesBootstrapSkipRef = useRef(initialSourceChanges && !hasInitialSourceChangeMetadataSync
    && pendingAddressOrderIds(initialSourceChanges, initialResultsData, activePortalDispatchDate).length === 0 ? {
    scopeKey: activePortalScopeKey,
    source: initialSourceChangesSource,
  } : null);
  const dispatchBootstrapInputsRef = useRef({
    dispatchSnapshotsSource: initialDispatchSnapshotsSource,
    portalPublicationsSource: initialPortalPublicationsSource,
    sourceChangesSource: initialSourceChangesSource,
    scopeKey: activePortalScopeKey,
  });

  useLayoutEffect(() => {
    renderCurrentSelectionRef.current = renderCurrentSelection;
  }, [renderCurrentSelection]);

  useLayoutEffect(() => {
    activePortalScopeRef.current = {
      dispatchDate: activePortalDispatchDate,
      dispatchPeriod: activePortalDispatchPeriod,
    };
    const previousInputs = dispatchBootstrapInputsRef.current;
    if (
      previousInputs.scopeKey === activePortalScopeKey
      && previousInputs.portalPublicationsSource === initialPortalPublicationsSource
      && previousInputs.dispatchSnapshotsSource === initialDispatchSnapshotsSource
      && previousInputs.sourceChangesSource === initialSourceChangesSource
    ) {
      return;
    }
    const didScopeChange = previousInputs.scopeKey !== activePortalScopeKey;
    if (didScopeChange) {
      openSourceChangesVerificationInFlightRef.current = null;
      daySourceChangesRequestIdRef.current += 1;
      eveningSourceChangesRequestIdRef.current += 1;
      const [previousDispatchDate, previousDispatchPeriod] = normalizeInlineText(
        previousInputs.scopeKey,
      ).split("|");
      const previousSourceChanges = sourceChangesStateRef.current;
      if (
        previousDispatchDate === activePortalDispatchDate
        && normalizeInlineText(previousSourceChanges?.dispatchDate) === previousDispatchDate
        && normalizePortalPublicationDispatchPeriod(previousSourceChanges?.dispatchPeriod)
          === normalizePortalPublicationDispatchPeriod(previousDispatchPeriod)
      ) {
        if (normalizePortalPublicationDispatchPeriod(previousDispatchPeriod) === "day") {
          setDaySourceChanges(previousSourceChanges);
        } else {
          setEveningSourceChanges(previousSourceChanges);
        }
      }
    }
    dispatchBootstrapInputsRef.current = {
      dispatchSnapshotsSource: initialDispatchSnapshotsSource,
      portalPublicationsSource: initialPortalPublicationsSource,
      sourceChangesSource: initialSourceChangesSource,
      scopeKey: activePortalScopeKey,
    };
    portalPublicationsRequestIdRef.current += 1;
    dispatchSnapshotsRequestIdRef.current += 1;
    sourceChangesRequestIdRef.current += 1;
    portalBootstrapSkipRef.current = initialPortalPublications ? {
      scopeKey: activePortalScopeKey,
      source: initialPortalPublicationsSource,
    } : null;
    dispatchSnapshotsBootstrapSkipRef.current = initialDispatchSnapshots ? {
      dispatchDate: activePortalDispatchDate,
      source: initialDispatchSnapshotsSource,
    } : null;
    sourceChangesBootstrapSkipRef.current = initialSourceChanges && !hasInitialSourceChangeMetadataSync ? {
      scopeKey: activePortalScopeKey,
      source: initialSourceChangesSource,
    } : null;
    setPortalPublications({
      ...(initialPortalPublications || normalizePortalPublications(null, {
        dispatchDate: activePortalDispatchDate,
        dispatchPeriod: activePortalDispatchPeriod,
      })),
      errorMessage: "",
      isLoaded: Boolean(initialPortalPublications),
      isLoading: !initialPortalPublications && canPublishDriverPortal,
    });
    setDispatchSnapshots((currentState) => ({
      ...(didScopeChange
        ? {
            isSaving: false,
            previewingSnapshotId: "",
            restoringSnapshotId: "",
            deletingSnapshotId: "",
            restorePreviewById: {},
          }
        : currentState),
      dispatchDate: activePortalDispatchDate,
      items: initialDispatchSnapshots?.items || [],
      isLoading: false,
    }));
    setSourceChanges((currentState) => {
      const pendingDeferredVerification = deferredSourceChangesVerificationRef.current;
      const hasMatchingDeferredVerification = !didScopeChange
        && pendingDeferredVerification?.dispatchDate === activePortalDispatchDate
        && pendingDeferredVerification?.dispatchPeriod === activePortalDispatchPeriod;
      const hasMatchingPlanningContextVerification = hasMatchingDeferredVerification
        && pendingDeferredVerification?.contextOnly === true;
      const hasMatchingVisibleDeferredVerification = hasMatchingDeferredVerification
        && !hasMatchingPlanningContextVerification;
      const hasVerifiedInitialSourceChanges = Boolean(initialSourceChanges)
        && !hasInitialSourceChangeMetadataSync
        && !hasMatchingVisibleDeferredVerification;
      // Même règle que l'effet d'amorçage, qui lance alors la vérification séparée.
      const isBootstrapVerificationPending = !initialSourceChanges && canApplyDispatchSourceChanges
        && !hasMatchingDeferredVerification;
      const emptyState = buildEmptySourceChangesState(
        activePortalDispatchDate,
        activePortalDispatchPeriod,
      );
      const nextState = {
        ...emptyState,
        ...(!hasInitialSourceChangeMetadataSync && initialSourceChanges ? initialSourceChanges : {}),
        hasVerified: hasVerifiedInitialSourceChanges,
        isVerifying: hasInitialSourceChangeMetadataSync || isBootstrapVerificationPending,
        isMutating: didScopeChange ? false : currentState.isMutating,
        needsPlanningContextVerification: hasMatchingPlanningContextVerification,
        needsVerification: hasInitialSourceChangeMetadataSync
          || hasMatchingVisibleDeferredVerification
          || isBootstrapVerificationPending
          || (!initialSourceChanges && emptyState.lastVerifiedPendingCount != null),
        planningContextVerificationErrorMessage: hasMatchingPlanningContextVerification
          ? normalizeInlineText(currentState.planningContextVerificationErrorMessage)
          : "",
        planningContextVerificationVersion: didScopeChange
          ? 0
          : Number(currentState.planningContextVerificationVersion || 0),
      };
      const nextPendingCount = nextState.hasVerified
        ? resolveSourceChangesAggregateCount(nextState)
        : nextState.lastVerifiedPendingCount;
      if (nextState.hasVerified && nextState.needsVerification !== true && nextPendingCount != null) {
        sourceChangesAggregateCache.write({
          dispatchDate: activePortalDispatchDate,
          dispatchPeriod: activePortalDispatchPeriod,
        }, nextPendingCount);
      }
      if (
        didScopeChange
        || (
          nextState.hasVerified
          && nextState.needsVerification !== true
          && nextState.needsPlanningContextVerification !== true
        )
      ) {
        deferredSourceChangesVerificationRef.current = null;
      }
      return {
        ...nextState,
        lastVerifiedPendingCount: nextPendingCount,
      };
    });
    if (didScopeChange && normalizeInlineText(previousInputs.scopeKey).split("|")[0] !== activePortalDispatchDate) {
      setDaySourceChanges(buildEmptySourceChangesState(activePortalDispatchDate, "day"));
      setEveningSourceChanges(buildEmptySourceChangesState(activePortalDispatchDate, "evening"));
    }
  }, [
    activePortalDispatchDate,
    activePortalDispatchPeriod,
    activePortalScopeKey,
    canApplyDispatchSourceChanges,
    canPublishDriverPortal,
    hasInitialSourceChangeMetadataSync,
    initialDispatchSnapshots,
    initialDispatchSnapshotsSource,
    initialPortalPublications,
    initialPortalPublicationsSource,
    initialSourceChanges,
    initialSourceChangesSource,
  ]);

  const commitPortalPublicationsChange = useCallback((payload, { invalidatePendingRequest = false } = {}) => {
    const activeScope = activePortalScopeRef.current;
    const nextPortalPublications = normalizePortalPublications(payload, activeScope);
    if (
      nextPortalPublications.dispatchDate !== activeScope.dispatchDate
      || nextPortalPublications.dispatchPeriod !== activeScope.dispatchPeriod
    ) {
      return false;
    }
    if (invalidatePendingRequest) {
      portalPublicationsRequestIdRef.current += 1;
    }
    setPortalPublications((currentState) => ({
      ...currentState,
      ...nextPortalPublications,
      errorMessage: "",
      isLoaded: true,
      isLoading: false,
    }));
    return true;
  }, []);

  const refreshPortalPublications = useCallback(async ({ background = false } = {}) => {
    if (!isAccessReady) {
      return null;
    }
    const requestedScope = { ...activePortalScopeRef.current };
    const isRequestedScopeCurrent = () => {
      const activeScope = activePortalScopeRef.current;
      return activeScope.dispatchDate === requestedScope.dispatchDate
        && activeScope.dispatchPeriod === requestedScope.dispatchPeriod;
    };
    const requestId = portalPublicationsRequestIdRef.current + 1;
    portalPublicationsRequestIdRef.current = requestId;
    if (!canPublishDriverPortal || !requestedScope.dispatchDate) {
      setPortalPublications({
        ...normalizePortalPublications(null, {
          dispatchDate: requestedScope.dispatchDate,
          dispatchPeriod: requestedScope.dispatchPeriod,
        }),
        errorMessage: "",
        isLoading: false,
      });
      return null;
    }

    if (!background) {
      setPortalPublications((currentState) => ({
        ...(currentState.dispatchDate === requestedScope.dispatchDate
          && currentState.dispatchPeriod === requestedScope.dispatchPeriod
          ? currentState
          : normalizePortalPublications(null, {
              dispatchDate: requestedScope.dispatchDate,
              dispatchPeriod: requestedScope.dispatchPeriod,
            })),
        dispatchDate: requestedScope.dispatchDate,
        dispatchPeriod: requestedScope.dispatchPeriod,
        errorMessage: "",
        isLoading: true,
      }));
    }
    const inFlightRequest = { scopeKey: `${requestedScope.dispatchDate}|${requestedScope.dispatchPeriod}` };
    portalPublicationsInFlightRef.current = inFlightRequest;
    try {
      const nextPortalPublications = await fetchPortalPublications(
        requestedScope.dispatchDate,
        requestedScope.dispatchPeriod,
      );
      if (portalPublicationsRequestIdRef.current !== requestId || !isRequestedScopeCurrent()) {
        return null;
      }
      if (!commitPortalPublicationsChange(nextPortalPublications)) {
        return null;
      }
      return nextPortalPublications;
    } catch (error) {
      if (portalPublicationsRequestIdRef.current !== requestId || !isRequestedScopeCurrent()) {
        return null;
      }
      const errorMessage = error instanceof Error
        ? error.message
        : "Impossible de charger les publications du portail chauffeur.";
      setPortalPublications((currentState) => {
        if (
          background
          && currentState.dispatchDate === requestedScope.dispatchDate
          && currentState.dispatchPeriod === requestedScope.dispatchPeriod
        ) {
          return {
            ...currentState,
            errorMessage,
            isLoading: false,
          };
        }
        return {
          ...normalizePortalPublications(null, {
            dispatchDate: requestedScope.dispatchDate,
            dispatchPeriod: requestedScope.dispatchPeriod,
          }),
          errorMessage,
          isLoading: false,
        };
      });
      return null;
    } finally {
      if (portalPublicationsInFlightRef.current === inFlightRequest) {
        portalPublicationsInFlightRef.current = null;
      }
    }
  }, [canPublishDriverPortal, commitPortalPublicationsChange, isAccessReady]);

  const handlePortalPublicationsChange = useCallback((payload) => {
    const didCommitPortalPublications = commitPortalPublicationsChange(payload, {
      invalidatePendingRequest: true,
    });
    void refreshPortalPublications({ background: didCommitPortalPublications });
  }, [commitPortalPublicationsChange, refreshPortalPublications]);

  const synchronizeSourceChangePortalPublicationEffects = useCallback(async (responsePayloads) => {
    const publicationEffects = resolveSourceChangePortalPublicationEffects(responsePayloads);
    const didCommitPortalPublications = publicationEffects.portalPublications
      ? commitPortalPublicationsChange(publicationEffects.portalPublications, { invalidatePendingRequest: true })
      : false;
    const shouldRefreshPortalPublications = !didCommitPortalPublications && (
      Boolean(publicationEffects.portalPublications)
      || publicationEffects.invalidatedTruckIds.length > 0
    );
    if (shouldRefreshPortalPublications) {
      await refreshPortalPublications();
    }
    return publicationEffects;
  }, [commitPortalPublicationsChange, refreshPortalPublications]);

  useEffect(() => {
    if (!isAccessReady) {
      return;
    }
    const pendingBootstrap = portalBootstrapSkipRef.current;
    if (
      canPublishDriverPortal
      && pendingBootstrap?.scopeKey === activePortalScopeKey
      && pendingBootstrap.source === initialPortalPublicationsSource
    ) {
      // Keep the marker while this loader payload remains active. Other hook
      // dependencies may settle after access loading without requiring a GET.
      return;
    }
    portalBootstrapSkipRef.current = null;
    void refreshPortalPublications();
  }, [
    activePortalDispatchDate,
    activePortalDispatchPeriod,
    activePortalScopeKey,
    canPublishDriverPortal,
    initialPortalPublicationsSource,
    isAccessReady,
    refreshPortalPublications,
  ]);

  const getSourceChangesDispatchDate = useCallback(
    () => resolveSourceChangesDispatchDate(location.search, workspaceStore, initialResultsDispatchDate),
    [initialResultsDispatchDate, location.search, workspaceStore],
  );

  const executeSourceChangesRefresh = useCallback(async ({
    background = false,
    forceFresh = false,
    performanceContext = null,
    preserveVerifiedPresentation = false,
    requestedScope = null,
    initialPreview = null,
    reportUnavailable = false,
  } = {}) => {
    if (!sourceChangesMountedRef.current) {
      return SOURCE_CHANGES_REFRESH_UNMOUNTED;
    }
    if (!isAccessReady) {
      return null;
    }
    const dispatchDate = normalizeInlineText(requestedScope?.dispatchDate);
    const dispatchPeriod = normalizePortalPublicationDispatchPeriod(requestedScope?.dispatchPeriod);
    const isRequestedScopeCurrent = () => {
      const activeScope = activePortalScopeRef.current;
      return sourceChangesMountedRef.current && activeScope.dispatchDate === dispatchDate
        && activeScope.dispatchPeriod === dispatchPeriod;
    };
    if (!isRequestedScopeCurrent()) {
      return null;
    }
    const requestId = sourceChangesRequestIdRef.current + 1;
    sourceChangesRequestIdRef.current = requestId;
    if (!dispatchDate) {
      setSourceChanges(buildEmptySourceChangesState("", dispatchPeriod));
      return null;
    }
    const performanceHeaders = buildSourceChangesPerformanceHeaders(performanceContext || {});
    const currentSourceChanges = sourceChangesStateRef.current;
    const shouldPreserveVerifiedPresentation = preserveVerifiedPresentation === true
      && currentSourceChanges?.dispatchDate === dispatchDate
      && currentSourceChanges?.dispatchPeriod === dispatchPeriod
      && currentSourceChanges?.hasVerified === true
      && currentSourceChanges?.needsVerification !== true
      && !normalizeInlineText(currentSourceChanges?.errorMessage);
    setSourceChanges((currentState) => ({
      ...(currentState.dispatchDate === dispatchDate && currentState.dispatchPeriod === dispatchPeriod
        ? currentState
        : buildEmptySourceChangesState(dispatchDate, dispatchPeriod)),
      dispatchDate,
      dispatchPeriod,
      unavailableMessage: "",
      ...(background ? {} : { errorMessage: "", isLoading: true }),
      isVerifying: true,
      ...(shouldPreserveVerifiedPresentation
        ? {
            needsPlanningContextVerification: true,
            planningContextVerificationErrorMessage: "",
          }
        : {
            hasVerified: false,
            needsPlanningContextVerification: false,
            needsVerification: true,
            planningContextVerificationErrorMessage: "",
          }),
    }));
    let metadataSyncActivity = null;
    let metadataResultRefreshRequired = false;
    try {
      const completedMetadataSyncKeys = new Set();
      let metadataConflictRetries = 0;
      let metadataSyncPostAttemptBudget = {
        attemptCount: 0,
        maxAttempts: SOURCE_CHANGE_METADATA_SYNC_MAX_POST_ATTEMPTS,
      };
      let metadataSyncAttempts = 0;
      const bootstrapMatchesRuntime = !forceFresh
        && normalizeInlineText(initialPreview?.runtime_revision)
        && initialPreview.runtime_revision === workspaceStore.getState()?.resultsData?.runtime_revision;
      let payload = bootstrapMatchesRuntime ? initialPreview : null;
      let nextSourceChanges = payload
        ? readInitialSourceChanges({ source_changes: payload }, { dispatchDate, dispatchPeriod })
        : null;

      while (metadataSyncAttempts <= SOURCE_CHANGE_METADATA_SYNC_MAX_ATTEMPTS) {
        if (sourceChangesRequestIdRef.current !== requestId || !isRequestedScopeCurrent()) {
          return (reportUnavailable || metadataSyncActivity) ? (isRequestedScopeCurrent() ? SOURCE_CHANGES_REFRESH_SUPERSEDED : payload) : null;
        }
        if (!nextSourceChanges) {
          const sourceChangesUrl = buildDispatchDateQueryUrl(
            "/api/planning/source-changes",
            dispatchDate,
            dispatchPeriod,
          );
          const response = await fetchWithSupabaseAuth(
            forceFresh ? `${sourceChangesUrl}&force_fresh=true` : sourceChangesUrl,
            { headers: performanceHeaders },
          );
          payload = await parseJsonResponse(response, "Impossible de charger les changements Data Manager.");
          if (sourceChangesRequestIdRef.current !== requestId || !isRequestedScopeCurrent()) {
            return (reportUnavailable || metadataSyncActivity) ? (isRequestedScopeCurrent() ? SOURCE_CHANGES_REFRESH_SUPERSEDED : payload) : null;
          }
          nextSourceChanges = readInitialSourceChanges(
            { source_changes: payload },
            { dispatchDate, dispatchPeriod },
          );
          if (!nextSourceChanges) {
            throw new Error("La réponse des changements Data Manager ne correspond pas au dispatch actif.");
          }
        }

        const metadataOrderIds = Array.isArray(nextSourceChanges.metadataSync?.orderIds)
          ? nextSourceChanges.metadataSync.orderIds
          : [];
        // Après les métadonnées, les adresses corrigées sont appliquées sur les camions ; un conflit reste dans Mise à jour.
        const addressOrderIds = metadataOrderIds.length === 0 && canApplyDispatchSourceChanges
          ? pendingAddressOrderIds(nextSourceChanges, workspaceStore.getState()?.resultsData || initialResultsData, dispatchDate)
          : [];
        const addressRefreshKey = [dispatchDate, dispatchPeriod, ...addressOrderIds].join("|");
        if (metadataOrderIds.length === 0
          && (addressOrderIds.length === 0 || attemptedAddressRefreshKeysRef.current.has(addressRefreshKey))) {
          break;
        }
        if (!metadataSyncActivity) {
          metadataSyncActivity = await acquireSourceChangeMetadataSyncActivity({ isCurrent: isRequestedScopeCurrent });
          if (!metadataSyncActivity) return null;
          if (sourceChangesRequestIdRef.current !== requestId || !isRequestedScopeCurrent()) {
            return isRequestedScopeCurrent() ? SOURCE_CHANGES_REFRESH_SUPERSEDED : null;
          }
          if (metadataSyncActivity.waited) {
            payload = null;
            nextSourceChanges = null;
            forceFresh = true;
            continue;
          }
        }
        if (metadataOrderIds.length === 0) {
          attemptedAddressRefreshKeysRef.current.add(addressRefreshKey);
          let addressResult;
          try {
            const addressResponse = await fetchWithSupabaseAuth(
              buildDispatchDateQueryUrl("/api/planning/source-changes/addresses/apply", dispatchDate, dispatchPeriod),
              {
                body: JSON.stringify({ order_ids: addressOrderIds }),
                headers: { "Content-Type": "application/json", ...performanceHeaders },
                method: "POST",
              },
            );
            addressResult = await parseJsonResponse(addressResponse, "Les adresses n’ont pas pu être actualisées.");
          } catch (error) {
            addressResult = { error };
          }
          const addressToast = addressRefreshToast(addressResult);
          if (addressToast) {
            showToast(addressToast);
          }
          if (addressResult?.applied_order_ids?.length) {
            metadataResultRefreshRequired ||= installSourceChangeMutationResult(addressResult, {
              dispatchDate, dispatchPeriod, performanceContext,
            }).needsRecovery;
          }
          // Mise à jour est relue : il n'y reste que les conflits, expliqués sous leurs commandes.
          payload = null;
          nextSourceChanges = null;
          forceFresh = true;
          continue;
        }
        if (!canApplyDispatchSourceChanges) {
          throw new Error("La synchronisation des métadonnées Data Manager requiert le droit de modifier le dispatch.");
        }
        const metadataPreviewRevision = normalizeInlineText(
          nextSourceChanges.metadataSync?.previewRevision || nextSourceChanges.mutationPreviewRevision,
        );
        if (!metadataPreviewRevision) {
          throw new Error("La révision des métadonnées Data Manager est absente. Rafraîchissez M-à-J commandes.");
        }
        const metadataSyncKey = [
          dispatchDate,
          dispatchPeriod,
          metadataPreviewRevision,
          metadataOrderIds.join(","),
        ].join("|");
        if (completedMetadataSyncKeys.has(metadataSyncKey)) {
          throw new Error("Les métadonnées Data Manager restent désynchronisées après leur mise à jour.");
        }
        if (metadataSyncAttempts >= SOURCE_CHANGE_METADATA_SYNC_MAX_ATTEMPTS) {
          throw new Error("Trop de mises à jour concurrentes empêchent la synchronisation des métadonnées Data Manager.");
        }
        metadataSyncAttempts += 1;

        const postMetadataSyncRequest = async (requestBody) => {
          if (metadataSyncPostAttemptBudget.attemptCount >= metadataSyncPostAttemptBudget.maxAttempts) {
            throw new Error("Trop de mises à jour concurrentes empêchent la synchronisation des métadonnées Data Manager.");
          }
          metadataSyncPostAttemptBudget.attemptCount += 1;
          const metadataResponse = await fetchWithSupabaseAuth(
            buildDispatchDateQueryUrl(
              "/api/planning/source-changes/sync-metadata",
              dispatchDate,
              dispatchPeriod,
            ),
            {
              body: JSON.stringify(requestBody),
              headers: {
                "Content-Type": "application/json",
                "X-Planning-Diagnostics": "1",
                ...performanceHeaders,
              },
              method: "POST",
            },
          );
          const metadataPayload = await parseJsonResponse(
            metadataResponse,
            "Impossible de synchroniser les métadonnées Data Manager.",
          );
          if (sourceChangesRequestIdRef.current === requestId && isRequestedScopeCurrent()) {
            const installation = installSourceChangeMutationResult(metadataPayload, {
              dispatchDate, dispatchPeriod, performanceContext,
            });
            metadataResultRefreshRequired ||= installation.needsRecovery;
          }
          const publicationEffects = await synchronizeSourceChangePortalPublicationEffects([metadataPayload]);
          const publicationInvalidationToast = buildPortalPublicationInvalidationToast(
            publicationEffects.invalidatedTruckIds,
          );
          if (publicationInvalidationToast) {
            showToast(publicationInvalidationToast);
          }
          return metadataPayload;
        };
        const metadataSyncEntry = sourceChangeMetadataSyncInFlightRef.current({
          key: metadataSyncKey,
          request: () => executeExactSelectionSourceChangeRequest({
            canRetry: () => (
              metadataSyncPostAttemptBudget.attemptCount < metadataSyncPostAttemptBudget.maxAttempts
            ),
            initialRequestBody: {
              preview_revision: metadataPreviewRevision,
              order_ids: metadataOrderIds,
            },
            postRequest: postMetadataSyncRequest,
            staleCodes: ["source_changes_metadata_preview_stale"],
          }),
          shared: { postAttemptBudget: metadataSyncPostAttemptBudget },
        });
        metadataSyncPostAttemptBudget = metadataSyncEntry.shared?.postAttemptBudget
          || metadataSyncPostAttemptBudget;
        const metadataSyncPromise = metadataSyncEntry.promise;

        try {
          const metadataPayload = await metadataSyncPromise;
          completedMetadataSyncKeys.add(metadataSyncKey);
          metadataConflictRetries = 0;
          const mutationSourceChanges = readSourceChangesMutationResponse(metadataPayload, {
            dispatchDate,
            dispatchPeriod,
          });
          if (mutationSourceChanges) {
            payload = metadataPayload.source_changes;
            nextSourceChanges = mutationSourceChanges;
          } else {
            // Compatibilité avec un backend antérieur : une seule nouvelle lecture
            // récupère le preview complet avant la prochaine mutation éventuelle.
            payload = null;
            nextSourceChanges = null;
          }
        } catch (error) {
          const isPlanningMutationInProgress = normalizeInlineText(error?.code) === "planning_mutation_in_progress";
          const shouldReloadMetadataPreview = [
            "source_changes_metadata_preview_stale",
            "source_changes_metadata_source_changed",
          ].includes(normalizeInlineText(error?.code));
          if (
            Number(error?.status) === 409
            && !isPlanningMutationInProgress
            && shouldReloadMetadataPreview
            && !wasSourceChangesRetryAttempted(error)
            && metadataConflictRetries < 1
            && metadataSyncPostAttemptBudget.attemptCount < metadataSyncPostAttemptBudget.maxAttempts
          ) {
            metadataConflictRetries += 1;
            payload = null;
            nextSourceChanges = null;
            continue;
          }
          throw error;
        }
      }

      if (sourceChangesRequestIdRef.current !== requestId || !isRequestedScopeCurrent()) {
        return (reportUnavailable || metadataSyncActivity) ? (isRequestedScopeCurrent() ? SOURCE_CHANGES_REFRESH_SUPERSEDED : payload) : null;
      }
      if (!nextSourceChanges || hasPendingSourceChangeMetadataSync(nextSourceChanges)) {
        throw new Error("Les infos sans effet sur la route (silo, commentaire…) n'ont pas pu être recopiées. Réessayez la vérification.");
      }
      const nextPendingCount = resolveSourceChangesAggregateCount(nextSourceChanges);
      if (nextPendingCount != null) {
        sourceChangesAggregateCache.write({ dispatchDate, dispatchPeriod }, nextPendingCount);
      }
      deferredSourceChangesVerificationRef.current = null;
      setSourceChanges((currentState) => ({
        ...currentState,
        ...nextSourceChanges,
        previewRevision: normalizeInlineText(payload?.preview_revision),
        errorMessage: "",
        unavailableMessage: "",
        hasVerified: true,
        isLoading: false,
        isVerifying: false,
        lastVerifiedPendingCount: nextPendingCount ?? currentState.lastVerifiedPendingCount,
        needsPlanningContextVerification: false,
        needsVerification: false,
        planningContextVerificationErrorMessage: "",
      }));
      return metadataResultRefreshRequired ? { ...payload, result_refresh_required: true } : payload;
    } catch (error) {
      if (sourceChangesRequestIdRef.current !== requestId || !isRequestedScopeCurrent()) {
        if (reportUnavailable && !isRequestedScopeCurrent() && dispatchPeriod === "evening"
          && isSourceChangesSnapshotUnavailableError(error)) {
          return { source_changes_unavailable: true, dispatch_date: dispatchDate, dispatch_period: dispatchPeriod };
        }
        return metadataSyncActivity && isRequestedScopeCurrent() ? SOURCE_CHANGES_REFRESH_SUPERSEDED : null;
      }
      if (
        dispatchPeriod === "evening"
        && isSourceChangesSnapshotUnavailableError(error)
      ) {
        setSourceChanges((currentState) => ({
          ...buildEmptySourceChangesState(dispatchDate, dispatchPeriod),
          isMutating: currentState.isMutating,
          unavailableMessage: EVENING_PLANNING_UNAVAILABLE_MESSAGE,
          hasVerified: false,
          isLoading: false,
          isVerifying: false,
          needsPlanningContextVerification: false,
          needsVerification: false,
          planningContextVerificationErrorMessage: "",
          errorMessage: "",
        }));
        return reportUnavailable ? { source_changes_unavailable: true, dispatch_date: dispatchDate, dispatch_period: dispatchPeriod } : null;
      }
      const errorMessage = error instanceof Error && error.message
        ? error.message
        : "Impossible de charger les changements Data Manager.";
      setSourceChanges((currentState) => {
        if (
          shouldPreserveVerifiedPresentation
          && currentState.dispatchDate === dispatchDate
          && currentState.dispatchPeriod === dispatchPeriod
        ) {
          return {
            ...currentState,
            isLoading: false,
            isVerifying: false,
            unavailableMessage: "",
            needsPlanningContextVerification: true,
            planningContextVerificationErrorMessage: errorMessage,
          };
        }
        if (
          currentState.dispatchDate === dispatchDate
          && currentState.dispatchPeriod === dispatchPeriod
        ) {
          return {
            ...currentState,
            errorMessage,
            unavailableMessage: "",
            hasVerified: false,
            isLoading: false,
            isVerifying: false,
            needsPlanningContextVerification: false,
            needsVerification: true,
            planningContextVerificationErrorMessage: "",
          };
        }
        return {
          ...buildEmptySourceChangesState(dispatchDate, dispatchPeriod),
          errorMessage,
          hasVerified: false,
          isMutating: currentState.isMutating,
          needsVerification: true,
          planningContextVerificationErrorMessage: "",
        };
      });
      return null;
    } finally {
      metadataSyncActivity?.finish();
    }
  }, [
    installSourceChangeMutationResult,
    canApplyDispatchSourceChanges,
    initialResultsData,
    isAccessReady,
    showToast,
    synchronizeSourceChangePortalPublicationEffects,
    workspaceStore,
  ]);
  const refreshSourceChanges = useCallback(({
    action = "",
    background = false,
    dispatchDate = "",
    reportUnavailable = false,
    onDiagnostic,
    refreshKey = "",
    cause = "source_changes_action",
    chainId = "",
    forceFresh = false,
    initialPreview = null,
    origin = "local",
    preserveVerifiedPresentation = false,
  } = {}) => {
    if (!isAccessReady) {
      return Promise.resolve(null);
    }
    const requestedScope = {
      dispatchDate: dispatchDate || getSourceChangesDispatchDate(),
      dispatchPeriod: normalizePortalPublicationDispatchPeriod(activePortalScopeRef.current.dispatchPeriod),
    };
    const scopeKey = `${requestedScope.dispatchDate}|${requestedScope.dispatchPeriod}`;
    let pendingInitialPreview = initialPreview;
    return sourceChangesRefreshCoordinatorRef.current({
      background,
      forceFresh,
      refreshKey,
      reportUnavailable,
      onDiagnostic,
      refresh: ({ background: coalescedBackground, forceFresh: coalescedForceFresh, reportUnavailable: coalescedReportUnavailable }) => {
        const bootstrapPreview = pendingInitialPreview;
        pendingInitialPreview = null;
        return executeSourceChangesRefresh({
          initialPreview: bootstrapPreview,
          background: coalescedBackground,
          forceFresh: coalescedForceFresh,
          performanceContext: { action, cause, chainId, origin },
          preserveVerifiedPresentation,
          requestedScope,
          reportUnavailable: coalescedReportUnavailable,
        });
      },
      scopeKey,
    });
  }, [executeSourceChangesRefresh, getSourceChangesDispatchDate, isAccessReady]);
  const executeEveningSourceChangesRefresh = useCallback(async ({
    background = false,
    forceFresh = false,
    performanceContext = null,
    requestedScope = null,
    reportUnavailable = false,
  } = {}) => {
    if (!sourceChangesMountedRef.current) {
      return SOURCE_CHANGES_REFRESH_UNMOUNTED;
    }
    if (!isAccessReady) {
      return null;
    }
    const dispatchDate = normalizeInlineText(requestedScope?.dispatchDate);
    const dispatchPeriod = "evening";
    const requestId = eveningSourceChangesRequestIdRef.current + 1;
    eveningSourceChangesRequestIdRef.current = requestId;
    const isRequestedScopeCurrent = () => (
      sourceChangesMountedRef.current
      && normalizeInlineText(activePortalScopeRef.current.dispatchDate) === dispatchDate
      && normalizePortalPublicationDispatchPeriod(activePortalScopeRef.current.dispatchPeriod) !== dispatchPeriod
    );
    if (!dispatchDate || !isRequestedScopeCurrent()) {
      return null;
    }

    setEveningSourceChanges((currentState) => ({
      ...(currentState.dispatchDate === dispatchDate ? currentState : buildEmptySourceChangesState(dispatchDate, dispatchPeriod)),
      dispatchDate,
      dispatchPeriod,
      unavailableMessage: "",
      ...(background ? {} : { errorMessage: "", isLoading: true }),
      hasVerified: false,
      isVerifying: true,
      needsPlanningContextVerification: false,
      needsVerification: true,
      planningContextVerificationErrorMessage: "",
    }));

    const safePerformanceContext = performanceContext && typeof performanceContext === "object"
      ? performanceContext
      : {};
    const performanceHeaders = buildSourceChangesPerformanceHeaders(safePerformanceContext);
    let metadataSyncActivity = null;
    try {
      let payload = null;
      let nextSourceChanges = null;
      let metadataSyncPostAttemptBudget = {
        attemptCount: 0,
        maxAttempts: SOURCE_CHANGE_METADATA_SYNC_MAX_POST_ATTEMPTS,
      };
      let metadataSyncAttempts = 0;
      while (metadataSyncAttempts <= SOURCE_CHANGE_METADATA_SYNC_MAX_ATTEMPTS) {
        if (eveningSourceChangesRequestIdRef.current !== requestId || !isRequestedScopeCurrent()) {
          return (reportUnavailable || metadataSyncActivity) ? (isRequestedScopeCurrent() ? SOURCE_CHANGES_REFRESH_SUPERSEDED : payload) : null;
        }
        const sourceChangesUrl = buildDispatchDateQueryUrl(
          "/api/planning/source-changes",
          dispatchDate,
          dispatchPeriod,
        );
        const response = await fetchWithSupabaseAuth(
          forceFresh ? `${sourceChangesUrl}&force_fresh=true` : sourceChangesUrl,
          { headers: performanceHeaders },
        );
        payload = await parseJsonResponse(response, "Impossible de charger les changements Data Manager du Soir.");
        if (eveningSourceChangesRequestIdRef.current !== requestId || !isRequestedScopeCurrent()) {
          return (reportUnavailable || metadataSyncActivity) ? (isRequestedScopeCurrent() ? SOURCE_CHANGES_REFRESH_SUPERSEDED : payload) : null;
        }
        nextSourceChanges = readInitialSourceChanges(
          { source_changes: payload },
          { dispatchDate, dispatchPeriod },
        );
        if (!nextSourceChanges) {
          throw new Error("La réponse des changements Data Manager du Soir est invalide.");
        }
        if (!hasPendingSourceChangeMetadataSync(nextSourceChanges)) {
          break;
        }
        if (!metadataSyncActivity) {
          metadataSyncActivity = await acquireSourceChangeMetadataSyncActivity({ isCurrent: isRequestedScopeCurrent });
          if (!metadataSyncActivity) return null;
          if (eveningSourceChangesRequestIdRef.current !== requestId || !isRequestedScopeCurrent()) {
            return isRequestedScopeCurrent() ? SOURCE_CHANGES_REFRESH_SUPERSEDED : null;
          }
          if (metadataSyncActivity.waited) {
            forceFresh = true;
            continue;
          }
        }
        if (!canApplyDispatchSourceChanges) {
          throw new Error("La synchronisation des métadonnées Data Manager du Soir requiert le droit de modifier le dispatch.");
        }
        if (metadataSyncAttempts >= SOURCE_CHANGE_METADATA_SYNC_MAX_ATTEMPTS) {
          throw new Error("Les métadonnées Data Manager du Soir restent désynchronisées après leur mise à jour.");
        }
        metadataSyncAttempts += 1;
        const metadataOrderIds = nextSourceChanges.metadataSync.orderIds;
        const metadataPreviewRevision = normalizeInlineText(
          nextSourceChanges.metadataSync?.previewRevision || nextSourceChanges.mutationPreviewRevision,
        );
        if (!metadataPreviewRevision) {
          throw new Error("La révision des métadonnées Data Manager du Soir est absente.");
        }
        const metadataSyncKey = [
          dispatchDate,
          dispatchPeriod,
          metadataPreviewRevision,
          metadataOrderIds.join(","),
        ].join("|");
        const postMetadataSyncRequest = async (requestBody) => {
          if (metadataSyncPostAttemptBudget.attemptCount >= metadataSyncPostAttemptBudget.maxAttempts) {
            throw new Error("Trop de mises à jour concurrentes empêchent la synchronisation des métadonnées Data Manager du Soir.");
          }
          metadataSyncPostAttemptBudget.attemptCount += 1;
          const metadataResponse = await fetchWithSupabaseAuth(
            buildDispatchDateQueryUrl(
              "/api/planning/source-changes/sync-metadata",
              dispatchDate,
              dispatchPeriod,
            ),
            {
              body: JSON.stringify(requestBody),
              headers: {
                "Content-Type": "application/json",
                "X-Planning-Diagnostics": "1",
                ...performanceHeaders,
              },
              method: "POST",
            },
          );
          const metadataPayload = await parseJsonResponse(
            metadataResponse,
            "Impossible de synchroniser les métadonnées Data Manager du Soir.",
          );
          const metadataResultPayload = metadataPayload?.result_payload;
          if (metadataResultPayload && typeof metadataResultPayload === "object") {
            writePageDataCacheEntry(
              buildDispatchDateQueryUrl(RESULTS_LATEST_API_URL, dispatchDate, dispatchPeriod),
              metadataResultPayload,
            );
            invalidateShellDataCache();
            dispatchPlanningStateChange("dispatch", {
              actionName: "source-changes:sync-metadata",
              dispatchDate,
              dispatchPeriod,
              performanceChainId: safePerformanceContext.chainId,
              skipLocalSourceChangesRefresh: true,
            });
          }
          await synchronizeSourceChangePortalPublicationEffects([metadataPayload]);
          return metadataPayload;
        };
        const metadataSyncEntry = sourceChangeMetadataSyncInFlightRef.current({
          key: metadataSyncKey,
          request: () => executeExactSelectionSourceChangeRequest({
            canRetry: () => (
              metadataSyncPostAttemptBudget.attemptCount < metadataSyncPostAttemptBudget.maxAttempts
            ),
            initialRequestBody: {
              order_ids: metadataOrderIds,
              preview_revision: metadataPreviewRevision,
            },
            postRequest: postMetadataSyncRequest,
            staleCodes: ["source_changes_metadata_preview_stale"],
          }),
          shared: { postAttemptBudget: metadataSyncPostAttemptBudget },
        });
        metadataSyncPostAttemptBudget = metadataSyncEntry.shared?.postAttemptBudget
          || metadataSyncPostAttemptBudget;
        const metadataPayload = await metadataSyncEntry.promise;
        if (eveningSourceChangesRequestIdRef.current !== requestId || !isRequestedScopeCurrent()) {
          return (reportUnavailable || metadataSyncActivity) ? (isRequestedScopeCurrent() ? SOURCE_CHANGES_REFRESH_SUPERSEDED : payload) : null;
        }
        const mutationSourceChanges = readSourceChangesMutationResponse(metadataPayload, {
          dispatchDate,
          dispatchPeriod,
        });
        if (mutationSourceChanges && !hasPendingSourceChangeMetadataSync(mutationSourceChanges)) {
          payload = metadataPayload.source_changes;
          nextSourceChanges = mutationSourceChanges;
          break;
        }
      }

      if (
        eveningSourceChangesRequestIdRef.current !== requestId
        || !isRequestedScopeCurrent()
      ) {
        return (reportUnavailable || metadataSyncActivity) ? (isRequestedScopeCurrent() ? SOURCE_CHANGES_REFRESH_SUPERSEDED : payload) : null;
      }
      if (!nextSourceChanges || hasPendingSourceChangeMetadataSync(nextSourceChanges)) {
        throw new Error("Soir : les infos sans effet sur la route n'ont pas pu être recopiées. Réessayez la vérification.");
      }
      const nextPendingCount = resolveSourceChangesAggregateCount(nextSourceChanges);
      if (nextPendingCount != null) {
        sourceChangesAggregateCache.write({ dispatchDate, dispatchPeriod }, nextPendingCount);
      }
      setEveningSourceChanges((currentState) => ({
        ...currentState,
        ...nextSourceChanges,
        errorMessage: "",
        unavailableMessage: "",
        hasVerified: true,
        isLoading: false,
        isVerifying: false,
        lastVerifiedPendingCount: nextPendingCount ?? currentState.lastVerifiedPendingCount,
        needsPlanningContextVerification: false,
        needsVerification: false,
        planningContextVerificationErrorMessage: "",
      }));
      return payload;
    } catch (error) {
      if (eveningSourceChangesRequestIdRef.current !== requestId || !isRequestedScopeCurrent()) {
        if (reportUnavailable && !isRequestedScopeCurrent() && isSourceChangesSnapshotUnavailableError(error)) {
          return { source_changes_unavailable: true, dispatch_date: dispatchDate, dispatch_period: dispatchPeriod };
        }
        return metadataSyncActivity && isRequestedScopeCurrent() ? SOURCE_CHANGES_REFRESH_SUPERSEDED : null;
      }
      if (isSourceChangesSnapshotUnavailableError(error)) {
        setEveningSourceChanges({
          ...buildEmptySourceChangesState(dispatchDate, dispatchPeriod),
          unavailableMessage: EVENING_PLANNING_UNAVAILABLE_MESSAGE,
          hasVerified: false,
          isLoading: false,
          isVerifying: false,
          needsPlanningContextVerification: false,
          needsVerification: false,
          planningContextVerificationErrorMessage: "",
          errorMessage: "",
        });
        return reportUnavailable ? { source_changes_unavailable: true, dispatch_date: dispatchDate, dispatch_period: dispatchPeriod } : null;
      }
      setEveningSourceChanges((currentState) => ({
        ...(currentState.dispatchDate === dispatchDate
          ? currentState
          : buildEmptySourceChangesState(dispatchDate, dispatchPeriod)),
        errorMessage: error instanceof Error && error.message
          ? error.message
          : "Impossible de charger les changements Data Manager du Soir.",
        unavailableMessage: "",
        hasVerified: false,
        isLoading: false,
        isVerifying: false,
        needsPlanningContextVerification: false,
        needsVerification: true,
        planningContextVerificationErrorMessage: "",
      }));
      return null;
    } finally {
      metadataSyncActivity?.finish();
    }
  }, [
    canApplyDispatchSourceChanges,
    isAccessReady,
    synchronizeSourceChangePortalPublicationEffects,
  ]);
  const refreshEveningSourceChanges = useCallback(({
    action = "",
    background = false,
    dispatchDate = "",
    reportUnavailable = false,
    onDiagnostic,
    refreshKey = "",
    cause = "source_changes_action",
    chainId = "",
    forceFresh = false,
    origin = "local",
  } = {}) => {
    if (!isAccessReady) {
      return Promise.resolve(null);
    }
    if (normalizePortalPublicationDispatchPeriod(activePortalScopeRef.current.dispatchPeriod) === "evening") {
      return refreshSourceChanges({ action, background, cause, chainId, dispatchDate, forceFresh, origin, reportUnavailable, refreshKey, onDiagnostic });
    }

    const requestedScope = {
      dispatchDate: dispatchDate || getSourceChangesDispatchDate(),
      dispatchPeriod: "evening",
    };
    const scopeKey = `${requestedScope.dispatchDate}|${requestedScope.dispatchPeriod}`;
    return sourceChangesRefreshCoordinatorRef.current({
      background,
      forceFresh,
      refreshKey,
      reportUnavailable,
      refresh: ({ background: coalescedBackground, forceFresh: coalescedForceFresh, reportUnavailable: coalescedReportUnavailable }) => executeEveningSourceChangesRefresh({
        background: coalescedBackground,
        forceFresh: coalescedForceFresh,
        performanceContext: { action, cause, chainId, origin },
        requestedScope,
        reportUnavailable: coalescedReportUnavailable,
      }),
      onDiagnostic,
      scopeKey,
    });
  }, [
    executeEveningSourceChangesRefresh,
    getSourceChangesDispatchDate,
    isAccessReady,
    refreshSourceChanges,
  ]);
  const executeDaySourceChangesCompanionRefresh = useCallback(async ({
    background = false,
    forceFresh = false,
    performanceContext = null,
    requestedScope = null,
    reportUnavailable = false,
  } = {}) => {
    if (!sourceChangesMountedRef.current) {
      return SOURCE_CHANGES_REFRESH_UNMOUNTED;
    }
    if (!isAccessReady) {
      return null;
    }
    const dispatchDate = normalizeInlineText(requestedScope?.dispatchDate);
    const dispatchPeriod = "day";
    const requestId = daySourceChangesRequestIdRef.current + 1;
    daySourceChangesRequestIdRef.current = requestId;
    const isRequestedScopeCurrent = () => (
      normalizeInlineText(activePortalScopeRef.current.dispatchDate) === dispatchDate
      && normalizePortalPublicationDispatchPeriod(activePortalScopeRef.current.dispatchPeriod) === "evening"
    );
    if (!dispatchDate || !isRequestedScopeCurrent()) {
      return null;
    }
    setDaySourceChanges((currentState) => ({
      ...(currentState.dispatchDate === dispatchDate
        ? currentState
        : buildEmptySourceChangesState(dispatchDate, dispatchPeriod)),
      dispatchDate,
      dispatchPeriod,
      unavailableMessage: "",
      ...(background ? {} : { errorMessage: "", isLoading: true }),
      hasVerified: false,
      isVerifying: true,
      needsPlanningContextVerification: false,
      needsVerification: true,
      planningContextVerificationErrorMessage: "",
    }));
    const safePerformanceContext = performanceContext && typeof performanceContext === "object"
      ? performanceContext
      : {};
    try {
      const sourceChangesUrl = buildDispatchDateQueryUrl(
        "/api/planning/source-changes",
        dispatchDate,
        dispatchPeriod,
      );
      const response = await fetchWithSupabaseAuth(
        forceFresh ? `${sourceChangesUrl}&force_fresh=true` : sourceChangesUrl,
        { headers: buildSourceChangesPerformanceHeaders(safePerformanceContext) },
      );
      const payload = await parseJsonResponse(
        response,
        "Impossible de rafraîchir les changements Data Manager du Jour.",
      );
      if (daySourceChangesRequestIdRef.current !== requestId || !isRequestedScopeCurrent()) {
        return reportUnavailable ? (isRequestedScopeCurrent() ? SOURCE_CHANGES_REFRESH_SUPERSEDED : payload) : null;
      }
      const nextSourceChanges = readInitialSourceChanges(
        { source_changes: payload },
        { dispatchDate, dispatchPeriod },
      );
      if (!nextSourceChanges) {
        throw new Error("La réponse des changements Data Manager du Jour est invalide.");
      }
      const needsMetadataSync = hasPendingSourceChangeMetadataSync(nextSourceChanges);
      const nextPendingCount = resolveSourceChangesAggregateCount(nextSourceChanges);
      if (nextPendingCount != null) {
        sourceChangesAggregateCache.write({ dispatchDate, dispatchPeriod }, nextPendingCount);
      }
      setDaySourceChanges((currentState) => ({
        ...currentState,
        ...nextSourceChanges,
        errorMessage: "",
        unavailableMessage: "",
        hasVerified: !needsMetadataSync,
        isLoading: false,
        isVerifying: false,
        lastVerifiedPendingCount: nextPendingCount ?? currentState.lastVerifiedPendingCount,
        needsPlanningContextVerification: false,
        needsVerification: needsMetadataSync,
        planningContextVerificationErrorMessage: "",
      }));
      return payload;
    } catch (error) {
      if (daySourceChangesRequestIdRef.current !== requestId || !isRequestedScopeCurrent()) {
        return null;
      }
      setDaySourceChanges((currentState) => ({
        ...(currentState.dispatchDate === dispatchDate
          ? currentState
          : buildEmptySourceChangesState(dispatchDate, dispatchPeriod)),
        errorMessage: error instanceof Error && error.message
          ? error.message
          : "Impossible de charger les changements Data Manager du Jour.",
        unavailableMessage: "",
        hasVerified: false,
        isLoading: false,
        isVerifying: false,
        needsPlanningContextVerification: false,
        needsVerification: true,
        planningContextVerificationErrorMessage: "",
      }));
      return null;
    }
  }, [isAccessReady]);
  const refreshDaySourceChanges = useCallback(({
    action = "",
    background = false,
    dispatchDate = "",
    reportUnavailable = false,
    onDiagnostic,
    refreshKey = "",
    cause = "source_changes_action",
    chainId = "",
    forceFresh = false,
    origin = "local",
  } = {}) => {
    if (!isAccessReady) {
      return Promise.resolve(null);
    }
    if (normalizePortalPublicationDispatchPeriod(activePortalScopeRef.current.dispatchPeriod) !== "evening") {
      return refreshSourceChanges({ action, background, cause, chainId, dispatchDate, forceFresh, origin, reportUnavailable, refreshKey, onDiagnostic });
    }
    const requestedScope = {
      dispatchDate: dispatchDate || getSourceChangesDispatchDate(),
      dispatchPeriod: "day",
    };
    const scopeKey = `${requestedScope.dispatchDate}|${requestedScope.dispatchPeriod}`;
    return sourceChangesRefreshCoordinatorRef.current({
      background,
      forceFresh,
      refreshKey,
      reportUnavailable,
      refresh: ({ background: coalescedBackground, forceFresh: coalescedForceFresh, reportUnavailable: coalescedReportUnavailable }) => executeDaySourceChangesCompanionRefresh({
        background: coalescedBackground,
        forceFresh: coalescedForceFresh,
        performanceContext: { action, cause, chainId, origin },
        requestedScope,
        reportUnavailable: coalescedReportUnavailable,
      }),
      onDiagnostic,
      scopeKey,
    });
  }, [
    executeDaySourceChangesCompanionRefresh,
    getSourceChangesDispatchDate,
    isAccessReady,
    refreshSourceChanges,
  ]);
  const markSourceChangesForDeferredVerification = useCallback(({
    cause = "movement_verification",
    chainId = "",
    origin = "local",
  } = {}) => {
    const activeScope = { ...activePortalScopeRef.current };
    if (!activeScope.dispatchDate) {
      return;
    }
    sourceChangesRequestIdRef.current += 1;
    deferredSourceChangesVerificationRef.current = {
      cause: normalizeInlineText(cause) || "source_changes_action",
      chainId,
      contextOnly: false,
      dispatchDate: activeScope.dispatchDate,
      dispatchPeriod: activeScope.dispatchPeriod,
      origin,
    };
    const currentState = sourceChangesStateRef.current;
    if (
      currentState?.dispatchDate === activeScope.dispatchDate
      && currentState?.dispatchPeriod === activeScope.dispatchPeriod
    ) {
      sourceChangesStateRef.current = {
        ...currentState,
        hasVerified: false,
        isLoading: false,
        isVerifying: false,
        needsPlanningContextVerification: false,
        needsVerification: true,
        planningContextVerificationErrorMessage: "",
      };
    }
    setSourceChanges((currentState) => {
      if (
        currentState.dispatchDate !== activeScope.dispatchDate
        || currentState.dispatchPeriod !== activeScope.dispatchPeriod
      ) {
        return currentState;
      }
      return {
        ...currentState,
        hasVerified: false,
        isLoading: false,
        isVerifying: false,
        needsPlanningContextVerification: false,
        needsVerification: true,
        planningContextVerificationErrorMessage: "",
      };
    });
  }, []);
  const markSourceChangesForDeferredContextVerification = useCallback(({
    cause = "movement_verification",
    chainId = "",
    origin = "local",
  } = {}) => {
    const activeScope = { ...activePortalScopeRef.current };
    if (!activeScope.dispatchDate) {
      return;
    }
    sourceChangesRequestIdRef.current += 1;
    deferredSourceChangesVerificationRef.current = {
      cause: normalizeInlineText(cause) || "movement_verification",
      chainId,
      contextOnly: true,
      dispatchDate: activeScope.dispatchDate,
      dispatchPeriod: activeScope.dispatchPeriod,
      origin,
    };
    const markContextPending = (currentState) => {
      if (
        currentState?.dispatchDate !== activeScope.dispatchDate
        || currentState?.dispatchPeriod !== activeScope.dispatchPeriod
      ) {
        return currentState;
      }
      return {
        ...currentState,
        isLoading: false,
        isVerifying: false,
        needsPlanningContextVerification: true,
        planningContextVerificationErrorMessage: "",
        planningContextVerificationVersion: Number(
          currentState.planningContextVerificationVersion || 0,
        ) + 1,
      };
    };
    sourceChangesStateRef.current = markContextPending(sourceChangesStateRef.current);
    setSourceChanges(markContextPending);
  }, []);
  const verifySourceChangesOnOpen = useCallback(() => {
    const currentState = sourceChangesStateRef.current;
    if (openSourceChangesVerificationInFlightRef.current) {
      return openSourceChangesVerificationInFlightRef.current;
    }
    if (
      currentState?.isLoading === true
      || currentState?.isVerifying === true
    ) {
      return Promise.resolve(null);
    }
    if (
      currentState?.needsVerification !== true
      && currentState?.needsPlanningContextVerification !== true
      && currentState?.hasVerified === true
    ) {
      return Promise.resolve(null);
    }

    const activeScope = activePortalScopeRef.current;
    const pendingContext = deferredSourceChangesVerificationRef.current;
    const hasMatchingDeferredContext = pendingContext
      && pendingContext.dispatchDate === activeScope.dispatchDate
      && pendingContext.dispatchPeriod === activeScope.dispatchPeriod;
    const preserveVerifiedPresentation = currentState?.needsPlanningContextVerification === true
      && currentState?.hasVerified === true
      && currentState?.needsVerification !== true
      && !normalizeInlineText(currentState?.errorMessage);
    const refreshPromise = refreshSourceChanges({
      action: hasMatchingDeferredContext ? "" : "source_changes_open",
      background: true,
      cause: hasMatchingDeferredContext ? pendingContext.cause : "manual_open",
      chainId: hasMatchingDeferredContext ? pendingContext.chainId : createPerformanceCorrelationId(),
      forceFresh: true,
      origin: hasMatchingDeferredContext ? pendingContext.origin : "local",
      preserveVerifiedPresentation,
    }).finally(() => {
      if (openSourceChangesVerificationInFlightRef.current === refreshPromise) {
        openSourceChangesVerificationInFlightRef.current = null;
      }
      const latestPendingContext = deferredSourceChangesVerificationRef.current;
      if (
        latestPendingContext
        && latestPendingContext !== pendingContext
        && latestPendingContext.dispatchDate === activePortalScopeRef.current.dispatchDate
        && latestPendingContext.dispatchPeriod === activePortalScopeRef.current.dispatchPeriod
      ) {
        Promise.resolve().then(() => verifySourceChangesOnOpenRef.current?.());
      }
    });
    openSourceChangesVerificationInFlightRef.current = refreshPromise;
    return refreshPromise;
  }, [refreshSourceChanges]);
  verifySourceChangesOnOpenRef.current = verifySourceChangesOnOpen;
  const retrySourceChangesVerification = useCallback(() => {
    const currentState = sourceChangesStateRef.current;
    const preserveVerifiedPresentation = currentState?.needsPlanningContextVerification === true
      && currentState?.hasVerified === true
      && currentState?.needsVerification !== true
      && !normalizeInlineText(currentState?.errorMessage);
    return refreshSourceChanges({
      action: "source_changes_resume",
      background: true,
      cause: "resume",
      chainId: createPerformanceCorrelationId(),
      forceFresh: true,
      origin: "local",
      preserveVerifiedPresentation,
    });
  }, [refreshSourceChanges]);
  const retryDaySourceChangesVerification = useCallback(() => (
    refreshDaySourceChanges({
      action: "source_changes_resume",
      background: true,
      cause: "resume",
      chainId: createPerformanceCorrelationId(),
      forceFresh: true,
      origin: "local",
    })
  ), [refreshDaySourceChanges]);
  const verifyDaySourceChangesOnOpen = useCallback(() => {
    const currentState = daySourceChangesStateRef.current;
    if (currentState?.isLoading === true || currentState?.isVerifying === true) {
      return Promise.resolve(null);
    }
    if (
      currentState?.hasVerified === true
      && currentState?.needsVerification !== true
      && !normalizeInlineText(currentState?.errorMessage)
    ) {
      return Promise.resolve(null);
    }
    return retryDaySourceChangesVerification();
  }, [retryDaySourceChangesVerification]);
  const retryEveningSourceChangesVerification = useCallback(() => (
    refreshEveningSourceChanges({
      action: "source_changes_resume",
      background: true,
      cause: "resume",
      chainId: createPerformanceCorrelationId(),
      forceFresh: true,
      origin: "local",
    })
  ), [refreshEveningSourceChanges]);
  const verifyEveningSourceChangesOnOpen = useCallback(() => {
    const currentState = eveningSourceChangesStateRef.current;
    if (currentState?.isLoading === true || currentState?.isVerifying === true) {
      return Promise.resolve(null);
    }
    if (
      currentState?.hasVerified === true
      && currentState?.needsVerification !== true
      && !normalizeInlineText(currentState?.errorMessage)
    ) {
      return Promise.resolve(null);
    }
    if (
      normalizeInlineText(currentState?.unavailableMessage)
      && currentState?.needsVerification !== true
      && !normalizeInlineText(currentState?.errorMessage)
    ) {
      // L'absence de planning Soir est un état neutre déjà connu; le refresh
      // reprend automatiquement quand la révision runtime change via onEnsure.
      return Promise.resolve(null);
    }
    return retryEveningSourceChangesVerification();
  }, [retryEveningSourceChangesVerification]);
  const ensureEveningSourceChanges = useCallback(() => (
    refreshEveningSourceChanges({
      background: eveningSourceChangesStateRef.current?.hasVerified === true
        || Boolean(normalizeInlineText(eveningSourceChangesStateRef.current?.unavailableMessage)),
      cause: "evening_rail_open",
      chainId: createPerformanceCorrelationId(),
      origin: "local",
    })
  ), [refreshEveningSourceChanges]);
  const storeVerifiedSourceChangesPendingCount = useCallback((pendingCount) => {
    const normalizedPendingCount = Number(pendingCount);
    if (!Number.isFinite(normalizedPendingCount)) {
      return;
    }
    const safePendingCount = Math.max(0, Math.round(normalizedPendingCount));
    setSourceChanges((currentState) => {
      if (
        currentState.hasVerified !== true
        || currentState.needsVerification === true
        || !currentState.dispatchDate
      ) {
        return currentState;
      }
      sourceChangesAggregateCache.write({
        dispatchDate: currentState.dispatchDate,
        dispatchPeriod: currentState.dispatchPeriod,
      }, safePendingCount);
      if (currentState.lastVerifiedPendingCount === safePendingCount) {
        return currentState;
      }
      return {
        ...currentState,
        lastVerifiedPendingCount: safePendingCount,
      };
    });
  }, []);
  const commitSourceChangesMutationResponse = useCallback((payload, {
    dispatchDate = "",
    dispatchPeriod = "day",
  } = {}) => {
    const activeScope = activePortalScopeRef.current;
    const normalizedDispatchDate = normalizeInlineText(dispatchDate);
    const normalizedDispatchPeriod = normalizePortalPublicationDispatchPeriod(dispatchPeriod);
    const isActiveScope = normalizeInlineText(activeScope.dispatchDate) === normalizedDispatchDate
      && normalizePortalPublicationDispatchPeriod(activeScope.dispatchPeriod) === normalizedDispatchPeriod;
    const isEveningCompanionScope = normalizeInlineText(activeScope.dispatchDate) === normalizedDispatchDate
      && normalizedDispatchPeriod === "evening"
      && normalizePortalPublicationDispatchPeriod(activeScope.dispatchPeriod) !== "evening";
    const isDayCompanionScope = normalizeInlineText(activeScope.dispatchDate) === normalizedDispatchDate
      && normalizedDispatchPeriod === "day"
      && normalizePortalPublicationDispatchPeriod(activeScope.dispatchPeriod) === "evening";
    if (!isActiveScope && !isDayCompanionScope && !isEveningCompanionScope) {
      return false;
    }

    const nextSourceChanges = readSourceChangesMutationResponse(payload, {
      dispatchDate: normalizedDispatchDate,
      dispatchPeriod: normalizedDispatchPeriod,
    });
    if (!nextSourceChanges || hasPendingSourceChangeMetadataSync(nextSourceChanges)) {
      return false;
    }

    // Une lecture M-à-J commencée avant la mutation ne doit pas pouvoir
    // remplacer ensuite cette prévisualisation, déjà bornée à la nouvelle
    // révision. Le contrôle requestId existant ignorera sa réponse tardive.
    if (isActiveScope) {
      sourceChangesRequestIdRef.current += 1;
    } else if (isEveningCompanionScope) {
      eveningSourceChangesRequestIdRef.current += 1;
    } else {
      daySourceChangesRequestIdRef.current += 1;
    }
    const nextPendingCount = resolveSourceChangesAggregateCount(nextSourceChanges);
    if (nextPendingCount != null) {
      sourceChangesAggregateCache.write({
        dispatchDate: normalizedDispatchDate,
        dispatchPeriod: normalizedDispatchPeriod,
      }, nextPendingCount);
    }
    if (isActiveScope) {
      deferredSourceChangesVerificationRef.current = null;
    }
    const setScopedSourceChanges = isActiveScope
      ? setSourceChanges
      : isEveningCompanionScope
        ? setEveningSourceChanges
        : setDaySourceChanges;
    setScopedSourceChanges((currentState) => ({
      ...currentState,
      ...nextSourceChanges,
      errorMessage: "",
      hasVerified: true,
      isLoading: false,
      isVerifying: false,
      lastVerifiedPendingCount: nextPendingCount ?? currentState.lastVerifiedPendingCount,
      needsPlanningContextVerification: false,
      needsVerification: false,
      planningContextVerificationErrorMessage: "",
    }));
    return true;
  }, []);
  const refreshDispatchSnapshots = useCallback(async ({ background = false } = {}) => {
    if (!isAccessReady) {
      return null;
    }
    const dispatchDate = getSourceChangesDispatchDate();
    const requestedScope = { ...activePortalScopeRef.current };
    const isRequestedScopeCurrent = () => activePortalScopeRef.current.dispatchDate === requestedScope.dispatchDate;
    const requestId = dispatchSnapshotsRequestIdRef.current + 1;
    dispatchSnapshotsRequestIdRef.current = requestId;
    if ((!canSaveDispatchSnapshot && !canRestoreDispatchSnapshot) || !dispatchDate) {
      setDispatchSnapshots((currentState) => ({
        ...currentState,
        dispatchDate,
        items: [],
        isLoading: false,
        restorePreviewById: {},
      }));
      return null;
    }
    if (!background) {
      setDispatchSnapshots((currentState) => ({
        ...(currentState.dispatchDate === dispatchDate
          ? currentState
          : {
              dispatchDate,
              items: [],
              isSaving: false,
              previewingSnapshotId: "",
              restoringSnapshotId: "",
              deletingSnapshotId: "",
              restorePreviewById: {},
            }),
        dispatchDate,
        isLoading: true,
      }));
    }
    try {
      const response = await fetchWithSupabaseAuth(buildDispatchDateQueryUrl("/api/planning/snapshots", dispatchDate));
      const payload = await parseJsonResponse(response, "Impossible de charger les sauvegardes dispatch.");
      if (dispatchSnapshotsRequestIdRef.current !== requestId || !isRequestedScopeCurrent()) {
        return null;
      }
      setDispatchSnapshots((currentState) => ({
        ...currentState,
        dispatchDate,
        items: Array.isArray(payload?.items) ? payload.items.filter(Boolean) : [],
        isLoading: false,
        restorePreviewById: background ? currentState.restorePreviewById : {},
      }));
      return payload;
    } catch {
      if (dispatchSnapshotsRequestIdRef.current !== requestId || !isRequestedScopeCurrent()) {
        return null;
      }
      setDispatchSnapshots((currentState) => {
        if (background && currentState.dispatchDate === dispatchDate) {
          return {
            ...currentState,
            isLoading: false,
          };
        }
        return {
          ...currentState,
          dispatchDate,
          items: [],
          isLoading: false,
          restorePreviewById: {},
        };
      });
      return null;
    }
  }, [
    canRestoreDispatchSnapshot,
    canSaveDispatchSnapshot,
    getSourceChangesDispatchDate,
    isAccessReady,
  ]);

  visibilityWorkspaceRefreshRef.current = () => {
    if (typeof document !== "undefined" && document.visibilityState !== "visible") {
      return;
    }
    // Des retours d'onglet rapprochés réutilisent le GET déjà en cours : chaque
    // appel reconstruit tout l'aperçu M-à-J côté serveur.
    if (canPublishDriverPortal && portalPublicationsInFlightRef.current?.scopeKey !== activePortalScopeKey) {
      void refreshPortalPublications({ background: true });
    }
    if (canSaveDispatchSnapshot || canRestoreDispatchSnapshot) {
      void refreshDispatchSnapshots({ background: true });
    }
  };

  const requestVisibilityWorkspaceRefresh = useCallback(() => {
    const runRefresh = () => visibilityWorkspaceRefreshRef.current?.();
    if (deferPlanningBackgroundWorkUntilIdle(
      visibilityWorkspaceRefreshKeyRef.current,
      runRefresh,
    )) {
      return;
    }
    runRefresh();
  }, []);

  useEffect(() => () => {
    cancelDeferredPlanningBackgroundWork(visibilityWorkspaceRefreshKeyRef.current);
  }, []);

  const refreshDispatchRestorePreview = useCallback(async (snapshotId) => {
    const dispatchDate = getSourceChangesDispatchDate();
    const safeSnapshotId = normalizeInlineText(snapshotId);
    if (!canRestoreDispatchSnapshot || !dispatchDate || !safeSnapshotId) {
      return;
    }
    setDispatchSnapshots((currentState) => ({
      ...currentState,
      previewingSnapshotId: safeSnapshotId,
    }));
    try {
      const restorePreview = await fetchDispatchSnapshotRestorePreview(safeSnapshotId, dispatchDate);
      setDispatchSnapshots((currentState) => ({
        ...currentState,
        previewingSnapshotId: "",
        restorePreviewById: {
          ...(currentState.restorePreviewById || {}),
          [safeSnapshotId]: restorePreview,
        },
      }));
    } catch {
      setDispatchSnapshots((currentState) => ({
        ...currentState,
        previewingSnapshotId: "",
        restorePreviewById: {
          ...(currentState.restorePreviewById || {}),
          [safeSnapshotId]: buildDispatchRestorePreviewError(),
        },
      }));
    }
  }, [canRestoreDispatchSnapshot, getSourceChangesDispatchDate]);

  useEffect(() => {
    if (!isAccessReady) {
      return;
    }
    if (canApplyDispatchSourceChanges) {
      const pendingBootstrap = sourceChangesBootstrapSkipRef.current;
      const pendingDeferredVerification = deferredSourceChangesVerificationRef.current;
      const hasMatchingSourceChangesBootstrap = pendingBootstrap?.scopeKey === activePortalScopeKey
        && pendingBootstrap.source === initialSourceChangesSource;
      const hasMatchingDeferredVerification = pendingDeferredVerification?.dispatchDate === activePortalDispatchDate
        && pendingDeferredVerification?.dispatchPeriod === activePortalDispatchPeriod;
      if (!hasMatchingSourceChangesBootstrap && !hasMatchingDeferredVerification) {
        sourceChangesBootstrapSkipRef.current = null;
        void refreshSourceChanges({
          cause: "bootstrap", origin: "local", initialPreview: initialSourceChangesSource,
        }).then((payload) => {
          // Meuneries absorbées après la réponse d'ouverture : recharger aussitôt la vue de cette révision,
          // sauf si la synchronisation recharge déjà la vue.
          const previewRuntimeRevision = normalizeInlineText(payload?.runtime_revision);
          const displayedRuntimeRevision = normalizeInlineText(workspaceStore.getState()?.resultsData?.runtime_revision);
          if (
            previewRuntimeRevision && displayedRuntimeRevision && previewRuntimeRevision !== displayedRuntimeRevision
            && !dispatchReloadingRef.current
          ) {
            invalidatePageDataCacheEntries([RESULTS_LATEST_API_URL]);
            dispatchPlanningStateChange("dispatch", {
              actionName: "source-changes:reload",
              dispatchDate: activePortalDispatchDate,
              dispatchPeriod: activePortalDispatchPeriod,
              skipLocalSourceChangesRefresh: true,
              revalidateCurrentView: true,
            });
          }
        });
      }
    } else {
      sourceChangesBootstrapSkipRef.current = null;
      sourceChangesRequestIdRef.current += 1;
      daySourceChangesRequestIdRef.current += 1;
      eveningSourceChangesRequestIdRef.current += 1;
      setSourceChanges(buildEmptySourceChangesState(activePortalDispatchDate, activePortalDispatchPeriod));
      setDaySourceChanges(buildEmptySourceChangesState(activePortalDispatchDate, "day"));
      setEveningSourceChanges(buildEmptySourceChangesState(activePortalDispatchDate, "evening"));
    }
    if (canSaveDispatchSnapshot || canRestoreDispatchSnapshot) {
      const pendingBootstrap = dispatchSnapshotsBootstrapSkipRef.current;
      const hasMatchingBootstrap = pendingBootstrap?.dispatchDate === activePortalDispatchDate
        && pendingBootstrap.source === initialDispatchSnapshotsSource;
      // A matching loader payload remains authoritative until the scope or
      // loader source changes; visibility and mutations refresh explicitly.
      if (!hasMatchingBootstrap) {
        dispatchSnapshotsBootstrapSkipRef.current = null;
        void refreshDispatchSnapshots();
      }
    } else {
      dispatchSnapshotsBootstrapSkipRef.current = null;
      dispatchSnapshotsRequestIdRef.current += 1;
      setDispatchSnapshots((currentState) => ({
        ...currentState,
        dispatchDate: activePortalDispatchDate,
        items: [],
        isLoading: false,
        restorePreviewById: {},
      }));
    }
  }, [
    activePortalDispatchDate,
    activePortalDispatchPeriod,
    activePortalScopeKey,
    canApplyDispatchSourceChanges,
    canRestoreDispatchSnapshot,
    canSaveDispatchSnapshot,
    initialDispatchSnapshotsSource,
    initialSourceChangesSource,
    isAccessReady,
    refreshDispatchSnapshots,
    refreshSourceChanges,
    workspaceStore,
  ]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState !== "visible") {
        return;
      }
      requestVisibilityWorkspaceRefresh();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [requestVisibilityWorkspaceRefresh]);

  const refreshEveningCompanionFromPlanningEvent = useCallback((detail, source = "") => {
    const eventDispatchDate = normalizeInlineText(detail?.dispatchDate || detail?.dispatch_date);
    const eventDispatchPeriod = normalizePortalPublicationDispatchPeriod(
      detail?.dispatchPeriod || detail?.dispatch_period,
    );
    if (
      !canApplyDispatchSourceChanges
      || detail?.skipLocalSourceChangesRefresh === true
      || eventDispatchDate !== normalizeInlineText(activePortalScopeRef.current.dispatchDate)
      || eventDispatchPeriod !== "evening"
      || normalizePortalPublicationDispatchPeriod(activePortalScopeRef.current.dispatchPeriod) === "evening"
    ) {
      return;
    }
    void refreshEveningSourceChanges({
      background: true,
      cause: "planning_event",
      chainId: normalizeInlineText(detail?.performanceChainId),
      origin: source || "local",
    });
  }, [canApplyDispatchSourceChanges, refreshEveningSourceChanges]);

  useEffect(() => {
    return subscribePlanningStateChange((detail) => {
      const source = normalizeInlineText(detail?.source).toLowerCase();
      const isDataManagerChange = source === "data-manager";
      refreshEveningCompanionFromPlanningEvent(detail, source);
      if (
        canPublishDriverPortal
        && isPlanningStateChangeInScope(detail, activePortalScopeRef.current)
      ) {
        const incomingPortalPublications = detail?.portalPublications
          || detail?.portal_publications
          || null;
        // Chauffeur ou camion changé dans le Gantt : le destinataire du portail, donc « Publier », change avec lui.
        const shouldRefreshPortalPublications = isDataManagerChange
          || detail?.refreshPortalPublications === true
          || GANTT_FLEET_EDIT_SECTIONS.has(normalizeInlineText(detail?.actionName))
          || Boolean(incomingPortalPublications);
        if (shouldRefreshPortalPublications) {
          if (incomingPortalPublications) {
            handlePortalPublicationsChange(incomingPortalPublications);
          } else {
            void refreshPortalPublications();
          }
        }
      }
      // Changement de chauffeur ou de camion dans le Gantt : relecture et actualisation automatique.
      if (source === "dispatch" && detail?.fromBroadcast !== true && canApplyDispatchSourceChanges
        && GANTT_FLEET_EDIT_SECTIONS.has(normalizeInlineText(detail?.actionName))
        && isPlanningStateChangeInScope(detail, activePortalScopeRef.current)
        && refreshTruckChangesCreatedByEditRef.current?.(detail)) {
        return;
      }
      if (source === "dispatch" && detail?.fromBroadcast !== true) finishHandedOffToast(updateToast, detail?.loadingToast);
      const requestedSourceChangePeriods = readRequestedSourceChangeRefreshPeriods(detail);
      if (requestedSourceChangePeriods.length > 0) {
        const eventDispatchDate = normalizeInlineText(
          detail?.dispatchDate || detail?.dispatch_date,
        );
        const activeDispatchDate = normalizeInlineText(
          activePortalScopeRef.current.dispatchDate,
        );
        const eventMatchesActiveDate = !eventDispatchDate
          || !activeDispatchDate
          || eventDispatchDate === activeDispatchDate;
        if (
          canApplyDispatchSourceChanges
          && detail?.skipLocalSourceChangesRefresh !== true
          && eventMatchesActiveDate
        ) {
          const activeDispatchPeriod = normalizePortalPublicationDispatchPeriod(
            activePortalScopeRef.current.dispatchPeriod,
          );
          const refreshOptions = {
            background: true,
            cause: "planning_event_fleet",
            chainId: normalizeInlineText(detail?.performanceChainId),
            origin: source || "local",
          };
          if (requestedSourceChangePeriods.includes(activeDispatchPeriod)) {
            void refreshSourceChanges(refreshOptions);
          }
          if (
            activeDispatchPeriod !== "day"
            && requestedSourceChangePeriods.includes("day")
          ) {
            void refreshDaySourceChanges(refreshOptions);
          }
          if (
            activeDispatchPeriod !== "evening"
            && requestedSourceChangePeriods.includes("evening")
          ) {
            void refreshEveningSourceChanges(refreshOptions);
          }
        }
        if (
          isDataManagerChange
          && (canSaveDispatchSnapshot || canRestoreDispatchSnapshot)
        ) {
          void refreshDispatchSnapshots();
        }
        // The explicit period list owns this event. Do not let the generic
        // classifier enqueue a second refresh for the active scope.
        return;
      }
      const refreshDecision = classifySourceChangesPlanningEvent(
        detail,
        activePortalScopeRef.current,
      );
      if (refreshDecision.behavior === "ignore") {
        return;
      }

      if (refreshDecision.behavior === "defer_context") {
        if (canApplyDispatchSourceChanges) {
          markSourceChangesForDeferredContextVerification(refreshDecision);
        }
        return;
      }
      if (refreshDecision.behavior === "defer") {
        if (canApplyDispatchSourceChanges) {
          markSourceChangesForDeferredVerification(refreshDecision);
        }
        return;
      }
      if (canApplyDispatchSourceChanges) {
        void refreshSourceChanges({
          cause: refreshDecision.cause,
          chainId: refreshDecision.chainId,
          origin: refreshDecision.origin,
        });
      }
      if (isDataManagerChange) {
        if (canSaveDispatchSnapshot || canRestoreDispatchSnapshot) {
          void refreshDispatchSnapshots();
        }
      }
    });
  }, [
    canApplyDispatchSourceChanges,
    canPublishDriverPortal,
    canRestoreDispatchSnapshot,
    canSaveDispatchSnapshot,
    handlePortalPublicationsChange,
    markSourceChangesForDeferredVerification,
    markSourceChangesForDeferredContextVerification,
    refreshDispatchSnapshots,
    refreshDaySourceChanges,
    refreshEveningSourceChanges,
    refreshPortalPublications,
    refreshEveningCompanionFromPlanningEvent,
    refreshSourceChanges,
    updateToast,
  ]);

  useEffect(() => {
    const state = workspaceStore.getState();
    if (!state) {
      return;
    }

    state.renderFleetColumn = () => {
      syncWorkspaceUi();
    };
  }, [syncWorkspaceUi, workspaceElements, workspaceStore]);


  const planningMutation = usePlanningMutation({
    onPortalPublicationsChange: handlePortalPublicationsChange,
    onPortalPublicationsRefresh: refreshPortalPublications,
    onWorkspaceRender(selection = null) {
      if (selection && (selection.assignment !== undefined || selection.trip !== undefined)) {
        renderCurrentSelection(selection.assignment || null, selection.trip || null);
        return;
      }

      syncWorkspaceUi();
    },
    protectAfterReassignment,
    reassignmentRebuildMode,
    workspaceStore,
  });

  const isAutoRefreshWaitingForFirstChecks = isProtectedOrderAutoRefreshWaitingForFirstChecks(
    protectedOrderAutoRefreshSettledScopesRef.current,
    {
      canPublishPortal: canPublishDriverPortal,
      canVerifySourceChanges: canApplyDispatchSourceChanges,
      portalPublications,
      scopeKey: activePortalScopeKey,
      sourceChanges,
    },
  );

  useLayoutEffect(() => {
    protectedOrderAutoRefreshPlanningMutationRef.current = planningMutation;
    protectedOrderAutoRefreshPortalPublicationsRef.current = portalPublications;
    protectedOrderAutoRefreshSourceChangesBusyRef.current = isSourceChangesBusy
      || isAutoRefreshWaitingForFirstChecks;
  }, [isAutoRefreshWaitingForFirstChecks, isSourceChangesBusy, planningMutation, portalPublications]);

  useEffect(() => {
    protectedOrderAutoRefreshMountedRef.current = true;
    if (typeof document === "undefined") {
      return () => {
        protectedOrderAutoRefreshMountedRef.current = false;
      };
    }

    const handleVisibilityChange = () => {
      setIsDocumentVisible(document.visibilityState === "visible");
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      protectedOrderAutoRefreshMountedRef.current = false;
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  const currentProtectedOrderAutoRefreshResultsData = workspaceStore.getState()?.resultsData
    || initialResultsData
    || null;
  const protectedOrderAutoRefreshCandidateTriggerKey = JSON.stringify(
    selectProtectedOrderAutoRefreshCandidates(currentProtectedOrderAutoRefreshResultsData || {}, {
      dispatchDate: activePortalDispatchDate,
    }).map((candidate) => candidate.attemptKey),
  );
  const protectedOrderAutoRefreshPublicationTriggerKey = JSON.stringify(
    (Array.isArray(portalPublications?.routes) ? portalPublications.routes : [])
      .map((route) => [normalizeInlineText(route?.truckId || route?.truck_id), normalizeInlineText(route?.status)])
      .sort((left, right) => left[0].localeCompare(right[0])),
  );

  const runProtectedOrderAutoRefresh = useCallback(async () => {
    if (
      protectedOrderAutoRefreshRunningRef.current
      || protectedOrderAutoRefreshSourceChangesBusyRef.current
    ) {
      return;
    }

    protectedOrderAutoRefreshRunningRef.current = true;
    try {
      while (protectedOrderAutoRefreshMountedRef.current) {
        if (typeof document !== "undefined" && document.visibilityState !== "visible") {
          break;
        }
        if (protectedOrderAutoRefreshSourceChangesBusyRef.current) {
          break;
        }

        const currentWorkspaceState = workspaceStore.getState();
        if (!currentWorkspaceState) {
          // Le bootstrap initialise le store juste après le premier rendu. Ne
          // consommons pas la tentative avant que la mutation puisse démarrer.
          break;
        }
        const currentResultsData = currentWorkspaceState?.resultsData || initialResultsData || null;
        const currentDispatchDate = normalizeInlineText(activePortalScopeRef.current.dispatchDate)
          || normalizeInlineText(currentResultsData?.dispatch_date);
        if (!isProtectedOrderAutoRefreshDispatchOpen(currentResultsData, currentWorkspaceState, currentDispatchDate)) {
          break;
        }

        const currentPlanningMutation = protectedOrderAutoRefreshPlanningMutationRef.current;
        const nextCandidate = selectProtectedOrderAutoRefreshCandidates(currentResultsData || {}, {
          dispatchDate: currentDispatchDate,
        }).find((candidate) => {
          if (
            protectedOrderAutoRefreshAttemptedKeysRef.current.has(candidate.attemptKey)
            || hasStoredProtectedOrderAutoRefreshAttempt(candidate.attemptKey)
            || isProtectedOrderAutoRefreshTruckPublished(
              protectedOrderAutoRefreshPortalPublicationsRef.current,
              candidate.truckId,
            )
            || currentPlanningMutation?.isTruckMutationPending?.(candidate.truckId)
          ) {
            return false;
          }
          return true;
        });
        if (!nextCandidate || typeof currentPlanningMutation?.refreshTruckDataPreserveOrderAuto !== "function") {
          break;
        }
        if (protectedOrderAutoRefreshSourceChangesBusyRef.current) {
          break;
        }

        protectedOrderAutoRefreshAttemptedKeysRef.current.add(nextCandidate.attemptKey);
        storeProtectedOrderAutoRefreshAttempt(nextCandidate.attemptKey);
        await currentPlanningMutation.refreshTruckDataPreserveOrderAuto(nextCandidate);
      }
    } finally {
      protectedOrderAutoRefreshRunningRef.current = false;
    }
  }, [initialResultsData, workspaceStore]);

  useEffect(() => {
    if (
      !isAccessReady
      || !canMutatePlanning
      || !isDocumentVisible
      || isSourceChangesBusy
      || isAutoRefreshWaitingForFirstChecks
      || portalPublications?.isLoading === true
      || protectedOrderAutoRefreshCandidateTriggerKey === "[]"
    ) {
      return;
    }

    void runProtectedOrderAutoRefresh();
  }, [
    canMutatePlanning,
    isAccessReady,
    isAutoRefreshWaitingForFirstChecks,
    isDocumentVisible,
    isSourceChangesBusy,
    portalPublications?.isLoading,
    protectedOrderAutoRefreshCandidateTriggerKey,
    protectedOrderAutoRefreshPublicationTriggerKey,
    runProtectedOrderAutoRefresh,
    workspaceState.workspaceUi,
  ]);

  const tripSelection = useTripSelection({
    renderAssignmentCards,
    renderCurrentSelection,
    setAssignmentOpenState,
    workspaceStore,
  });

  // Lien externe (ex. Geotab) : ?truck=<id> sélectionne ce camion dès que le dispatch est chargé.
  const requestedTruckId = new URLSearchParams(location.search).get("truck") || "";
  useEffect(() => {
    if (!requestedTruckId || !workspaceStore.getState()?.assignmentsById?.has(requestedTruckId)) {
      return;
    }
    tripSelection.selectAssignment(requestedTruckId);
    const nextSearchParams = new URLSearchParams(location.search);
    nextSearchParams.delete("truck");
    navigate({ pathname: location.pathname, search: `?${nextSearchParams.toString()}`, hash: location.hash }, { replace: true });
  }, [requestedTruckId, workspaceState.workspaceUi]);

  const navigateToDispatchDay = (dispatchDate, { resetDetailView = false } = {}) => {
    const safeDispatchDate = typeof dispatchDate === "string" ? dispatchDate.trim() : "";
    if (!safeDispatchDate) {
      return;
    }

    writeLastDispatchContext({
      dispatch_date: safeDispatchDate,
      dispatch_month: safeDispatchDate.slice(0, 7),
    });

    const nextSearchParams = new URLSearchParams(location.search);
    nextSearchParams.set("dispatch_date", safeDispatchDate);
    nextSearchParams.set("dispatch_month", safeDispatchDate.slice(0, 7));

    if (resetDetailView) {
      clearResultsDetailViewSearchParam(nextSearchParams);
    }

    if (resetDetailView && detailViewState?.activeView && detailViewState.activeView !== "fleet") {
      detailViewState.activateView?.("fleet", {
        scroll: false,
        syncLocation: false,
      });
    }

    // Ancien paramètre de la vue « jour » retirée : nettoyé des anciens liens.
    nextSearchParams.delete("dispatchView");

    return navigate(
      {
        pathname: location.pathname,
        search: nextSearchParams.toString() ? `?${nextSearchParams.toString()}` : "",
      },
      { replace: false },
    );
  };

  const resolveCurrentDispatchDate = useCallback(
    () => normalizeInlineText(readDispatchDateFromSearch(location.search)) || readWorkspaceDispatchDate(workspaceStore),
    [location.search, workspaceStore],
  );

  const handleSelectDispatchDay = useCallback(async (dispatchDate) => {
    const safeDispatchDate = normalizeInlineText(dispatchDate);
    if (!safeDispatchDate) {
      return;
    }
    navigateToDispatchDay(safeDispatchDate, { resetDetailView: true });
  }, [navigateToDispatchDay]);

  const handleChangeDispatchMonth = (dispatchMonth) => {
    const safeDispatchMonth = typeof dispatchMonth === "string" ? dispatchMonth.trim() : "";
    if (!safeDispatchMonth) {
      return;
    }

    writeLastDispatchContext({
      dispatch_date: readDispatchDateFromSearch(location.search),
      dispatch_month: safeDispatchMonth,
    });

    const nextSearchParams = new URLSearchParams(location.search);
    nextSearchParams.set("dispatch_month", safeDispatchMonth);
    navigate(
      {
        pathname: location.pathname,
        search: nextSearchParams.toString() ? `?${nextSearchParams.toString()}` : "",
      },
      { replace: false },
    );
  };

  const handleToggleDispatchArchive = (isOpen) => {
    const nextSearchParams = new URLSearchParams(location.search);
    if (isOpen) {
      nextSearchParams.set("dispatchArchive", "1");
    } else {
      nextSearchParams.delete("dispatchArchive");
    }

    navigate(
      {
        pathname: location.pathname,
        search: nextSearchParams.toString() ? `?${nextSearchParams.toString()}` : "",
      },
      { replace: false },
    );
  };

  const sourceChangesVerificationErrorMessage = normalizeInlineText(sourceChanges.errorMessage);
  const isSourceChangesVerificationPending = canApplyDispatchSourceChanges
    && Boolean(activePortalDispatchDate)
    && (
      sourceChanges.hasVerified !== true
      || sourceChanges.needsVerification === true
      || sourceChanges.dispatchDate !== activePortalDispatchDate
      || sourceChanges.dispatchPeriod !== activePortalDispatchPeriod
      || sourceChanges.isLoading === true
      || sourceChanges.isMutating === true
      || sourceChanges.isVerifying === true
    );
  const shouldFailClosedPortalPublication = canApplyDispatchSourceChanges
    && Boolean(activePortalDispatchDate)
    && (isSourceChangesVerificationPending || Boolean(sourceChangesVerificationErrorMessage));
  const portalPublicationsWithSourceChangesGuard = useMemo(() => {
    const portalPublicationsWithOutputPolicy = {
      ...portalPublications,
      truckOutputPolicy: sourceChanges.truckOutputPolicy,
    };
    if (!shouldFailClosedPortalPublication) {
      return portalPublicationsWithOutputPolicy;
    }
    const publicationUnavailableReason = sourceChangesVerificationErrorMessage
      ? "Impossible de vérifier les M-à-J commandes. Réessayez la vérification avant de publier."
      : "Vérification des M-à-J commandes en cours. La publication est temporairement bloquée.";
    return {
      ...portalPublicationsWithOutputPolicy,
      publicationAllowed: false,
      publicationUnavailableReason,
      routes: (Array.isArray(portalPublications.routes) ? portalPublications.routes : []).map((route) => ({
        ...route,
        publicationAllowed: false,
        publicationUnavailableReason,
      })),
    };
  }, [
    portalPublications,
    shouldFailClosedPortalPublication,
    sourceChanges.truckOutputPolicy,
    sourceChangesVerificationErrorMessage,
  ]);

  const workspaceInteractions = useResultsWorkspaceInteractions({
    canApplyDispatchSourceChanges,
    canCloseDispatchDay,
    canInvalidateDispatchDay,
    canResetDispatchRevalidation,
    canManageDispatcherFreeze,
    canPublishDriverPortal,
    canMutatePlanning,
    canReopenDispatchDay,
    canRestoreDispatchSnapshot,
    canSaveDispatchSnapshot,
    canResetHereRouteCache,
    canResetPlanning,
    canDeleteDispatchSnapshot,
    canRunOptimization,
    canManageRouteAlternatives,
    isAccessLoading,
    closeReassignmentPopover,
    detailViewState,
    handleClientSelection,
    mapStateRef,
    onChangeDispatchMonth: handleChangeDispatchMonth,
    onCloseDispatchDaySuccess(dispatchDate) {
      navigateToDispatchDay(dispatchDate);
    },
    onDispatchSnapshotResetSuccess() {
      void refreshDispatchSnapshots({ background: true });
      void refreshPortalPublications({ background: true });
    },
    onReopenDispatchDaySuccess(dispatchDate) {
      return navigateToDispatchDay(dispatchDate);
    },
    onSelectDispatchDay: handleSelectDispatchDay,
    onToggleDispatchArchive: handleToggleDispatchArchive,
    onProtectAfterReassignmentChange: handleProtectAfterReassignmentChange,
    onReassignmentRebuildModeChange: handleReassignmentRebuildModeChange,
    openErrorsPanel,
    planningMutation,
    portalPublications: portalPublicationsWithSourceChangesGuard,
    protectAfterReassignment,
    reassignmentRebuildMode,
    refreshPortalPublications,
    renderCurrentSelection,
    toggleReassignmentPopover,
    tripSelection,
  });

  const getSourceChangesStateForPeriod = useCallback((requestedPeriod = "") => {
    const dispatchPeriod = normalizePortalPublicationDispatchPeriod(
      requestedPeriod || activePortalScopeRef.current.dispatchPeriod,
    );
    return dispatchPeriod === normalizePortalPublicationDispatchPeriod(activePortalScopeRef.current.dispatchPeriod)
      ? sourceChangesStateRef.current
      : dispatchPeriod === "day"
        ? daySourceChangesStateRef.current
        : eveningSourceChangesStateRef.current;
  }, []);
  const refreshSourceChangesForPeriod = useCallback((dispatchPeriod, options = {}) => {
    const normalizedDispatchPeriod = normalizePortalPublicationDispatchPeriod(dispatchPeriod);
    if (options.dispatchDate && options.dispatchDate !== activePortalScopeRef.current.dispatchDate) {
      const dispatchDate = options.dispatchDate;
      return sourceChangesRefreshCoordinatorRef.current({
        scopeKey: `${dispatchDate}|${normalizedDispatchPeriod}`,
        forceFresh: options.forceFresh === true,
        refreshKey: options.refreshKey || "",
        onDiagnostic: options.onDiagnostic,
        refresh: async ({ forceFresh }) => {
          try {
            const url = buildDispatchDateQueryUrl("/api/planning/source-changes", dispatchDate, normalizedDispatchPeriod);
            const response = await fetchWithSupabaseAuth(forceFresh ? `${url}&force_fresh=true` : url, {
              headers: buildSourceChangesPerformanceHeaders(options),
            });
            return await parseJsonResponse(response, "Impossible de recharger les mises à jour du dispatch traité.");
          } catch (error) {
            if (normalizedDispatchPeriod === "evening" && isSourceChangesSnapshotUnavailableError(error)) {
              return { source_changes_unavailable: true, dispatch_date: dispatchDate, dispatch_period: normalizedDispatchPeriod };
            }
            throw error;
          }
        },
      });
    }
    const activeDispatchPeriod = normalizePortalPublicationDispatchPeriod(
      activePortalScopeRef.current.dispatchPeriod,
    );
    if (normalizedDispatchPeriod === activeDispatchPeriod) {
      return refreshSourceChanges(options);
    }
    return normalizedDispatchPeriod === "evening"
      ? refreshEveningSourceChanges(options)
      : refreshDaySourceChanges(options);
  }, [refreshDaySourceChanges, refreshEveningSourceChanges, refreshSourceChanges]);
  const refreshVisibleSourceChangePeriods = useCallback(async (options = {}, operationCapture = null) => {
    const dispatchDate = options.dispatchDate || activePortalScopeRef.current.dispatchDate;
    const dispatchPeriod = options.dispatchPeriod || activePortalScopeRef.current.dispatchPeriod;
    const scopes = sourceChangeMutationScopes(dispatchDate, dispatchPeriod, options.payloads, options.periods);
    const lastPayload = options.payloads?.at(-1);
    const results = await Promise.allSettled(scopes.map((scope) => {
      if (options.reuseCompleteResponse && lastPayload?.source_changes_refresh_required !== true
        && isSourceChangeFinalPreview(lastPayload?.source_changes, scope)) {
        return lastPayload.source_changes;
      }
      return refreshSourceChangesForPeriod(scope.dispatchPeriod, {
        ...options, ...scope, reportUnavailable: true,
        onDiagnostic: (details) => {
          operationCapture?.recordStepDetails("refresh", details);
          options.onDiagnostic?.(details);
        },
      });
    }));
    const ok = results.every((result, index) => result.status === "fulfilled"
      && isAcceptableSourceChangeRefreshResult(result.value, scopes[index]));
    if (!ok) {
      if (results.some((result, index) => result.status === "fulfilled" && result.value
        && !isAcceptableSourceChangeRefreshResult(result.value, scopes[index]))) {
        operationCapture?.recordStepDetails("refresh", { result_code: "invalid_preview" });
      }
      operationCapture?.fail({});
      operationCapture?.reportProgress("refresh", "failed");
    } else operationCapture?.reportProgress("refresh", "completed");
    return {
      ok,
      previews: results.flatMap((result, index) => (
        result.status === "fulfilled" && isSourceChangeFinalPreview(result.value, scopes[index])
          ? [result.value]
          : []
      )),
    };
  }, [refreshSourceChangesForPeriod]);

  const completeSourceChangeMutation = useCallback(async ({
    dispatchDate, dispatchPeriod, performanceContext, capture, payloads, toast, expectedCounts, periods, applied = true, installSavedResult = applied, recoverSavedResult = false,
  }) => {
    periods = sourceChangeMutationRefreshPeriods(payloads, periods);
    const businessStatus = capture.getStatus();
    let installation = installSavedResult ? installSourceChangeMutationResult(payloads?.at(-1), {
      dispatchDate, dispatchPeriod, performanceContext, capture,
    }) : { needsRecovery: false };
    if (payloads?.length && !installation.previewCommitted && !installation.needsRecovery) {
      installation.previewCommitted = commitSourceChangesMutationResponse(payloads.at(-1), { dispatchDate, dispatchPeriod });
    }
    const options = {
      background: true, ...performanceContext, dispatchDate, dispatchPeriod, payloads, periods,
      forceFresh: true, refreshKey: `${performanceContext.chainId}:final`, reuseCompleteResponse: !installation.needsRecovery,
    };
    sourceChangeMutationScopes(dispatchDate, dispatchPeriod, payloads, periods).forEach((scope) => sourceChangesAggregateCache.invalidate(scope));
    capture.reportProgress("refresh");
    // Saving has already succeeded. A failed read must never replay the POST.
    const refreshResult = await refreshVisibleSourceChangePeriods(options, capture);
    const finalPreview = refreshResult.previews.find((preview) =>
      (preview.dispatch_date || preview.dispatchDate) === dispatchDate
      && (preview.dispatch_period || preview.dispatchPeriod) === dispatchPeriod);
    const finalRevision = normalizeInlineText(finalPreview?.runtime_revision || finalPreview?.runtimeRevision);
    // The list GET can observe a newer state. Only a results GET can install
    // that state; never stamp its revision onto the earlier POST result.
    const recoveryReason = installation.needsRecovery ? "installation_failed"
      : refreshResult.previews.some((preview) => preview?.result_refresh_required === true) ? "preview_requires_result"
        : !installation.runtimeRevision ? "saved_revision_missing"
          : finalPreview && !finalRevision ? "final_revision_missing"
            : finalRevision && finalRevision !== installation.runtimeRevision ? "preview_revision_changed" : "";
    let displayRefreshRequired = installation.needsRecovery
      || refreshResult.previews.some((preview) => preview?.result_refresh_required === true);
    if (recoverSavedResult && recoveryReason) {
      capture.reportProgress("reconciliation", "running", { result_code: recoveryReason, read_count: 1 });
      try {
        const response = await fetchWithSupabaseAuth(
          buildDispatchDateQueryUrl(RESULTS_LATEST_API_URL, dispatchDate, dispatchPeriod),
          { method: "GET", cache: "no-store", headers: buildSourceChangesPerformanceHeaders(performanceContext) },
        );
        const result = await parseJsonResponse(response, "Impossible de récupérer la vue Dispatch enregistrée.");
        installation = installSourceChangeMutationResult({ result_payload: result, _planning_diagnostics: result?._planning_diagnostics }, {
          dispatchDate, dispatchPeriod, performanceContext, capture,
        });
        displayRefreshRequired = installation.needsRecovery || !installation.runtimeRevision;
        if (!displayRefreshRequired) capture.setStatus(businessStatus);
      } catch {
        displayRefreshRequired = true;
        capture.reportProgress("display", "warning", { result_code: "result_recovery_failed" });
      }
      capture.reportProgress("reconciliation", displayRefreshRequired ? "warning" : "completed");
    } else if (recoverSavedResult) {
      capture.reportProgress("reconciliation", "completed", { result_code: "saved_revision_installed", read_count: 0 });
    }
    const { failurePhase, ...completionToast } = buildSourceChangeCompletionToast({
      toast, payloads: recoverSavedResult && !displayRefreshRequired
        ? payloads.map((payload) => ({ ...payload, result_refresh_required: false })) : payloads,
      refreshResult: recoverSavedResult && !displayRefreshRequired
        ? { ...refreshResult, previews: refreshResult.previews.map((preview) => ({ ...preview, result_refresh_required: false })) } : refreshResult,
      expectedCounts, captureStatus: capture.getStatus(), applied, displayRefreshRequired,
      onReload: async () => {
        const readContext = { ...performanceContext, action: "source_changes_read", chainId: createPerformanceCorrelationId() };
        invalidatePageDataCacheEntries([RESULTS_LATEST_API_URL]);
        dispatchPlanningStateChange("dispatch", {
          actionName: "source-changes:reload", dispatchDate, dispatchPeriod, skipLocalSourceChangesRefresh: true,
          revalidateCurrentView: displayRefreshRequired, performanceChainId: readContext.chainId,
        });
        return refreshVisibleSourceChangePeriods({ ...options, ...readContext,
          refreshKey: `${readContext.chainId}:read`, reuseCompleteResponse: false });
      },
    });
    if (failurePhase && failurePhase !== "refresh") capture.reportProgress(failurePhase, "warning");
    if (applied) capture.setStatus(completionToast.type === "warning" ? "partial" : "success");
    return completionToast;
  }, [commitSourceChangesMutationResponse, installSourceChangeMutationResult, refreshVisibleSourceChangePeriods]);

  const refreshLoadingConstraintMutationSelection = useCallback(async ({
    dispatchDate,
    dispatchPeriod,
    performanceContext,
    capture,
    truckId,
  }) => {
    const refreshedPayload = await refreshSourceChangesForPeriod(dispatchPeriod, {
      background: true,
      forceFresh: true,
      ...performanceContext, dispatchDate,
      onDiagnostic: (details) => capture?.recordStepDetails("refresh", details),
    });
    const refreshedSourceChanges = readInitialSourceChanges(
      { source_changes: refreshedPayload },
      { dispatchDate, dispatchPeriod },
    );
    const refreshedDetail = (Array.isArray(refreshedSourceChanges?.loadingConstraintChanges?.details)
      ? refreshedSourceChanges.loadingConstraintChanges.details
      : []).find((item) => normalizeInlineText(item?.truckId) === truckId);
    return {
      previewRevision: normalizeInlineText(refreshedSourceChanges?.previewRevision),
      truckId: normalizeInlineText(refreshedDetail?.truckId),
      changeRevision: normalizeInlineText(refreshedDetail?.changeRevision),
    };
  }, [refreshSourceChangesForPeriod]);

  const applySourceChangeMutation = useCallback(async (action, requestedPeriod = "") => {
    const dispatchPeriod = normalizePortalPublicationDispatchPeriod(
      requestedPeriod || activePortalScopeRef.current.dispatchPeriod,
    );
    const sourceChanges = getSourceChangesStateForPeriod(dispatchPeriod);
    const dispatchDate = getSourceChangesDispatchDate();
    if (!dispatchDate || dispatchReloadingRef.current || sourceChangesMutationInFlightRef.current || sourceChanges.isMutating) {
      return null;
    }
    const performanceContext = {
      action: SOURCE_CHANGE_MUTATION_PERFORMANCE_ACTIONS[action] || "",
      cause: "source_changes_action",
      chainId: createPerformanceCorrelationId(),
      origin: "local",
    };
    let loadingToastId = null;
    const capture = createOperationFeedbackCapture({
      action: performanceContext.action, operationEventId: performanceContext.chainId,
      dispatchDate, dispatchPeriods: [dispatchPeriod], resourceKind: "order",
      ...sourceChangeCaptureResourceContext(action, sourceChanges),
      onProgress: (nextProgress) => { if (loadingToastId) updateToast(loadingToastId, { progress: nextProgress }); },
    });
    const reportProgress = capture.reportProgress;
    const withMutationEvidence = (toast) => {
      const evidence = capture.getMutationCaptureContext();
      return evidence ? { ...toast, captureContext: { ...toast.captureContext, ...evidence } } : toast;
    };
    const showOperationToast = (toast) => showToast(capture.recordToast(withMutationEvidence(toast)));
    const updateOperationToast = (id, toast) => updateToast(id, capture.recordToast(withMutationEvidence(toast)));

    if (!isSourceChangesActionPreviewFresh(sourceChanges, { dispatchDate, dispatchPeriod })) {
      showOperationToast({
        duration: 6000,
        message: "Ouvrez M-à-J commandes et attendez la fin de la vérification avant d'appliquer cette action.",
        title: "Vérification M-à-J requise",
        type: "warning",
      });
      capture.setStatus("refused");
      if (!sourceChangesMutationInFlightRef.current) capture.finish();
      return null;
    }
    const clickedReassignmentRebuildMode = normalizeDispatchReassignmentMode(
      reassignmentRebuildMode,
    );

    const performanceHeaders = buildSourceChangesPerformanceHeaders(performanceContext);
    const refreshSourceChangesForMutation = (options = {}) => refreshSourceChangesForPeriod(dispatchPeriod, {
      forceFresh: true,
      ...options,
      ...performanceContext, dispatchDate,
      onDiagnostic: (details) => capture.recordStepDetails("refresh", details),
    });
    let mutationRequestAttemptCount = 0;
    const postSourceChangeRequest = async (requestEndpoint, requestBody, fallbackMessage) => {
      mutationRequestAttemptCount += 1;
      const hasRequestBody = requestBody !== undefined;
      capture.recordMutationPhase("request");
      const response = await fetchWithSupabaseAuth(
        buildDispatchDateQueryUrl(requestEndpoint, dispatchDate, dispatchPeriod),
        {
          ...(hasRequestBody ? { body: JSON.stringify(requestBody) } : {}),
          headers: {
            ...(hasRequestBody ? { "Content-Type": "application/json" } : {}),
            "X-Planning-Diagnostics": "1",
            ...DEFERRED_SOURCE_CHANGES_HEADERS,
            ...performanceHeaders,
          },
          method: "POST",
        },
      );
      capture.recordMutationResponse(response);
      const payload = await parseJsonResponse(response, fallbackMessage, capture);
      capture.markApplied();
      capture.recordMutationPhase("install_result");
      installSourceChangeMutationResult(payload, { dispatchDate, dispatchPeriod, performanceContext, capture });
      return payload;
    };
    const readCompleteMutationSourceChanges = (responsePayload) => {
      const nextSourceChanges = readSourceChangesMutationResponse(responsePayload, {
        dispatchDate,
        dispatchPeriod,
      });
      return nextSourceChanges && !hasPendingSourceChangeMetadataSync(nextSourceChanges)
        ? nextSourceChanges
        : null;
    };
    const refreshExactMutationSelection = async (selectOrderIds) => {
      const refreshedPayload = await refreshSourceChangesForMutation({ background: true });
      const refreshedSourceChanges = readInitialSourceChanges(
        { source_changes: refreshedPayload },
        { dispatchDate, dispatchPeriod },
      );
      return {
        order_ids: typeof selectOrderIds === "function"
          ? selectOrderIds(refreshedSourceChanges)
          : [],
        preview_revision: normalizeInlineText(refreshedSourceChanges?.mutationPreviewRevision),
      };
    };
    const resolveNextMutationPreviewRevision = async (responsePayload) => {
      const completeSourceChanges = readCompleteMutationSourceChanges(responsePayload);
      const responsePreviewRevision = normalizeInlineText(completeSourceChanges?.mutationPreviewRevision);
      if (responsePreviewRevision) {
        return responsePreviewRevision;
      }
      const refreshedPayload = await refreshSourceChangesForMutation({ background: true });
      const refreshedRevision = normalizeInlineText(refreshedPayload?.mutation_preview_revision);
      if (refreshedRevision) {
        return refreshedRevision;
      }
      const refreshError = new Error(
        "La prévisualisation M-à-J a changé. Rafraîchissez-la avant de poursuivre le retrait.",
      );
      markSourceChangesRefreshPerformed(refreshError);
      throw refreshError;
    };
    const removedEndOfDayLoadDetails = Array.isArray(sourceChanges.details?.removed_end_of_day_loads)
      ? sourceChanges.details.removed_end_of_day_loads.filter(
        (detail) => normalizeInlineText(detail?.marker_key) && isRemovableOrphanEndOfDayLoadDetail(detail),
      )
      : [];
    const missingDispatchOrderDetails = Array.isArray(sourceChanges.details?.missing_dispatch_orders)
      ? sourceChanges.details.missing_dispatch_orders.filter((detail) => normalizeInlineText(detail?.order_id))
      : [];
    const dateChangedOrderDetails = Array.isArray(sourceChanges.details?.date_changed_remove)
      ? sourceChanges.details.date_changed_remove.filter((detail) => normalizeInlineText(detail?.order_id))
      : [];
    const dateChangedEndOfDayLoadDetails = Array.isArray(sourceChanges.details?.removed_end_of_day_loads)
      ? sourceChanges.details.removed_end_of_day_loads.filter(
        (detail) => normalizeInlineText(detail?.marker_key) && isDateChangedEndOfDayLoadDetail(detail),
      )
      : [];
    const recoverableExecutionDetails = Array.isArray(sourceChanges.details?.inconsistent_execution_removals)
      ? sourceChanges.details.inconsistent_execution_removals.filter(
        (detail) => detail?.recovery_eligible === true && normalizeInlineText(detail?.order_id),
      )
      : [];
    const endpoint = action === "import-new"
      ? "/api/planning/source-changes/import-new"
      : action === "import-suspendable-errors"
        ? "/api/planning/source-changes/import-suspendable-errors"
        : action === "remove-date-changed"
          ? "/api/planning/source-changes/remove-date-changed"
          : action === "repair-ghost-orders"
            ? "/api/planning/source-changes/remove-ghost-orders"
          : action === "restore-executions"
            ? "/api/planning/source-changes/restore-executions"
            : "/api/planning/source-changes/remove-modified";
    const loadingTitle = action === "import-new"
      ? "Import vers Suspendues"
      : action === "import-suspendable-errors"
        ? "Import erreurs vers Suspendues"
        : action === "remove-date-changed"
          ? "Dates déplacées"
          : action === "repair-ghost-orders"
            ? "Retrait du dispatch"
          : action === "restore-executions"
            ? "Restauration des exécutions"
            : "Suspension en cours";
    const loadingMessage = action === "import-new"
      ? "Nous importons les nouvelles commandes vers Suspendues."
      : action === "import-suspendable-errors"
        ? "Nous transférons les commandes en erreur métier vers Suspendues."
        : action === "remove-date-changed"
          ? "Nous retirons les commandes changées de date du dispatch courant et recalculons les camions touchés."
          : action === "repair-ghost-orders"
            ? "Nous retirons les commandes supprimées et les Chargés PM orphelins, puis recalculons les camions touchés."
          : action === "restore-executions"
            ? "Nous restaurons les confirmations Portail dans leur tournée d'origine."
            : "Nous retirons les commandes modifiées de la flotte active et recalculons le planning courant.";
    if (!setSourceChangesMutationBusy(true)) {
      capture.setStatus("refused");
      capture.finish();
      return null;
    }
    const sourceChangeResponsePayloads = [];
    let sourceChangePortalPublicationEffects = null;
    let hasShownPortalPublicationInvalidationToast = false;
    publishSourceChangesStatusNotice({
      message: loadingMessage,
      status: "running",
      title: loadingTitle,
    });
    try {
      loadingToastId = showOperationToast({
        message: loadingMessage,
        title: loadingTitle,
        type: "loading",
      });
      let mutationPayload = null;
      let removedEndOfDayLoadCount = 0;
      let removedDateChangedPmCount = 0;
      let updatedAlreadySuspendedOrderCount = 0;
      if (action === "repair-ghost-orders") {
        const previewRevision = normalizeInlineText(sourceChanges.mutationPreviewRevision);
        const previewDispatchDate = normalizeInlineText(sourceChanges.dispatchDate);
        const previewDispatchPeriod = normalizePortalPublicationDispatchPeriod(sourceChanges.dispatchPeriod);
        if (
          sourceChanges.hasVerified !== true
          || !previewRevision
          || previewDispatchDate !== dispatchDate
          || previewDispatchPeriod !== dispatchPeriod
        ) {
          throw new Error("Rafraîchissez M-à-J commandes avant de retirer ces éléments du dispatch.");
        }
        const shouldRemoveNormalGhosts = missingDispatchOrderDetails.length > 0;
        if (!shouldRemoveNormalGhosts && !removedEndOfDayLoadDetails.length) {
          throw new Error("Aucun élément à retirer du dispatch.");
        }
        let currentRemovalPreviewRevision = previewRevision;
        if (shouldRemoveNormalGhosts) {
          const ghostOrderIds = missingDispatchOrderDetails
            .map((detail) => normalizeInlineText(detail?.order_id))
            .filter((orderId, index, values) => orderId && values.indexOf(orderId) === index);
          const ghostRequestBody = {
            dispatch_date: dispatchDate,
            order_ids: ghostOrderIds,
            preview_revision: previewRevision,
          };
          mutationPayload = await executeExactSelectionSourceChangeRequest({
            initialRequestBody: ghostRequestBody,
            postRequest: (requestBody) => postSourceChangeRequest(
              endpoint,
              requestBody,
              "Impossible de retirer les commandes supprimées du dispatch.",
            ),
            refreshSelection: () => refreshExactMutationSelection((refreshedSourceChanges) => (
              Array.isArray(refreshedSourceChanges?.details?.missing_dispatch_orders)
                ? refreshedSourceChanges.details.missing_dispatch_orders
                  .map((detail) => normalizeInlineText(detail?.order_id))
                  .filter((orderId, index, values) => orderId && values.indexOf(orderId) === index)
                : []
            )),
          });
          sourceChangeResponsePayloads.push(mutationPayload);
          capture.markApplied();
          if (removedEndOfDayLoadDetails.length > 0) {
            currentRemovalPreviewRevision = await resolveNextMutationPreviewRevision(mutationPayload);
          }
        }
        for (const [detailIndex, detail] of removedEndOfDayLoadDetails.entries()) {
          mutationPayload = await postSourceChangeRequest(
            "/api/planning/source-changes/remove-orphan-end-of-day-loads",
            buildRemovedEndOfDayLoadRemovalPayload(
              detail,
              dispatchDate,
              currentRemovalPreviewRevision,
            ),
            "Impossible de retirer le Chargé PM orphelin.",
          );
          sourceChangeResponsePayloads.push(mutationPayload);
          capture.markApplied();
          removedEndOfDayLoadCount += 1;
          if (detailIndex < removedEndOfDayLoadDetails.length - 1) {
            currentRemovalPreviewRevision = await resolveNextMutationPreviewRevision(mutationPayload);
          }
        }
      } else if (action === "remove-date-changed") {
        let currentRemovalPreviewRevision = normalizeInlineText(sourceChanges.mutationPreviewRevision);
        if (
          sourceChanges.hasVerified !== true
          || !currentRemovalPreviewRevision
          || normalizeInlineText(sourceChanges.dispatchDate) !== dispatchDate
          || normalizePortalPublicationDispatchPeriod(sourceChanges.dispatchPeriod) !== dispatchPeriod
        ) {
          throw new Error("Rafraîchissez M-à-J commandes avant de retirer les dates déplacées.");
        }
        if (!dateChangedOrderDetails.length && !dateChangedEndOfDayLoadDetails.length) {
          throw new Error("Aucune date déplacée à retirer du dispatch.");
        }
        if (dateChangedOrderDetails.length > 0) {
          const dateChangedOrderIds = dateChangedOrderDetails
            .map((detail) => normalizeInlineText(detail?.order_id))
            .filter((orderId, index, values) => orderId && values.indexOf(orderId) === index);
          mutationPayload = await executeExactSelectionSourceChangeRequest({
            initialRequestBody: {
              dispatch_date: dispatchDate,
              order_ids: dateChangedOrderIds,
              preview_revision: currentRemovalPreviewRevision,
            },
            postRequest: (requestBody) => postSourceChangeRequest(
              endpoint,
              requestBody,
              "Impossible de retirer les commandes dont la date a changé.",
            ),
            refreshSelection: () => refreshExactMutationSelection((refreshedSourceChanges) => (
              Array.isArray(refreshedSourceChanges?.details?.date_changed_remove)
                ? refreshedSourceChanges.details.date_changed_remove
                  .map((detail) => normalizeInlineText(detail?.order_id))
                  .filter((orderId, index, values) => orderId && values.indexOf(orderId) === index)
                : []
            )),
          });
          sourceChangeResponsePayloads.push(mutationPayload);
          capture.markApplied();
          if (dateChangedEndOfDayLoadDetails.length > 0) {
            currentRemovalPreviewRevision = await resolveNextMutationPreviewRevision(mutationPayload);
          }
        }
        for (const [detailIndex, detail] of dateChangedEndOfDayLoadDetails.entries()) {
          mutationPayload = await postSourceChangeRequest(
            "/api/planning/source-changes/remove-orphan-end-of-day-loads",
            buildRemovedEndOfDayLoadRemovalPayload(
              detail,
              dispatchDate,
              currentRemovalPreviewRevision,
              "date_changed",
            ),
            "Impossible de retirer le Chargé PM associé à la date déplacée.",
          );
          sourceChangeResponsePayloads.push(mutationPayload);
          capture.markApplied();
          removedDateChangedPmCount += 1;
          if (detailIndex < dateChangedEndOfDayLoadDetails.length - 1) {
            currentRemovalPreviewRevision = await resolveNextMutationPreviewRevision(mutationPayload);
          }
        }
      } else if (action === "restore-executions") {
        const recoverableOrderIds = recoverableExecutionDetails
          .map((detail) => normalizeInlineText(detail?.order_id))
          .filter((orderId, index, values) => orderId && values.indexOf(orderId) === index);
        if (!recoverableOrderIds.length) {
          throw new Error("Aucune exécution admissible à restaurer.");
        }
        mutationPayload = await executeExactSelectionSourceChangeRequest({
          initialRequestBody: {
            dispatch_date: dispatchDate,
            order_ids: recoverableOrderIds,
            preview_revision: normalizeInlineText(sourceChanges.mutationPreviewRevision),
          },
          postRequest: (requestBody) => postSourceChangeRequest(
            endpoint,
            requestBody,
            "Impossible de restaurer les exécutions.",
          ),
          refreshSelection: () => refreshExactMutationSelection((refreshedSourceChanges) => (
            Array.isArray(refreshedSourceChanges?.details?.inconsistent_execution_removals)
              ? refreshedSourceChanges.details.inconsistent_execution_removals
                .filter((detail) => detail?.recovery_eligible === true)
                .map((detail) => normalizeInlineText(detail?.order_id))
                .filter((orderId, index, values) => orderId && values.indexOf(orderId) === index)
              : []
          )),
        });
        sourceChangeResponsePayloads.push(mutationPayload);
        capture.markApplied();
      } else if (action === "remove-modified") {
        const removeModifiedRequest = buildRemoveModifiedSourceChangeRequest(
          sourceChanges,
          clickedReassignmentRebuildMode,
        );
        updatedAlreadySuspendedOrderCount = removeModifiedRequest.selectedDetails.filter(
          (detail) => detail?.already_suspended === true,
        ).length;
        if (
          sourceChanges.hasVerified !== true
          || !removeModifiedRequest.requestBody.preview_revision
          || removeModifiedRequest.hasIncompleteOrderIds
          || removeModifiedRequest.orderIds.length === 0
        ) {
          throw new Error("Rafraîchissez M-à-J commandes avant de retirer les commandes modifiées.");
        }
        const postRemoveModifiedRequest = async (requestBody) => {
          return postSourceChangeRequest(
            endpoint,
            requestBody,
            "Impossible de retirer les commandes modifiées.",
          );
        };
        mutationPayload = await executeRemoveModifiedSourceChangeRequest({
          initialRequest: removeModifiedRequest,
          postRequest: postRemoveModifiedRequest,
          refreshSourceChanges: async () => {
            const refreshedPayload = await refreshSourceChangesForMutation({ background: true });
            return readInitialSourceChanges(
              { source_changes: refreshedPayload },
              { dispatchDate, dispatchPeriod },
            );
          },
        });
        sourceChangeResponsePayloads.push(mutationPayload);
        capture.markApplied();
      } else {
        const payload = await postSourceChangeRequest(
          endpoint,
          undefined,
          "Impossible d'appliquer les changements Data Manager.",
        );
        sourceChangeResponsePayloads.push(payload);
        capture.markApplied();
        mutationPayload = payload;
      }
      reportProgress("process", "completed");
      const payload = mutationPayload;
      capture.recordMutationPhase("publication_effects");
      sourceChangePortalPublicationEffects = await synchronizeSourceChangePortalPublicationEffects(
        sourceChangeResponsePayloads,
      );
      const mutationCounts = payload?.source_changes?.mutation_counts || {};
      const removedMissingDispatchOrderCount = action === "repair-ghost-orders"
        ? sourceChangeResponsePayloads.reduce(
          (total, responsePayload) => total + Number(
            responsePayload?.source_changes?.mutation_counts?.removed_missing_dispatch_orders || 0,
          ),
          0,
        )
        : 0;
      const removedDateChangedOrderCount = action === "remove-date-changed"
        ? sourceChangeResponsePayloads.reduce(
          (total, responsePayload) => total + Number(
            responsePayload?.source_changes?.mutation_counts?.removed_date_changed || 0,
          ),
          0,
        )
        : 0;
      const removedDispatchItemsLabel = [
        removedMissingDispatchOrderCount > 0
          ? `${removedMissingDispatchOrderCount} commande(s)`
          : "",
        removedEndOfDayLoadCount > 0
          ? `${removedEndOfDayLoadCount} Chargé(s) PM`
          : "",
      ].filter(Boolean).join(" et ") || "Les éléments sélectionnés";
      const message = action === "remove-date-changed"
        ? `${removedDateChangedOrderCount} commande(s) et ${removedDateChangedPmCount} Chargé(s) PM associé(s) retiré(s) pour changement de date.`
        : action === "remove-modified"
        ? formatRemoveModifiedSuccessMessage(
          mutationCounts.suspended_modified,
          updatedAlreadySuspendedOrderCount,
        )
        : action === "import-suspendable-errors"
          ? payload?.message || `${Number(mutationCounts.imported_suspendable_errors || 0)} commande(s) transférée(s) vers Suspendues.`
          : action === "repair-ghost-orders"
            ? `${removedDispatchItemsLabel} retiré(s) du dispatch.`
          : action === "restore-executions"
            ? payload?.message || `${Number(mutationCounts.restored_executions || 0)} exécution(s) restaurée(s).`
          : payload?.message || `${Number(mutationCounts.imported_new || 0)} nouvelle(s) commande(s) importée(s).`;
      const countOrders = (details = []) => new Set(details.map((detail) => detail?.order_id || detail?.orderId).filter(Boolean)).size;
      const expectedCounts = action === "repair-ghost-orders" ? { removed_missing_dispatch_orders: countOrders(missingDispatchOrderDetails), removed_end_of_day_loads: removedEndOfDayLoadDetails.length }
        : action === "remove-date-changed" ? { removed_date_changed: countOrders(dateChangedOrderDetails), removed_end_of_day_loads: dateChangedEndOfDayLoadDetails.length }
        : action === "remove-modified" ? { suspended_modified: buildRemoveModifiedSourceChangeRequest(sourceChanges, clickedReassignmentRebuildMode).orderIds.length }
        : action === "import-new" ? { imported_new: countOrders(sourceChanges.details?.new) }
        : action === "import-suspendable-errors" ? { imported_suspendable_errors: sourceChanges.suspendableErrors?.details?.length }
        : action === "restore-executions" ? { restored_executions: countOrders(recoverableExecutionDetails) } : {};
      capture.recordMutationPhase("completion");
      const successToast = await completeSourceChangeMutation({
        dispatchDate, dispatchPeriod, performanceContext, capture, payloads: sourceChangeResponsePayloads, expectedCounts,
        toast: {
          duration: 5000,
          message,
          title: action === "import-new"
            ? loadingTitle
            : action === "import-suspendable-errors"
              ? loadingTitle
              : action === "remove-date-changed"
                ? "Dates retirées"
                : action === "repair-ghost-orders"
                  ? "Retrait terminé"
                : action === "remove-modified" && updatedAlreadySuspendedOrderCount > 0
                  ? "Mise à jour terminée"
                : action === "restore-executions"
                  ? "Exécutions restaurées"
                : "Retrait terminé",
          type: "success",
        },
      });
      if (loadingToastId) {
        updateOperationToast(loadingToastId, successToast);
      } else {
        showOperationToast(successToast);
      }
      publishSourceChangesStatusNotice({
        message: successToast.message,
        planningDiagnostics: payload?._planning_diagnostics || null,
        status: capture.getStatus() === "partial" ? "partial" : "completed",
        title: successToast.title,
      });
      const portalPublicationInvalidationToast = buildPortalPublicationInvalidationToast(
        sourceChangePortalPublicationEffects.invalidatedTruckIds,
      );
      if (portalPublicationInvalidationToast) {
        showToast(portalPublicationInvalidationToast);
        hasShownPortalPublicationInvalidationToast = true;
      }
      capture.recordMutationPhase("completed");
      return payload;
    } catch (error) {
      capture.fail(error);
      const isGroupedRemovalAction = action === "repair-ghost-orders"
        || action === "remove-date-changed";
      const hasPartialRemoval = isGroupedRemovalAction
        && sourceChangeResponsePayloads.length > 0;
      if (!sourceChangePortalPublicationEffects && sourceChangeResponsePayloads.length > 0) {
        sourceChangePortalPublicationEffects = await synchronizeSourceChangePortalPublicationEffects(
          sourceChangeResponsePayloads,
        );
      }
      const errorStatus = Number(error?.status) || 0;
      const errorCode = normalizeInlineText(error?.code);
      const isStaleOrChangedSelection = errorStatus === 404 || [
        "execution_protected",
        "execution_recovery_blocked",
        "no_new_source_changes",
        "no_recoverable_executions",
        "no_suspendable_errors",
        "source_changes_orphan_date_changed",
        "source_changes_orphan_not_removable",
        "source_changes_preview_stale",
      ].includes(errorCode);
      const isAmbiguousMutationFailure = mutationRequestAttemptCount > 0
        && (errorStatus === 0 || errorStatus >= 500);
      const shouldRefreshAfterError = (
        hasPartialRemoval
        || isStaleOrChangedSelection
        || isAmbiguousMutationFailure
      ) && !wasSourceChangesRefreshPerformed(error);
      if (shouldRefreshAfterError) {
        try {
          if (!hasPartialRemoval) await refreshSourceChangesForMutation({ background: true });
        } catch {
          // L'erreur initiale reste la plus utile; la confirmation demeure ouverte.
        }
      }
      const baseErrorMessage = error instanceof Error && error.message
        ? error.message
        : "Impossible d'appliquer les changements Data Manager.";
      const isFastRebuildRejected = normalizeInlineText(error?.code)
        === "source_changes_fast_rebuild_rejected";
      let errorToast = {
        // Same incident as the status notice below: one capture, not two.
        captureContext: buildStatusNoticeAlertCaptureContext({
          planningDiagnostics: error?.payload?._planning_diagnostics || null, source: "planning",
        }),
        duration: 7000,
        message: hasPartialRemoval
          ? `Une partie des éléments a été retirée et enregistrée. ${baseErrorMessage} Rechargez la liste pour vérifier ce qui reste.`
          : baseErrorMessage,
        title: hasPartialRemoval
          ? "Retrait partiel"
          : isFastRebuildRejected
            ? "Retrait interrompu"
            : loadingTitle,
        type: hasPartialRemoval ? "warning" : "error",
        ...(hasPartialRemoval ? {
          actionLabel: "Recharger la liste",
          onAction: () => refreshVisibleSourceChangePeriods({
            background: true, forceFresh: true, ...performanceContext, dispatchDate, dispatchPeriod,
            payloads: sourceChangeResponsePayloads,
          }),
        } : {}),
      };
      if (hasPartialRemoval) errorToast = await completeSourceChangeMutation({
        dispatchDate, dispatchPeriod, performanceContext, capture, payloads: sourceChangeResponsePayloads, toast: errorToast,
      });
      if (loadingToastId) {
        updateOperationToast(loadingToastId, errorToast);
      } else {
        showOperationToast(errorToast);
      }
      publishSourceChangesStatusNotice({
        message: errorToast.message,
        planningDiagnostics: error?.payload?._planning_diagnostics || null,
        status: capture.getStatus() === "partial" ? "partial" : "failed",
        title: errorToast.title,
      });
      if (sourceChangePortalPublicationEffects && !hasShownPortalPublicationInvalidationToast) {
        const portalPublicationInvalidationToast = buildPortalPublicationInvalidationToast(
          sourceChangePortalPublicationEffects.invalidatedTruckIds,
        );
        if (portalPublicationInvalidationToast) {
          showToast(portalPublicationInvalidationToast);
        }
      }
      return null;
    } finally {
      capture.finish();
      setSourceChangesMutationBusy(false);
    }
  }, [installSourceChangeMutationResult, completeSourceChangeMutation, commitSourceChangesMutationResponse, getSourceChangesDispatchDate, getSourceChangesStateForPeriod, reassignmentRebuildMode, refreshSourceChangesForPeriod, refreshVisibleSourceChangePeriods, setSourceChangesMutationBusy, showToast, synchronizeSourceChangePortalPublicationEffects, updateToast]);

  const refreshLoadingConstraintTruck = useCallback(async (detail, requestedPeriod = "") => {
    const dispatchPeriod = normalizePortalPublicationDispatchPeriod(
      requestedPeriod || detail?.dispatchPeriod || activePortalScopeRef.current.dispatchPeriod,
    );
    const dispatchDate = getSourceChangesDispatchDate();
    const sourceChangesState = getSourceChangesStateForPeriod(dispatchPeriod);
    const truckId = normalizeInlineText(detail?.truckId);
    const changeRevision = normalizeInlineText(detail?.changeRevision);
    const recoveryRevision = normalizeInlineText(detail?.recoveryRevision);
    const currentDetail = (Array.isArray(sourceChangesState?.loadingConstraintChanges?.details)
      ? sourceChangesState.loadingConstraintChanges.details
      : []).find((item) => normalizeInlineText(item?.truckId) === truckId);
    const performanceContext = {
      action: "source_changes_refresh_loading_constraints",
      cause: "source_changes_action",
      chainId: createPerformanceCorrelationId(),
      origin: "local",
    };
    let loadingToastId = null;
    const capture = createOperationFeedbackCapture({
      action: performanceContext.action, operationEventId: performanceContext.chainId,
      dispatchDate, dispatchPeriods: [dispatchPeriod], resourceKind: "truck",
      resourceIds: [truckId], resourceLabel: detail?.truckLabel || truckId,
      onProgress: (nextProgress) => { if (loadingToastId) updateToast(loadingToastId, { progress: nextProgress }); },
    });
    const reportProgress = capture.reportProgress;
    const progress = capture.getSnapshot();
    const showOperationToast = (toast) => showToast(capture.recordToast(toast));
    const updateOperationToast = (id, toast) => updateToast(id, capture.recordToast(toast));

    if (
      !canApplyDispatchSourceChanges
      || dispatchReloadingRef.current || sourceChangesMutationInFlightRef.current
      || !dispatchDate
      || !truckId
      || !changeRevision
      || !isSourceChangesActionPreviewFresh(sourceChangesState, { dispatchDate, dispatchPeriod })
      || normalizeInlineText(currentDetail?.changeRevision) !== changeRevision
      || (recoveryRevision && (
        !canMutatePlanning
        || normalizeInlineText(detail?.dispatchDate) !== dispatchDate
        || normalizeInlineText(detail?.previewRevision) !== sourceChangesState.previewRevision
      ))
    ) {
      showOperationToast({
        duration: 6500,
        message: "Les contraintes de ce camion ne correspondent plus à la prévisualisation. Rechargez Mise à jour.",
        title: "Actualisation meunerie périmée",
        type: "warning",
      });
      capture.setStatus("refused");
      if (!sourceChangesMutationInFlightRef.current) capture.finish();
      return null;
    }


    if (!setSourceChangesMutationBusy(true)) {
      capture.setStatus("refused");
      capture.finish();
      return null;
    }
    publishSourceChangesStatusNotice({
      message: `Actualisation des contraintes de meunerie de ${currentDetail?.truckLabel || truckId}.`,
      status: "running",
      title: "Actualisation meunerie",
    });
    try {
      const loadingToast = {
        message: `Recalcul local de ${currentDetail?.truckLabel || truckId}.`,
        title: "Actualisation meunerie",
        type: "loading",
        progress,
      };
      // « Actualiser les meuneries » : un seul toast pour tous les camions.
      loadingToastId = detail?.loadingToastId || showOperationToast(loadingToast);
      if (detail?.loadingToastId) updateOperationToast(loadingToastId, loadingToast);
      const initialRequestBody = {
        dispatch_date: dispatchDate,
        dispatch_period: dispatchPeriod,
        preview_revision: sourceChangesState.previewRevision,
        truck_id: truckId,
        change_revision: changeRevision,
      };
      if (recoveryRevision) initialRequestBody.recovery_revision = recoveryRevision;
      // Un dépassement d'heures passe en temps supplémentaire, sauf pour confirmer une suspension proposée.
      if (!recoveryRevision) initialRequestBody.accept_overtime = true;
      const postRequest = async (requestBody) => {
        const response = await fetchWithSupabaseAuth(
          recoveryRevision
            ? "/api/planning/source-changes/loading-constraints/refresh-with-suspension"
            : "/api/planning/source-changes/loading-constraints/refresh",
          {
            body: JSON.stringify(requestBody),
            headers: {
              "Content-Type": "application/json",
              "X-Planning-Diagnostics": "1",
              ...buildSourceChangesPerformanceHeaders(performanceContext),
            },
            method: "POST",
          },
        );
        return parseJsonResponse(
          response,
          "Impossible d’actualiser les contraintes de meunerie.",
        );
      };
      const payload = recoveryRevision ? await postRequest(initialRequestBody) : await executeLoadingConstraintSourceChangeRequest({
        initialRequestBody,
        postRequest,
        refreshSelection: () => refreshLoadingConstraintMutationSelection({
          dispatchDate,
          dispatchPeriod,
          performanceContext,
          capture,
          truckId,
        }),
      });
      const outcome = normalizeInlineText(payload?.outcome).toLowerCase();
      if (outcome === "confirmation_required") {
        const recovery = readLoadingConstraintRecovery(payload);
        if (!recovery || recoveryRevision || recovery.dispatchDate !== dispatchDate
          || recovery.dispatchPeriod !== dispatchPeriod || recovery.truckId !== truckId
          || recovery.changeRevision !== changeRevision) {
          throw new Error("La proposition de suspension meunerie est incomplète.");
        }
        capture.setStatus("awaiting_confirmation");
        reportProgress("process", "warning");
        clearAppStatusNoticeBySource("planning");
        updateOperationToast(loadingToastId, {
          duration: 4000,
          message: "Vérifiez les commandes proposées avant de confirmer leur déplacement.",
          title: "Confirmation de suspension",
          type: "info",
          progress: null,
        });
        return payload;
      }
      if (outcome === "blocked") {
        capture.setStatus("refused");
        reportProgress("process", "warning");
        const conflicts = (Array.isArray(payload?.conflicts) ? payload.conflicts : []).map((conflict) => ({
          orderId: normalizeInlineText(conflict?.order_id || conflict?.orderId),
          deliveryId: normalizeInlineText(conflict?.delivery_id || conflict?.deliveryId),
          plannedTime: normalizeInlineText(conflict?.planned_time || conflict?.plannedTime),
          newLimit: conflict?.new_limit ?? conflict?.newLimit ?? null,
          constraintType: normalizeInlineText(conflict?.constraint_type || conflict?.constraintType),
          reasonCode: normalizeInlineText(conflict?.reason_code || conflict?.reasonCode),
          message: normalizeInlineText(conflict?.message),
        }));
        const activeScope = activePortalScopeRef.current;
        const isActiveScope = normalizeInlineText(activeScope.dispatchDate) === dispatchDate
          && normalizePortalPublicationDispatchPeriod(activeScope.dispatchPeriod) === dispatchPeriod;
        const setScopedSourceChanges = isActiveScope
          ? setSourceChanges
          : dispatchPeriod === "evening"
            ? setEveningSourceChanges
            : setDaySourceChanges;
        setScopedSourceChanges((currentState) => ({
          ...currentState,
          loadingConstraintChanges: {
            ...currentState.loadingConstraintChanges,
            details: (currentState.loadingConstraintChanges?.details || []).map((item) => (
              normalizeInlineText(item?.truckId) === truckId
                ? { ...item, conflicts, applicationFeedback: normalizeApplicationFeedback(payload.application_feedback) }
                : item
            )),
          },
        }));
        const blockedToast = {
          duration: 8000,
          message: applicationFeedbackMessage(payload.application_feedback) || (conflicts.length > 0
            ? "La tournée n’a pas été modifiée. Seules les commandes réellement en conflit sont affichées dans Mise à jour > Meuneries."
            : normalizeInlineText(payload?.detail) || "La tournée n’a pas été modifiée."),
          title: conflicts.length > 0 ? "Contraintes incompatibles" : "Actualisation non appliquée",
          type: "warning",
        };
        if (loadingToastId) {
          updateOperationToast(loadingToastId, blockedToast);
        } else {
          showOperationToast(blockedToast);
        }
        if (isBusinessApplicationBlock(payload)) {
          clearAppStatusNoticeBySource("planning");
        } else publishSourceChangesStatusNotice({
          message: blockedToast.message,
          status: "failed",
          title: blockedToast.title,
        });
        return payload;
      }
      if (outcome === "applied") capture.markApplied();
      if (outcome !== "applied") {
        throw new Error("La réponse d’actualisation meunerie est incomplète.");
      }

      const overtimeOpened = Array.isArray(payload?.auto_overtime_authorized_truck_ids)
        && payload.auto_overtime_authorized_truck_ids.some((item) => normalizeInlineText(item) === truckId);
      const overtimeClosed = Array.isArray(payload?.auto_overtime_removed_truck_ids)
        && payload.auto_overtime_removed_truck_ids.some((item) => normalizeInlineText(item) === truckId);
      const suspendedCount = Array.isArray(payload?.suspended_order_ids) ? payload.suspended_order_ids.length : 0;
      const scheduleMessage = suspendedCount > 0
        ? `${suspendedCount} commande${suspendedCount > 1 ? "s" : ""} déplacée${suspendedCount > 1 ? "s" : ""} vers Suspendues. La tournée restante a été recalculée.`
        : payload.replay_performed
        ? "Les heures et les totaux du camion ont été recalculés sans déplacer de commande."
        : "Les nouvelles heures de meunerie ont été appliquées sans modifier l’horaire.";
      const successToast = {
        duration: 5000,
        message: overtimeOpened
          ? `${scheduleMessage} Les heures supplémentaires ont été ouvertes automatiquement.`
          : overtimeClosed
            ? `${scheduleMessage} L’autorisation d’heures supplémentaires devenue inutile a été refermée.`
            : scheduleMessage,
        title: "Meunerie actualisée",
        type: "success",
      };
      const finalCompletionToast = await completeSourceChangeMutation({
        dispatchDate, dispatchPeriod, performanceContext, capture, payloads: [payload], periods: [dispatchPeriod], toast: successToast,
      });
      if (loadingToastId) {
        updateOperationToast(loadingToastId, finalCompletionToast);
      } else {
        showOperationToast(finalCompletionToast);
      }
      if (getSourceChangesDispatchDate() === dispatchDate) publishSourceChangesStatusNotice({
        message: finalCompletionToast.message,
        status: capture.getStatus() === "partial" ? "partial" : "completed",
        title: finalCompletionToast.title,
      });
      return payload;
    } catch (error) {
      capture.fail(error);
      if (getSourceChangesDispatchDate() !== dispatchDate) return null;
      if (!wasSourceChangesRefreshPerformed(error)) {
        reportProgress("process", "warning");
        reportProgress("display", "skipped");
        reportProgress("refresh");
        try {
          reportProgress("refresh");
          await refreshSourceChangesForPeriod(dispatchPeriod, {
            background: true,
            forceFresh: true,
            ...performanceContext, dispatchDate,
            onDiagnostic: (details) => capture.recordStepDetails("refresh", details),
          });
        } catch {
          // L'erreur de mutation demeure prioritaire.
        }
      }
      const errorToast = {
        duration: 7500,
        message: error instanceof Error && error.message
          ? error.message
          : "Impossible d’actualiser les contraintes de meunerie.",
        title: error?.status === 409 ? "Proposition à vérifier à nouveau" : "Actualisation meunerie impossible",
        type: error?.status === 409 ? "warning" : "error",
      };
      if (loadingToastId) {
        updateOperationToast(loadingToastId, errorToast);
      } else {
        showOperationToast(errorToast);
      }
      if (error?.status === 409) clearAppStatusNoticeBySource("planning");
      else publishSourceChangesStatusNotice({
        message: errorToast.message,
        status: "failed",
        title: errorToast.title,
      });
      return null;
    } finally {
      capture.finish();
      if (loadingToastId && getSourceChangesDispatchDate() !== dispatchDate) dismissToast(loadingToastId);
      setSourceChangesMutationBusy(false);
    }
  }, [completeSourceChangeMutation, canApplyDispatchSourceChanges, canMutatePlanning, commitSourceChangesMutationResponse, dismissToast, getSourceChangesDispatchDate, getSourceChangesStateForPeriod, refreshLoadingConstraintMutationSelection, refreshSourceChangesForPeriod, setSourceChangesMutationBusy, showToast, updateToast]);

  // « Actualiser les meuneries » : chaque camion comme sa carte, l'un après l'autre. Comme en
  // Réaffectation rapide, un dépassement d'heures ouvre le temps supplémentaire au lieu de bloquer.
  const refreshAllLoadingConstraints = useCallback(async (requestedPeriod = "") => {
    const dispatchPeriod = normalizePortalPublicationDispatchPeriod(requestedPeriod || activePortalScopeRef.current.dispatchPeriod);
    const dispatchDate = getSourceChangesDispatchDate();
    const toastId = showToast({ type: "loading", title: "Actualisation des meuneries", message: "Préparation…", duration: null });
    const tried = new Set();
    let applied = 0;
    for (;;) {
      // La liste est relue après chaque camion : attendre la version à jour.
      for (let wait = 0; wait < 100 && (sourceChangesMutationInFlightRef.current
        || !isSourceChangesActionPreviewFresh(getSourceChangesStateForPeriod(dispatchPeriod), { dispatchDate, dispatchPeriod })); wait += 1) {
        await new Promise((resolve) => { setTimeout(resolve, 100); });
      }
      const next = (getSourceChangesStateForPeriod(dispatchPeriod)?.loadingConstraintChanges?.details || [])
        .find((detail) => !tried.has(detail.truckId) && detail.applicationFeedback?.outcome !== "confirmation_required");
      if (!next || getSourceChangesDispatchDate() !== dispatchDate) break;
      tried.add(next.truckId);
      const payload = await refreshLoadingConstraintTruck({ ...next, loadingToastId: toastId }, dispatchPeriod);
      // Un camion qui ne passe pas garde sa carte ; on continue avec les suivants.
      if (payload?.outcome === "applied") applied += 1;
    }
    const left = (getSourceChangesStateForPeriod(dispatchPeriod)?.loadingConstraintChanges?.details || []).length;
    updateToast(toastId, {
      type: left ? "warning" : "success", progress: null, duration: left ? 8000 : 5000,
      title: !left ? "Meuneries actualisées" : applied ? "Meuneries actualisées en partie" : "Meuneries non actualisées",
      message: `${applied} camion${applied > 1 ? "s" : ""} actualisé${applied > 1 ? "s" : ""}${left ? ` · ${left} à revoir dans Mise à jour` : ""}.`,
    });
  }, [getSourceChangesDispatchDate, getSourceChangesStateForPeriod, refreshLoadingConstraintTruck, showToast, updateToast]);

  const rejectLoadingConstraintTruck = useCallback(async (detail, requestedPeriod = "") => {
    const dispatchPeriod = normalizePortalPublicationDispatchPeriod(
      requestedPeriod || detail?.dispatchPeriod || activePortalScopeRef.current.dispatchPeriod,
    );
    const dispatchDate = getSourceChangesDispatchDate();
    const sourceChangesState = getSourceChangesStateForPeriod(dispatchPeriod);
    const truckId = normalizeInlineText(detail?.truckId);
    const changeRevision = normalizeInlineText(detail?.changeRevision);
    const currentDetail = (Array.isArray(sourceChangesState?.loadingConstraintChanges?.details)
      ? sourceChangesState.loadingConstraintChanges.details
      : []).find((item) => normalizeInlineText(item?.truckId) === truckId);
    const performanceContext = {
      action: "source_changes_reject_loading_constraints",
      cause: "source_changes_action",
      chainId: createPerformanceCorrelationId(),
      origin: "local",
    };
    let loadingToastId = null;
    const capture = createOperationFeedbackCapture({
      action: performanceContext.action, operationEventId: performanceContext.chainId,
      dispatchDate, dispatchPeriods: [dispatchPeriod], resourceKind: "truck",
      resourceIds: [truckId], resourceLabel: detail?.truckLabel || truckId,
      onProgress: (nextProgress) => { if (loadingToastId) updateToast(loadingToastId, { progress: nextProgress }); },
    });
    const reportProgress = capture.reportProgress;
    const showOperationToast = (toast) => showToast(capture.recordToast(toast));
    const updateOperationToast = (id, toast) => updateToast(id, capture.recordToast(toast));

    if (
      !canApplyDispatchSourceChanges
      || dispatchReloadingRef.current || sourceChangesMutationInFlightRef.current
      || !dispatchDate
      || !truckId
      || !changeRevision
      || !isSourceChangesActionPreviewFresh(sourceChangesState, { dispatchDate, dispatchPeriod })
      || normalizeInlineText(currentDetail?.changeRevision) !== changeRevision
    ) {
      showOperationToast({
        duration: 6500,
        message: "Les contraintes de ce camion ne correspondent plus à la prévisualisation. Rechargez Mise à jour.",
        title: "Rejet meunerie périmé",
        type: "warning",
      });
      capture.setStatus("refused");
      if (!sourceChangesMutationInFlightRef.current) capture.finish();
      return null;
    }


    if (!setSourceChangesMutationBusy(true)) {
      capture.setStatus("refused");
      capture.finish();
      return null;
    }
    publishSourceChangesStatusNotice({
      message: `Rejet des nouvelles contraintes de meunerie de ${currentDetail?.truckLabel || truckId}.`,
      status: "running",
      title: "Rejet meunerie",
    });
    try {
      loadingToastId = showOperationToast({
        message: `Conservation des contraintes actuelles de ${currentDetail?.truckLabel || truckId}.`,
        title: "Rejet meunerie",
        type: "loading",
      });
      const initialRequestBody = {
        dispatch_date: dispatchDate,
        dispatch_period: dispatchPeriod,
        preview_revision: sourceChangesState.previewRevision,
        truck_id: truckId,
        change_revision: changeRevision,
      };
      const postRequest = async (requestBody) => {
        const response = await fetchWithSupabaseAuth(
          "/api/planning/source-changes/loading-constraints/reject",
          {
            body: JSON.stringify(requestBody),
            headers: {
              "Content-Type": "application/json",
              "X-Planning-Diagnostics": "1",
              ...buildSourceChangesPerformanceHeaders(performanceContext),
            },
            method: "POST",
          },
        );
        return parseJsonResponse(
          response,
          "Impossible de rejeter les contraintes de meunerie.",
        );
      };
      const payload = await executeLoadingConstraintSourceChangeRequest({
        initialRequestBody,
        postRequest,
        refreshSelection: () => refreshLoadingConstraintMutationSelection({
          dispatchDate,
          dispatchPeriod,
          performanceContext,
          capture,
          truckId,
        }),
      });
      if (normalizeInlineText(payload?.outcome).toLowerCase() !== "rejected") {
        throw new Error("La réponse de rejet meunerie est incomplète.");
      }
      capture.markApplied();
      reportProgress("display");
      const successToast = await completeSourceChangeMutation({
        dispatchDate, dispatchPeriod, performanceContext, capture, payloads: [payload], periods: [dispatchPeriod],
        toast: {
          duration: 6000,
          message: "Le dispatch conserve ses contraintes de meunerie actuelles jusqu’à une nouvelle modification Data Manager.",
          title: "Mise à jour meunerie rejetée",
          type: "success",
        },
      });
      if (loadingToastId) {
        updateOperationToast(loadingToastId, successToast);
      } else {
        showOperationToast(successToast);
      }
      publishSourceChangesStatusNotice({
        message: successToast.message,
        status: capture.getStatus() === "partial" ? "partial" : "completed",
        title: successToast.title,
      });
      return payload;
    } catch (error) {
      capture.fail(error);
      if (!wasSourceChangesRefreshPerformed(error)) {
        try {
          reportProgress("refresh");
          await capture.observeRefresh(() => refreshSourceChangesForPeriod(dispatchPeriod, {
            background: true,
            forceFresh: true,
            ...performanceContext, dispatchDate,
            onDiagnostic: (details) => capture.recordStepDetails("refresh", details),
          }));
        } catch {
          // L’erreur du rejet reste prioritaire et l’action n’est jamais réémise.
        }
      }
      const errorToast = {
        duration: 7500,
        message: error instanceof Error && error.message
          ? error.message
          : "Impossible de rejeter les contraintes de meunerie.",
        title: "Rejet meunerie impossible",
        type: "error",
      };
      if (loadingToastId) {
        updateOperationToast(loadingToastId, errorToast);
      } else {
        showOperationToast(errorToast);
      }
      publishSourceChangesStatusNotice({
        message: errorToast.message,
        status: "failed",
        title: errorToast.title,
      });
      return null;
    } finally {
      capture.finish();
      setSourceChangesMutationBusy(false);
    }
  }, [completeSourceChangeMutation, canApplyDispatchSourceChanges, commitSourceChangesMutationResponse, getSourceChangesDispatchDate, getSourceChangesStateForPeriod, refreshLoadingConstraintMutationSelection, refreshSourceChangesForPeriod, setSourceChangesMutationBusy, showToast, updateToast]);

  const refreshSourceChangeTrucks = useCallback(async (
    selection,
    requestedPeriod = "",
    verifiedSourceChanges = null,
    providedPerformanceContext = null,
  ) => {
    const dispatchPeriod = normalizePortalPublicationDispatchPeriod(
      requestedPeriod || selection?.dispatchPeriod || activePortalScopeRef.current.dispatchPeriod,
    );
    const dispatchDate = normalizeInlineText(selection?.dispatchDate);
    const selectedTruckIds = Array.isArray(selection?.truckIds)
      ? [...new Set(selection.truckIds.map(normalizeInlineText).filter(Boolean))].sort((left, right) => (
          left.localeCompare(right, "fr", { sensitivity: "base" })
        ))
      : [];
    let selectedTruckChangeRevisions = selection?.truckChangeRevisions
      && typeof selection.truckChangeRevisions === "object"
      && !Array.isArray(selection.truckChangeRevisions)
      ? selection.truckChangeRevisions
      : null;
    const currentDispatchDate = getSourceChangesDispatchDate();
    const initialActivePeriod = activePortalScopeRef.current.dispatchPeriod;
    const currentSourceChanges = getSourceChangesStateForPeriod(dispatchPeriod);
    // Toast transmis par un changement du Gantt : terminé tel quel si l'actualisation ne démarre pas.
    const releaseHandedOffToast = () => {
      if (selection?.loadingToast?.id) updateToast(selection.loadingToast.id, { type: "success", message: selection.loadingToast.message, duration: 5000 });
    };
    if (
      !canApplyDispatchSourceChanges
      || dispatchReloadingRef.current || sourceChangesMutationInFlightRef.current
      || currentSourceChanges?.isMutating === true
      || !dispatchDate
      || dispatchDate !== currentDispatchDate
      || dispatchPeriod !== normalizePortalPublicationDispatchPeriod(selection?.dispatchPeriod)
      || selectedTruckIds.length === 0
      || !selectedTruckChangeRevisions
    ) {
      releaseHandedOffToast();
      return null;
    }

    const performanceContext = providedPerformanceContext || {
      action: "source_changes_refresh_trucks",
      cause: "source_changes_action",
      chainId: createPerformanceCorrelationId(),
      origin: "local",
    };
    let loadingToastId = null;
    const capture = createOperationFeedbackCapture({
      action: performanceContext.action, operationEventId: performanceContext.chainId,
      dispatchDate, dispatchPeriods: [dispatchPeriod], resourceKind: "truck",
      resourceIds: selectedTruckIds, resourceLabel: selection?.truckLabel || selectedTruckIds.join(", "),
      linkedCapture: selection?.loadingToast?.capture,
      progressSteps: [
        ...(selection?.verifyFirst ? [{ key: "verify", label: "Vérification préalable" }] : []),
        { key: "process", label: "Traitement du camion" },
        { key: "display", label: "Actualisation du Dispatch" },
        { key: "refresh", label: "Rafraîchissement de la liste" },
        { key: "reconciliation", label: "Vérification du Dispatch affiché" },
      ],
      onProgress: (nextProgress) => { if (loadingToastId) updateToast(loadingToastId, { progress: nextProgress }); },
    });
    const reportProgress = capture.reportProgress;
    const progress = capture.getSnapshot();
    const showOperationToast = (toast) => showToast(capture.recordToast(toast));
    const updateOperationToast = (id, toast) => updateToast(id, capture.recordToast(toast));

    let postAttempted = false;
    let reconciliationAttempted = false;
    if (!setSourceChangesMutationBusy(true)) {
      capture.setStatus("refused");
      capture.finish();
      releaseHandedOffToast();
      return null;
    }
    // This handler owns the update lock from the accepted click through readback.
    // Its preliminary GET and POST share the same clock and correlation ID.
    try {
      const loadingToast = {
        message: `${selection?.truckLabel || selectedTruckIds.join(", ")} — ${selection?.verifyFirst ? "Vérification préalable" : "Actualisation en cours"}.`,
        title: selection?.loadingToast?.title || "Actualisation camion", type: "loading", progress,
      };
      // Changement du Gantt : son toast (« Actualisation chauffeur », « … de la remorque ») continue ici.
      loadingToastId = selection?.loadingToast?.id || showOperationToast(loadingToast);
      if (selection?.loadingToast?.id) updateOperationToast(loadingToastId, loadingToast);
      if (selection?.verifyFirst) {
        const refreshedPayload = await refreshSourceChangesForPeriod(dispatchPeriod, {
          background: true, forceFresh: true, ...performanceContext, dispatchDate,
          onDiagnostic: (details) => capture.recordStepDetails("verify", details),
        });
        verifiedSourceChanges = readInitialSourceChanges({ source_changes: refreshedPayload }, { dispatchDate, dispatchPeriod });
        if (!verifiedSourceChanges || !isSourceChangeFinalPreview(refreshedPayload, { dispatchDate, dispatchPeriod })) {
          throw new Error("La vérification n’a pas pu être terminée. Aucune mise à jour n’a été appliquée.");
        }
        if (!sourceChangesMountedRef.current || dispatchDate !== getSourceChangesDispatchDate()
          || initialActivePeriod !== activePortalScopeRef.current.dispatchPeriod) {
          throw Object.assign(new Error("La navigation a changé pendant la vérification. Aucune actualisation camion appliquée."), { status: 409 });
        }
        // Normalization omits UI verification state; this fresh, complete read
        // has now passed the scope checks and belongs to the current operation.
        verifiedSourceChanges = { ...verifiedSourceChanges, hasVerified: true };
        reportProgress("verify", "completed");
        const detail = verifiedSourceChanges.truckChanges?.details?.find((item) => item.truckId === selectedTruckIds[0]);
        if (!detail || !canRefreshTruckSourceChange(detail) || detail.recovery?.status === "confirmation_required") {
          const confirmation = canRefreshTruckSourceChange(detail) && detail?.recovery?.status === "confirmation_required";
          capture.setStatus(!detail ? "success" : confirmation ? "awaiting_confirmation" : "refused");
          updateOperationToast(loadingToastId, {
            type: !detail ? "success" : "warning", duration: 7000, progress: null,
            title: !detail ? "Camion déjà à jour" : confirmation ? "Confirmation nécessaire" : "Camion encore bloqué",
            message: `${detail?.truckLabel || selection.truckLabel || selectedTruckIds[0]} : ${!detail
              ? "aucun changement camion à appliquer."
              : confirmation ? "Vérifiez la proposition de suspension avant de confirmer."
                : applicationFeedbackMessage(detail.applicationFeedback) || "Consultez le diagnostic dans Mise à jour."}`,
          });
          return confirmation ? { blocked_trucks: [{ truck_id: detail.truckId, change_revision: detail.changeRevision, recovery: detail.recovery }] } : null;
        }
        selectedTruckChangeRevisions = { [detail.truckId]: detail.changeRevision };
      }
      reportProgress("process");
      const sourceChanges = verifiedSourceChanges && typeof verifiedSourceChanges === "object"
        ? verifiedSourceChanges
        : currentSourceChanges;
      const truckChanges = sourceChanges?.truckChanges;
      const previewRevision = normalizeInlineText(sourceChanges?.previewRevision);
      const featureRevision = normalizeInlineText(truckChanges?.featureRevision);
      const currentTruckDetailsById = new Map(
        (Array.isArray(truckChanges?.details) ? truckChanges.details : []).map((detail) => (
          [normalizeInlineText(detail?.truckId), detail]
        )),
      );
      const hasExactSelection = selectedTruckIds.length > 0
        && selectedTruckChangeRevisions
        && Object.keys(selectedTruckChangeRevisions).length === selectedTruckIds.length
        && selectedTruckIds.every((truckId) => {
          const detail = currentTruckDetailsById.get(truckId);
          const canRefreshTruck = canRefreshTruckSourceChange(detail);
          return canRefreshTruck
            && normalizeInlineText(detail?.changeRevision)
              === normalizeInlineText(selectedTruckChangeRevisions[truckId]);
        })
        && Object.keys(selectedTruckChangeRevisions).every((truckId) => selectedTruckIds.includes(truckId));
      if (
        !canApplyDispatchSourceChanges
        || dispatchReloadingRef.current
        || !sourceChangesMountedRef.current
        || dispatchDate !== getSourceChangesDispatchDate()
        || initialActivePeriod !== activePortalScopeRef.current.dispatchPeriod
        || !dispatchDate
        || dispatchDate !== currentDispatchDate
        || dispatchDate !== normalizeInlineText(sourceChanges?.dispatchDate)
        || dispatchPeriod !== normalizePortalPublicationDispatchPeriod(sourceChanges?.dispatchPeriod)
        || dispatchPeriod !== normalizePortalPublicationDispatchPeriod(selection?.dispatchPeriod)
        || !isSourceChangesActionPreviewFresh(sourceChanges, { dispatchDate, dispatchPeriod })
        || !previewRevision
        || truckChanges?.state !== "ready"
        || truckChanges?.supported !== true
        || truckChanges?.enabled !== true
        || !featureRevision
        || !hasExactSelection
      ) {
        updateOperationToast(loadingToastId, {
          duration: 6500,
          message: "La sélection des camions n’est plus alignée avec la prévisualisation. Rouvrez Mise à jour avant de réessayer.",
          title: "Sélection camions périmée",
          type: "warning",
        });
        capture.setStatus("refused");
        return null;
      }

      const requestBody = {
        preview_revision: previewRevision,
        feature_revision: featureRevision,
        truck_ids: selectedTruckIds,
        truck_change_revisions: Object.fromEntries(selectedTruckIds.map((truckId) => (
          [truckId, normalizeInlineText(selectedTruckChangeRevisions[truckId])]
        ))),
        // Toute actualisation de Mise à jour : un dépassement d'heures passe en temps supplémentaire.
        accept_overtime_truck_ids: selectedTruckIds,
      };
      publishSourceChangesStatusNotice({
        message: `${selectedTruckIds.length} camion(s) sélectionné(s) sont actualisé(s) depuis Data Manager.`,
        status: "running",
        title: "Actualisation camions",
      });
      postAttempted = true;
      const response = await fetchWithSupabaseAuth(
        buildDispatchDateQueryUrl(
          "/api/planning/source-changes/trucks/refresh",
          dispatchDate,
          dispatchPeriod,
        ),
        {
          body: JSON.stringify(requestBody),
          headers: {
            "Content-Type": "application/json",
            "X-Planning-Diagnostics": "1",
            ...buildSourceChangesPerformanceHeaders(performanceContext),
          },
          method: "POST",
        },
      );
      const responsePayload = await parseJsonResponse(
        response,
        "Impossible d’actualiser les camions sélectionnés.",
      );
      const refreshContract = readSourceChangeTruckRefreshContract(responsePayload, selectedTruckIds);
      if (refreshContract.applied > 0) capture.markApplied();
      const payload = refreshContract.sourceChangesRefreshRequired
        ? responsePayload
        : attachBlockedTruckRecoveriesToPayload(
            responsePayload,
            refreshContract.blockedTrucks,
          );
      const planningDiagnostics = payload?._planning_diagnostics
        && typeof payload._planning_diagnostics === "object"
        && !Array.isArray(payload._planning_diagnostics)
        ? payload._planning_diagnostics
        : null;
      const hasAppliedTruckRefresh = refreshContract.applied > 0;
      reportProgress("process", refreshContract.outcome === "applied" ? "completed" : "warning");
      const isWarningOutcome = refreshContract.outcome !== "applied";
      const recoverableBlockedTruck = refreshContract.blockedTrucks.find((item) => (
        item?.recovery?.status === "confirmation_required"
      ));
      const allSelectedTrucksAreProtected = refreshContract.outcome === "blocked"
        && refreshContract.blockedTrucks.length === selectedTruckIds.length
        && refreshContract.blockedTrucks.every((item) => (
          item?.reasonCode === "protected_capacity_trim_unavailable"
          && item?.recovery?.status === "protected_only"
        ));
      const protectedTruckLabels = refreshContract.blockedTrucks.map((item) => (
        normalizeInlineText(currentTruckDetailsById.get(item.truckId)?.truckLabel) || item.truckId
      ));
      const blockedRefreshDetail = refreshContract.blockedTrucks.map((item) => {
        const reason = applicationFeedbackMessage(item.applicationFeedback);
        if (!reason) return "";
        const label = normalizeInlineText(currentTruckDetailsById.get(item.truckId)?.truckLabel) || item.truckId;
        return `${label} : ${reason}`;
      }).filter(Boolean).join(" ");
      // Section de Mise à jour où restent les cartes bloquées (Camions, Remorques ou Chauffeurs).
      const blockedMenuPath = formatTruckSourceChangesMenuPath(
        refreshContract.blockedTrucks.map((item) => currentTruckDetailsById.get(item.truckId)),
      );
      const completionMessage = allSelectedTrucksAreProtected
        ? protectedTruckLabels.length === 1
          ? `Le camion ${protectedTruckLabels[0]} n’a pas été actualisé : toutes les commandes de sa tournée sont protégées.${blockedRefreshDetail ? ` ${blockedRefreshDetail}` : ""} Libérez une ou plusieurs commandes, puis ouvrez ${blockedMenuPath} et cliquez de nouveau sur « Actualiser ».`
          : `Les camions ${protectedTruckLabels.join(", ")} n’ont pas été actualisés : toutes les commandes de leurs tournées sont protégées.${blockedRefreshDetail ? ` ${blockedRefreshDetail}` : ""} Libérez une ou plusieurs commandes, puis ouvrez ${blockedMenuPath} et cliquez de nouveau sur « Actualiser ».`
        : refreshContract.outcome === "applied"
          ? `${refreshContract.applied} camion(s) actualisé(s).`
          : refreshContract.outcome === "partial"
            ? `${refreshContract.applied} camion(s) actualisé(s); ${refreshContract.blocked} bloqué(s) restent à examiner dans ${blockedMenuPath}.${blockedRefreshDetail ? ` ${blockedRefreshDetail}` : ""}`
            : blockedRefreshDetail
              ? `Aucun camion actualisé. ${blockedRefreshDetail} Ajustez la tournée dans ${blockedMenuPath}, ou choisissez « Rejeter » pour conserver le profil actuel.`
              : `Aucun camion actualisé. Ouvrez ${blockedMenuPath} pour corriger ou réaffecter la tournée, ou choisissez « Rejeter » pour conserver le profil actuel.`;
      const completionTitle = allSelectedTrucksAreProtected
        ? protectedTruckLabels.length === 1
          ? "Actualisation camion bloquée"
          : "Actualisation des camions bloquée"
        : refreshContract.outcome === "blocked"
          ? "Actualisation camion bloquée"
          : isWarningOutcome
          ? "Actualisation camions partielle"
          : selection?.loadingToast?.title || "Camions actualisés";
      const completionToast = {
        duration: isWarningOutcome ? 7500 : 5000,
        message: completionMessage,
        title: completionTitle,
        type: isWarningOutcome ? "warning" : "success",
      };
      capture.setStatus(refreshContract.applied > 0
        ? refreshContract.outcome === "applied" ? "success" : "partial"
        : recoverableBlockedTruck ? "awaiting_confirmation" : "refused");
      if (isWarningOutcome) updateOperationToast(loadingToastId, {
        ...completionToast, duration: 0, operationPending: true, message: `${completionMessage} Liste en cours d’actualisation.`,
        progress: capture.getSnapshot(),
      });
      reconciliationAttempted = true;
      const finalCompletionToast = await completeSourceChangeMutation({
        dispatchDate, dispatchPeriod, performanceContext, capture, payloads: [payload], periods: [dispatchPeriod],
        toast: completionToast, applied: hasAppliedTruckRefresh, installSavedResult: true, recoverSavedResult: true,
      });
      if (loadingToastId) {
        updateOperationToast(loadingToastId, { ...finalCompletionToast, operationPending: false, progress: null });
      } else {
        showOperationToast(finalCompletionToast);
      }
      if (getSourceChangesDispatchDate() === dispatchDate) {
        if (!hasAppliedTruckRefresh && (recoverableBlockedTruck
          || (payload.blocked_trucks?.length > 0 && payload.blocked_trucks.every(isBusinessApplicationBlock)))) {
          clearAppStatusNoticeBySource("planning");
        } else publishSourceChangesStatusNotice({
          message: finalCompletionToast.message, planningDiagnostics,
          status: capture.getStatus() === "partial" ? "partial" : finalCompletionToast.type === "success" ? "completed" : "failed",
          title: finalCompletionToast.title,
        });
      }
      return payload;
    } catch (error) {
      capture.fail(error);
      let errorToast = {
        duration: 7500,
        message: error instanceof Error && error.message
          ? error.message
          : "Impossible d’actualiser les camions sélectionnés.",
        title: error?.status === 409 ? "Modification à vérifier à nouveau" : "Actualisation camions impossible",
        type: error?.status === 409 ? "warning" : "error", operationPending: false, progress: null,
      };
      if (postAttempted && !reconciliationAttempted) {
        reconciliationAttempted = true;
        errorToast = await completeSourceChangeMutation({
          dispatchDate, dispatchPeriod, performanceContext, capture, payloads: [], periods: [dispatchPeriod],
          toast: errorToast, applied: false, installSavedResult: false, recoverSavedResult: true,
        });
      }
      if (loadingToastId) {
        updateOperationToast(loadingToastId, errorToast);
      } else {
        showOperationToast(errorToast);
      }
      if (error?.status === 409) clearAppStatusNoticeBySource("planning");
      else publishSourceChangesStatusNotice({
        message: errorToast.message,
        planningDiagnostics: error?.payload?._planning_diagnostics || null,
        status: "failed",
        title: errorToast.title,
      });
      return null;
    } finally {
      capture.finish();
      if (loadingToastId && getSourceChangesDispatchDate() !== dispatchDate) dismissToast(loadingToastId);
      setSourceChangesMutationBusy(false);
    }
  }, [canApplyDispatchSourceChanges, commitSourceChangesMutationResponse, completeSourceChangeMutation, dismissToast, getSourceChangesDispatchDate, getSourceChangesStateForPeriod, refreshSourceChangesForPeriod, setSourceChangesMutationBusy, showToast, updateToast]);

  const rejectSourceChangeTruck = useCallback(async (selection, requestedPeriod = "") => {
    const dispatchPeriod = normalizePortalPublicationDispatchPeriod(
      requestedPeriod || selection?.dispatchPeriod || activePortalScopeRef.current.dispatchPeriod,
    );
    const dispatchDate = normalizeInlineText(selection?.dispatchDate);
    const truckId = normalizeInlineText(selection?.truckId);
    const selectedChangeRevision = normalizeInlineText(selection?.truckChangeRevision);
    const truckLabel = normalizeInlineText(selection?.truckLabel) || truckId;
    const currentDispatchDate = getSourceChangesDispatchDate();
    const currentSourceChanges = getSourceChangesStateForPeriod(dispatchPeriod);
    if (
      !canApplyDispatchSourceChanges
      || dispatchReloadingRef.current || sourceChangesMutationInFlightRef.current
      || currentSourceChanges?.isMutating === true
      || !dispatchDate
      || dispatchDate !== currentDispatchDate
      || dispatchPeriod !== normalizePortalPublicationDispatchPeriod(selection?.dispatchPeriod)
      || !truckId
      || !selectedChangeRevision
    ) {
      return null;
    }

    const performanceContext = {
      action: "source_changes_reject_truck",
      cause: "source_changes_action",
      chainId: createPerformanceCorrelationId(),
      origin: "local",
    };
    let loadingToastId = null;
    const capture = createOperationFeedbackCapture({
      action: performanceContext.action, operationEventId: performanceContext.chainId,
      dispatchDate, dispatchPeriods: [dispatchPeriod], resourceKind: "truck",
      resourceIds: [truckId], resourceLabel: selection?.truckLabel || truckId,
      onProgress: (nextProgress) => { if (loadingToastId) updateToast(loadingToastId, { progress: nextProgress }); },
    });
    const reportProgress = capture.reportProgress;
    const showOperationToast = (toast) => showToast(capture.recordToast(toast));
    const updateOperationToast = (id, toast) => updateToast(id, capture.recordToast(toast));

    let postAttempted = false;
    let reconciliationAttempted = false;
    if (!setSourceChangesMutationBusy(true)) {
      capture.setStatus("refused");
      capture.finish();
      return null;
    }
    try {
      const refreshedPayload = await refreshSourceChangesForPeriod(dispatchPeriod, {
        background: true,
        forceFresh: true,
        ...performanceContext, dispatchDate,
        onDiagnostic: (details) => capture.recordStepDetails("refresh", details),
      });
      if (!sourceChangesMountedRef.current || getSourceChangesDispatchDate() !== dispatchDate) return null;
      const refreshedSourceChanges = refreshedPayload
        ? readInitialSourceChanges(
            { source_changes: refreshedPayload },
            { dispatchDate, dispatchPeriod },
          )
        : null;
      const sourceChanges = refreshedSourceChanges
        ? {
            ...refreshedSourceChanges,
            errorMessage: "",
            hasVerified: true,
            isLoading: false,
            isMutating: false,
            isVerifying: false,
            needsPlanningContextVerification: false,
            needsVerification: false,
          }
        : null;
      const truckChanges = sourceChanges?.truckChanges;
      const previewRevision = normalizeInlineText(sourceChanges?.previewRevision);
      const featureRevision = normalizeInlineText(truckChanges?.featureRevision);
      const currentDetail = (Array.isArray(truckChanges?.details) ? truckChanges.details : [])
        .find((detail) => normalizeInlineText(detail?.truckId) === truckId);
      if (
        !isSourceChangesActionPreviewFresh(sourceChanges, { dispatchDate, dispatchPeriod })
        || !previewRevision
        || truckChanges?.state !== "ready"
        || truckChanges?.supported !== true
        || truckChanges?.enabled !== true
        || !featureRevision
        || !["actionable", "blocked", "unsupported"].includes(currentDetail?.status)
        || normalizeInlineText(currentDetail?.changeRevision) !== selectedChangeRevision
      ) {
        showOperationToast({
          duration: 6500,
          message: "Ce changement camion n’est plus aligné avec la prévisualisation. Rouvrez Mise à jour avant de réessayer.",
          title: "Rejet camion périmé",
          type: "warning",
        });
        capture.setStatus("refused");
        capture.finish();
        return null;
      }

      publishSourceChangesStatusNotice({
        message: `La mise à jour Data Manager de ${truckLabel} est rejetée pour ce dispatch.`,
        status: "running",
        title: "Rejet camion",
      });
      loadingToastId = showOperationToast({
        message: `Conservation du profil actuel de ${truckLabel}.`,
        title: "Rejet camion",
        type: "loading",
      });
      postAttempted = true;
      const response = await fetchWithSupabaseAuth(
        buildDispatchDateQueryUrl(
          "/api/planning/source-changes/trucks/reject",
          dispatchDate,
          dispatchPeriod,
        ),
        {
          body: JSON.stringify({
            preview_revision: previewRevision,
            feature_revision: featureRevision,
            truck_id: truckId,
            truck_change_revision: selectedChangeRevision,
          }),
          headers: {
            "Content-Type": "application/json",
            "X-Planning-Diagnostics": "1",
            ...buildSourceChangesPerformanceHeaders(performanceContext),
          },
          method: "POST",
        },
      );
      const payload = await parseJsonResponse(
        response,
        "Impossible de rejeter la mise à jour de ce camion.",
      );
      readSourceChangeTruckRejectionContract(payload, truckId);
      capture.markApplied();
      reportProgress("display");
      reconciliationAttempted = true;
      const completionToast = await completeSourceChangeMutation({
        dispatchDate, dispatchPeriod, performanceContext, capture, payloads: [payload], periods: [dispatchPeriod],
        toast: {
          duration: 6000,
          message: `${truckLabel} conserve son profil actuel dans ce dispatch jusqu’à la prochaine optimisation.`,
          title: "Mise à jour camion rejetée",
          type: "success",
        },
      });
      if (loadingToastId) {
        updateOperationToast(loadingToastId, completionToast);
      } else {
        showOperationToast(completionToast);
      }
      publishSourceChangesStatusNotice({
        message: completionToast.message,
        status: capture.getStatus() === "partial" ? "partial" : "completed",
        title: completionToast.title,
      });
      return payload;
    } catch (error) {
      capture.fail(error);
      if (
        postAttempted
        && !reconciliationAttempted
        && getSourceChangesDispatchDate() === dispatchDate
      ) {
        reconciliationAttempted = true;
        try {
          reportProgress("refresh");
          await refreshSourceChangesForPeriod(dispatchPeriod, {
            background: true,
            forceFresh: true,
            ...performanceContext, dispatchDate,
            onDiagnostic: (details) => capture.recordStepDetails("refresh", details),
          });
        } catch {
          // L'erreur du POST reste prioritaire et le rejet n'est jamais réémis.
        }
      }
      const errorToast = {
        duration: 7500,
        message: error instanceof Error && error.message
          ? error.message
          : "Impossible de rejeter la mise à jour de ce camion.",
        title: "Rejet camion impossible",
        type: "error",
      };
      if (loadingToastId) {
        updateOperationToast(loadingToastId, errorToast);
      } else {
        showOperationToast(errorToast);
      }
      publishSourceChangesStatusNotice({
        message: errorToast.message,
        status: "failed",
        title: errorToast.title,
      });
      return null;
    } finally {
      capture.finish();
      setSourceChangesMutationBusy(false);
    }
  }, [completeSourceChangeMutation, canApplyDispatchSourceChanges, commitSourceChangesMutationResponse, getSourceChangesDispatchDate, getSourceChangesStateForPeriod, refreshSourceChangesForPeriod, setSourceChangesMutationBusy, showToast, updateToast]);

  const recoverSourceChangeTruck = useCallback(async (selection, requestedPeriod = "") => {
    const dispatchPeriod = normalizePortalPublicationDispatchPeriod(
      requestedPeriod || selection?.dispatchPeriod || activePortalScopeRef.current.dispatchPeriod,
    );
    const dispatchDate = normalizeInlineText(selection?.dispatchDate);
    const truckId = normalizeInlineText(selection?.truckId);
    const truckChangeRevision = normalizeInlineText(selection?.truckChangeRevision);
    const recoveryRevision = normalizeInlineText(selection?.recoveryRevision);
	const selectedGroupKeys = Array.isArray(selection?.selectedGroupKeys)
	  ? selection.selectedGroupKeys.map(normalizeInlineText).filter(Boolean)
	  : [];
    const currentDispatchDate = getSourceChangesDispatchDate();
    const currentSourceChanges = getSourceChangesStateForPeriod(dispatchPeriod);
    if (
      !canApplyDispatchSourceChanges
      || !canMutatePlanning
      || dispatchReloadingRef.current || sourceChangesMutationInFlightRef.current
      || currentSourceChanges?.isMutating === true
      || !dispatchDate
      || dispatchDate !== currentDispatchDate
      || dispatchPeriod !== normalizePortalPublicationDispatchPeriod(selection?.dispatchPeriod)
      || !truckId
      || !truckChangeRevision
      || !recoveryRevision
    ) {
      return null;
    }

    const performanceContext = {
      action: "source_changes_recover_truck",
      cause: "source_changes_action",
      chainId: createPerformanceCorrelationId(),
      origin: "local",
    };
    let loadingToastId = null;
    const capture = createOperationFeedbackCapture({
      action: performanceContext.action, operationEventId: performanceContext.chainId,
      dispatchDate, dispatchPeriods: [dispatchPeriod], resourceKind: "truck",
      resourceIds: [truckId], resourceLabel: selection?.truckLabel || truckId,
      onProgress: (nextProgress) => { if (loadingToastId) updateToast(loadingToastId, { progress: nextProgress }); },
    });
    const reportProgress = capture.reportProgress;
    const showOperationToast = (toast) => showToast(capture.recordToast(toast));
    const updateOperationToast = (id, toast) => updateToast(id, capture.recordToast(toast));

    const sourceChanges = currentSourceChanges;
    const truckChanges = sourceChanges?.truckChanges;
    const previewRevision = normalizeInlineText(sourceChanges?.previewRevision);
    const featureRevision = normalizeInlineText(truckChanges?.featureRevision);
    const currentDetail = (Array.isArray(truckChanges?.details) ? truckChanges.details : [])
      .find((detail) => normalizeInlineText(detail?.truckId) === truckId);
    const truckLabel = normalizeInlineText(selection?.truckLabel || currentDetail?.truckLabel) || truckId;
    const currentRecovery = currentDetail?.recovery;
	const isLoadCapacityRecovery = currentRecovery?.constraintKind === "load_capacity";
	const candidateGroupsByKey = new Map(
	  (Array.isArray(currentRecovery?.candidateGroups) ? currentRecovery.candidateGroups : [])
		.map((group) => [normalizeInlineText(group?.sourceGroupKey), group]),
	);
	const projectedPeakTons = (Array.isArray(currentRecovery?.capacityCheckpoints)
	  ? currentRecovery.capacityCheckpoints
	  : []).reduce((peak, checkpoint) => {
		const removedTons = (Array.isArray(checkpoint?.contributions) ? checkpoint.contributions : [])
		  .reduce((total, contribution) => (
			selectedGroupKeys.includes(normalizeInlineText(contribution?.sourceGroupKey))
			  ? total + Math.max(0, Number(contribution?.quantityTons) || 0)
			  : total
		  ), 0);
		return Math.max(peak, Math.max(0, (Number(checkpoint?.loadTons) || 0) - removedTons));
	  }, 0);
	const hasValidSelectedGroups = !isLoadCapacityRecovery || (
	  selectedGroupKeys.length > 0
	  && new Set(selectedGroupKeys).size === selectedGroupKeys.length
	  && selectedGroupKeys.every((groupKey) => candidateGroupsByKey.get(groupKey)?.selectable === true)
	  && projectedPeakTons <= Number(currentRecovery?.availableTons) + 1e-9
	);
    const hasExactRecovery = currentDetail?.status === "blocked"
      && currentRecovery?.status === "confirmation_required"
      && normalizeInlineText(currentDetail?.changeRevision) === truckChangeRevision
	  && normalizeInlineText(currentRecovery?.recoveryRevision) === recoveryRevision
	  && hasValidSelectedGroups;
    if (
      !canApplyDispatchSourceChanges
      || !canMutatePlanning
      || dispatchReloadingRef.current || sourceChangesMutationInFlightRef.current
      || sourceChanges?.isMutating === true
      || !dispatchDate
      || dispatchDate !== currentDispatchDate
      || dispatchDate !== normalizeInlineText(sourceChanges?.dispatchDate)
      || dispatchPeriod !== normalizePortalPublicationDispatchPeriod(sourceChanges?.dispatchPeriod)
      || dispatchPeriod !== normalizePortalPublicationDispatchPeriod(selection?.dispatchPeriod)
      || !isSourceChangesActionPreviewFresh(sourceChanges, { dispatchDate, dispatchPeriod })
      || !previewRevision
      || previewRevision !== normalizeInlineText(sourceChanges?.previewRevision)
      || truckChanges?.state !== "ready"
      || truckChanges?.supported !== true
      || truckChanges?.enabled !== true
      || !featureRevision
      || featureRevision !== normalizeInlineText(truckChanges?.featureRevision)
      || !truckId
      || !truckChangeRevision
      || !recoveryRevision
      || !hasExactRecovery
    ) {
      showOperationToast({
        duration: 7000,
        message: "Le plan de récupération de ce camion n’est plus aligné avec la prévisualisation. Rouvrez Mise à jour avant de réessayer.",
        title: "Plan camion périmé",
        type: "warning",
      });
      capture.setStatus("refused");
      capture.finish();
      return null;
    }

    let postAttempted = false;
    let reconciliationAttempted = false;
    if (!setSourceChangesMutationBusy(true)) {
      capture.setStatus("refused");
      capture.finish();
      return null;
    }
    publishSourceChangesStatusNotice({
      message: `Les commandes prévues sont transférées vers Suspendues avant d’actualiser ${truckLabel}.`,
      status: "running",
      title: "Récupération camion",
    });
    try {
      loadingToastId = showOperationToast({
        message: `Suspension des commandes prévues et actualisation de ${truckLabel}.`,
        title: "Récupération camion",
        type: "loading",
      });
      postAttempted = true;
      const response = await fetchWithSupabaseAuth(
        buildDispatchDateQueryUrl(
          "/api/planning/source-changes/trucks/refresh-with-suspension",
          dispatchDate,
          dispatchPeriod,
        ),
        {
          body: JSON.stringify({
            preview_revision: previewRevision,
            feature_revision: featureRevision,
            truck_id: truckId,
            truck_change_revision: truckChangeRevision,
            recovery_revision: recoveryRevision,
			...(isLoadCapacityRecovery ? { selected_group_keys: [...selectedGroupKeys].sort() } : {}),
          }),
          headers: {
            "Content-Type": "application/json",
            "X-Planning-Diagnostics": "1",
            ...buildSourceChangesPerformanceHeaders(performanceContext),
          },
          method: "POST",
        },
      );
      const responsePayload = await parseJsonResponse(
        response,
        "Impossible de récupérer ce camion. Rien n’a été modifié.",
      );
      const recoveryContract = readSourceChangeTruckRecoveryContract(responsePayload, truckId);
      if (recoveryContract.outcome === "applied" || recoveryContract.suspendedOrderIds.length > 0) capture.markApplied();
      reportProgress("process", recoveryContract.outcome === "applied" ? "completed" : "warning");
      let payload = attachBlockedTruckRecoveriesToPayload(
        responsePayload,
        recoveryContract.blockedTrucks,
      );
      const planningDiagnostics = payload?._planning_diagnostics
        && typeof payload._planning_diagnostics === "object"
        && !Array.isArray(payload._planning_diagnostics)
        ? payload._planning_diagnostics
        : null;
      const wasApplied = recoveryContract.outcome === "applied";
      const suspendedCount = recoveryContract.suspendedOrderIds.length;
      const completionMessage = wasApplied
        ? `${truckLabel} actualisé; ${suspendedCount} commande(s) placée(s) dans Suspendues.`
        : `${truckLabel} reste bloqué. Le nouveau plan proposé est affiché dans Mise à jour.`;
      capture.setStatus(wasApplied ? "success" : suspendedCount > 0 ? "partial"
        : recoveryContract.blockedTrucks.some((item) => item?.recovery?.status === "confirmation_required") ? "awaiting_confirmation" : "refused");
      reconciliationAttempted = true;
      const completionToast = await completeSourceChangeMutation({
        dispatchDate, dispatchPeriod, performanceContext, capture, payloads: [payload], periods: [dispatchPeriod],
        applied: wasApplied || suspendedCount > 0, toast: {
          duration: wasApplied ? 6500 : 8000,
          message: completionMessage,
          title: wasApplied ? "Camion actualisé" : "Récupération camion incomplète",
          type: wasApplied ? "success" : "warning",
          ...(wasApplied ? {
            actionLabel: "Ouvrir Suspendues",
            onAction: () => {
              detailViewState?.activateView?.("suspended", { historyMode: "push" });
            },
          } : {}),
        },
      });
      if (loadingToastId) {
        updateOperationToast(loadingToastId, completionToast);
      } else {
        showOperationToast(completionToast);
      }
      publishSourceChangesStatusNotice({
        message: completionToast.message,
        planningDiagnostics,
        status: capture.getStatus() === "partial" ? "partial" : wasApplied ? "completed" : "failed",
        title: completionToast.title,
      });
      return payload;
    } catch (error) {
      capture.fail(error);
      if (
        postAttempted
        && !reconciliationAttempted
        && getSourceChangesDispatchDate() === dispatchDate
      ) {
        reconciliationAttempted = true;
        try {
          reportProgress("refresh");
          await refreshSourceChangesForPeriod(dispatchPeriod, {
            background: true,
            forceFresh: true,
            ...performanceContext, dispatchDate,
            onDiagnostic: (details) => capture.recordStepDetails("refresh", details),
          });
        } catch {
          // L'erreur du POST reste la plus utile; aucune seconde mutation n'est tentée.
        }
      }
      const errorToast = {
        duration: 8000,
        message: error instanceof Error && error.message
          ? error.message
          : "Impossible de récupérer ce camion. Rien n’a été modifié.",
        title: "Récupération camion impossible",
        type: "error",
      };
      if (loadingToastId) {
        updateOperationToast(loadingToastId, errorToast);
      } else {
        showOperationToast(errorToast);
      }
      publishSourceChangesStatusNotice({
        message: errorToast.message,
        planningDiagnostics: error?.payload?._planning_diagnostics || null,
        status: "failed",
        title: errorToast.title,
      });
      return null;
    } finally {
      capture.finish();
      setSourceChangesMutationBusy(false);
    }
  }, [
    canApplyDispatchSourceChanges,
    canMutatePlanning,
    commitSourceChangesMutationResponse,
    completeSourceChangeMutation,
    detailViewState,
    getSourceChangesDispatchDate,
    getSourceChangesStateForPeriod,
    refreshSourceChangesForPeriod,
    setSourceChangesMutationBusy,
    showToast,
    updateToast,
  ]);

  const retrySourceChangeTruck = useCallback(async (detail, requestedPeriod = "") => {
    const dispatchPeriod = normalizePortalPublicationDispatchPeriod(
      requestedPeriod || activePortalScopeRef.current.dispatchPeriod,
    );
    const activeScope = { ...activePortalScopeRef.current };
    const currentSourceChanges = getSourceChangesStateForPeriod(dispatchPeriod);
    const dispatchDate = getSourceChangesDispatchDate();
    const truckId = normalizeInlineText(detail?.truckId || detail?.truck_id);
    if (
      !canApplyDispatchSourceChanges
      || dispatchReloadingRef.current || sourceChangesMutationInFlightRef.current
      || currentSourceChanges?.isMutating === true
      || !dispatchDate
      || !truckId
    ) {
      return null;
    }

    const performanceContext = {
      action: "source_changes_read",
      cause: "source_changes_action",
      chainId: createPerformanceCorrelationId(),
      origin: "local",
    };
    const capture = createOperationFeedbackCapture({
      action: performanceContext.action, operationEventId: performanceContext.chainId,
      dispatchDate, dispatchPeriods: [dispatchPeriod], resourceKind: "truck",
      resourceIds: [truckId], resourceLabel: detail?.truckLabel || truckId,
      progressSteps: [{ key: "refresh", label: "Lecture des mises à jour" }],
    });
    const reportProgress = capture.reportProgress;
    const showOperationToast = (toast) => showToast(capture.recordToast(toast));

    if (!setSourceChangesMutationBusy(true)) {
      capture.setStatus("refused");
      capture.finish();
      return null;
    }
    try {
      const refreshedPayload = await refreshSourceChangesForPeriod(dispatchPeriod, {
        background: true,
        forceFresh: true,
        ...performanceContext, dispatchDate,
        onDiagnostic: (details) => capture.recordStepDetails("refresh", details),
      });
      if (activeScope.dispatchDate !== activePortalScopeRef.current.dispatchDate
        || activeScope.dispatchPeriod !== activePortalScopeRef.current.dispatchPeriod) return null;
      const refreshedSourceChanges = readInitialSourceChanges(
        { source_changes: refreshedPayload },
        { dispatchDate, dispatchPeriod },
      );
      if (!refreshedSourceChanges) {
        showOperationToast({
          duration: 7000,
          message: "La vérification n’a pas pu être terminée. Aucune mise à jour n’a été appliquée.",
          title: "Actualisation impossible",
          type: "error",
        });
        capture.setStatus("failed");
        capture.finish();
        return null;
      }
      const refreshedDetail = (Array.isArray(refreshedSourceChanges.truckChanges?.details)
        ? refreshedSourceChanges.truckChanges.details
        : []).find((item) => normalizeInlineText(item?.truckId) === truckId);
      if (!refreshedDetail) {
        showOperationToast({
          duration: 5000,
          message: `${truckId} n’a plus de changement camion à appliquer.`,
          title: "Camion déjà à jour",
          type: "success",
        });
        capture.setStatus("success");
        capture.finish();
        return null;
      }
      if (!canRefreshTruckSourceChange(refreshedDetail)) {
        showOperationToast({
          duration: 8000,
          message: `${refreshedDetail.truckLabel || truckId} ne peut pas être actualisé. Consultez le diagnostic dans Mise à jour.`,
          title: "Camion encore bloqué",
          type: "warning",
        });
        capture.setStatus("refused");
        capture.finish();
        return null;
      }

      showOperationToast({ type: "success", title: "Lecture des mises à jour terminée",
        message: `${refreshedDetail.truckLabel || truckId} — vérification terminée.`, duration: 5000 });
      capture.setStatus("success");
      return {
        dispatchDate,
        dispatchPeriod,
        featureRevision: normalizeInlineText(refreshedSourceChanges.truckChanges?.featureRevision),
        previewRevision: normalizeInlineText(refreshedSourceChanges.previewRevision),
        truckChangeRevisions: { [truckId]: normalizeInlineText(refreshedDetail.changeRevision) },
        truckIds: [truckId],
      };
    } catch (error) {
      capture.fail(error);
      showOperationToast({ type: "error", title: "Lecture des mises à jour impossible",
        message: error?.message || "La liste n’a pas pu être chargée.", duration: 7000 });
      return null;
    } finally {
      capture.finish();
      setSourceChangesMutationBusy(false);
    }
  }, [
    canApplyDispatchSourceChanges,
    getSourceChangesDispatchDate,
    getSourceChangesStateForPeriod,
    refreshSourceChangesForPeriod,
    showToast,
    setSourceChangesMutationBusy,
  ]);

  const openSourceChangeTruck = useCallback((detail) => {
    const truckId = normalizeInlineText(detail?.truckId || detail?.truck_id);
    if (!truckId) {
      return;
    }
    detailViewState?.activateView?.("fleet", {
      historyMode: "push",
      scroll: true,
    });
    tripSelection.selectAssignment(truckId);
  }, [detailViewState, tripSelection]);

  // Cartes d'un camion vide, ou cartes Chauffeurs d'un camion sans chauffeur, selon le Gantt affiché.
  // Appliquées seulement après un changement fait dans ce dispatch, jamais à sa simple ouverture :
  // un dispatch reconsulté garde les valeurs de son moment.
  const readAutomaticTruckChanges = useCallback((details, dispatchDate, dispatchPeriod) => {
    const state = workspaceStore.getState();
    const resultsData = state?.resultsData || initialResultsData;
    if (dispatchPeriod !== normalizePortalPublicationDispatchPeriod(activePortalScopeRef.current.dispatchPeriod)
      || normalizeInlineText(resultsData?.dispatch_date) !== dispatchDate
      || !isProtectedOrderAutoRefreshDispatchOpen(resultsData, state, dispatchDate)) return [];
    return selectAutomaticTruckSourceChanges(details, resultsData?.assignments)
      .filter((item) => !automaticTruckChangeKeysRef.current.has(automaticTruckChangeKey(dispatchDate, dispatchPeriod, item)));
  }, [initialResultsData, workspaceStore]);
  const refreshTruckChangesAutomatically = useCallback((items, dispatchDate, dispatchPeriod, verified = null, loadingToast = null) => {
    items.forEach((item) => automaticTruckChangeKeysRef.current.add(automaticTruckChangeKey(dispatchDate, dispatchPeriod, item)));
    return refreshSourceChangeTrucks({
      dispatchDate, dispatchPeriod, loadingToast,
      truckIds: items.map((item) => item.truckId),
      truckChangeRevisions: Object.fromEntries(items.map((item) => [item.truckId, item.changeRevision])),
      truckLabel: items.map((item) => item.truckLabel || item.truckId).join(", "),
    }, dispatchPeriod, verified);
  }, [refreshSourceChangeTrucks]);

  // Les cartes camion qu'un changement du Gantt vient de créer ou de modifier sont actualisées tout de
  // suite, comme une sauvegarde du Data Manager, si tous leurs champs relèvent de ce changement.
  // Les autres cartes en attente et les refus restent dans Mise à jour.
  // Rend false (sans rien lancer) si la liste de départ n'est pas connue.
  const refreshTruckChangesCreatedByEdit = useCallback((detail) => {
    const dispatchDate = normalizeInlineText(detail?.dispatchDate || detail?.dispatch_date);
    const dispatchPeriod = normalizePortalPublicationDispatchPeriod(detail?.dispatchPeriod || detail?.dispatch_period);
    const sections = GANTT_FLEET_EDIT_SECTIONS.get(normalizeInlineText(detail?.actionName));
    const before = getSourceChangesStateForPeriod(dispatchPeriod);
    if (!sections || !dispatchDate || normalizeInlineText(before?.dispatchDate) !== dispatchDate
      || before?.truckChanges?.state !== "ready") {
      return false;
    }
    const revisionsBefore = new Map((before.truckChanges.details || []).map((item) => [item?.truckId, item?.changeRevision]));
    void (async () => {
      const payload = await refreshSourceChangesForPeriod(dispatchPeriod, {
        background: true, forceFresh: true, dispatchDate, cause: "planning_event_fleet", origin: "local",
        chainId: normalizeInlineText(detail?.performanceChainId),
      }).catch(() => null);
      const verified = payload ? readInitialSourceChanges({ source_changes: payload }, { dispatchDate, dispatchPeriod }) : null;
      if (!verified || !isSourceChangeFinalPreview(payload, { dispatchDate, dispatchPeriod })) return finishHandedOffToast(updateToast, detail?.loadingToast);
      const created = (verified.truckChanges?.details || []).filter((item) => revisionsBefore.get(item?.truckId) !== item?.changeRevision
        && (item.changedFields || []).every((field) => sections.has(truckSourceChangeFieldSection(field?.field)))
        && canRefreshTruckSourceChange(item) && item.recovery?.status !== "confirmation_required");
      // Une seule actualisation, avec aussi les cartes des camions vides ou sans chauffeur.
      const items = [...new Map([...created, ...readAutomaticTruckChanges(verified.truckChanges?.details, dispatchDate, dispatchPeriod)]
        .map((item) => [item.truckId, item])).values()];
      if (items.length === 0) return finishHandedOffToast(updateToast, detail?.loadingToast);
      await refreshTruckChangesAutomatically(items, dispatchDate, dispatchPeriod, { ...verified, hasVerified: true }, detail?.loadingToast);
      // L'actualisation change la tournée, donc son empreinte : « Publier » doit partir de la nouvelle.
      void refreshPortalPublications({ background: true });
    })();
    return true;
  }, [getSourceChangesStateForPeriod, readAutomaticTruckChanges, refreshPortalPublications, refreshSourceChangesForPeriod, refreshTruckChangesAutomatically, updateToast]);
  refreshTruckChangesCreatedByEditRef.current = refreshTruckChangesCreatedByEdit;

  const acknowledgeProtectedSourceChange = useCallback(async (detail, requestedPeriod = "") => {
    const dispatchPeriod = normalizePortalPublicationDispatchPeriod(
      requestedPeriod || activePortalScopeRef.current.dispatchPeriod,
    );
    const sourceChanges = getSourceChangesStateForPeriod(dispatchPeriod);
    const dispatchDate = getSourceChangesDispatchDate();
    const orderId = normalizeInlineText(detail?.order_id);
    const changeRevision = normalizeInlineText(detail?.change_revision);
    const previewRevision = normalizeInlineText(sourceChanges.mutationPreviewRevision);
    if (
      !dispatchDate
      || dispatchReloadingRef.current || sourceChangesMutationInFlightRef.current
      || sourceChanges.isMutating
      || sourceChangeAcknowledgementInFlightRef.current
    ) {
      return null;
    }
    const performanceContext = {
      action: "source_changes_acknowledge_protected",
      cause: "source_changes_action",
      chainId: createPerformanceCorrelationId(),
      origin: "local",
    };
    const capture = createOperationFeedbackCapture({
      action: performanceContext.action, operationEventId: performanceContext.chainId,
      dispatchDate, dispatchPeriods: [dispatchPeriod], resourceKind: "order",
      resourceIds: [orderId], resourceLabel: orderId,
    });
    const reportProgress = capture.reportProgress;
    const showOperationToast = (toast) => showToast(capture.recordToast(toast));

    if (!isSourceChangesActionPreviewFresh(sourceChanges, { dispatchDate, dispatchPeriod })) {
      showOperationToast({
        duration: 6000,
        message: "Ouvrez M-à-J commandes et attendez la fin de la vérification avant de conserver ce dispatch.",
        title: "Vérification M-à-J requise",
        type: "warning",
      });
      capture.setStatus("refused");
      if (!sourceChangesMutationInFlightRef.current) capture.finish();
      return null;
    }
    if (!orderId || !changeRevision || !previewRevision) {
      showOperationToast({
        duration: 6000,
        message: "Rafraîchissez M-à-J commandes avant de conserver ce dispatch.",
        title: "Révision incomplète",
        type: "error",
      });
      capture.setStatus("refused");
      if (!sourceChangesMutationInFlightRef.current) capture.finish();
      return null;
    }

    const performanceHeaders = buildSourceChangesPerformanceHeaders(performanceContext);

    let acknowledgementRequestAttempted = false;
    if (!setSourceChangesMutationBusy(true)) {
      capture.setStatus("refused");
      capture.finish();
      return null;
    }
    sourceChangeAcknowledgementInFlightRef.current = true;
    publishSourceChangesStatusNotice({
      message: `Nous enregistrons la décision pour ${orderId}.`,
      status: "running",
      title: "Conservation du dispatch",
    });
    try {
      acknowledgementRequestAttempted = true;
      const response = await fetchWithSupabaseAuth(
        buildDispatchDateQueryUrl(
          "/api/planning/source-changes/acknowledge-protected",
          dispatchDate,
          dispatchPeriod,
        ),
        {
          body: JSON.stringify({
            order_id: orderId,
            preview_revision: previewRevision,
            change_revision: changeRevision,
          }),
          headers: {
            "Content-Type": "application/json",
            "X-Planning-Diagnostics": "1",
            ...DEFERRED_SOURCE_CHANGES_HEADERS,
            ...performanceHeaders,
          },
          method: "POST",
        },
      );
      const payload = await parseJsonResponse(
        response,
        "Impossible de conserver le dispatch actuel pour cette commande.",
      );
      capture.markApplied();
      reportProgress("process", "completed");
      const acknowledgementToast = await completeSourceChangeMutation({
        dispatchDate, dispatchPeriod, performanceContext, capture, payloads: [payload],
        toast: { duration: 5000, message: `${orderId} reste dans son camion.`, title: "Décision enregistrée", type: "success" },
      });
      showOperationToast(acknowledgementToast);
      publishSourceChangesStatusNotice({
        message: acknowledgementToast.message,
        planningDiagnostics: payload?._planning_diagnostics || null,
        status: capture.getStatus() === "partial" ? "partial" : "completed",
        title: acknowledgementToast.title,
      });
      return payload;
    } catch (error) {
      capture.fail(error);
      const errorMessage = error instanceof Error && error.message
        ? error.message
        : "Impossible de conserver le dispatch actuel pour cette commande.";
      showOperationToast({
        duration: 7000,
        message: errorMessage,
        title: "Conservation impossible",
        type: "error",
      });
      publishSourceChangesStatusNotice({
        message: errorMessage,
        planningDiagnostics: error?.payload?._planning_diagnostics || null,
        status: capture.getStatus() === "partial" ? "partial" : "failed",
        title: "Conservation impossible",
      });
      const errorStatus = Number(error?.status) || 0;
      const shouldRefreshAfterError = [
        "source_change_revision_stale",
        "source_changes_preview_stale",
      ].includes(normalizeInlineText(error?.code)) || (
        acknowledgementRequestAttempted
        && (errorStatus === 0 || errorStatus >= 500)
      );
      if (shouldRefreshAfterError) {
        reportProgress("refresh");
        await refreshSourceChangesForPeriod(dispatchPeriod, { background: true, ...performanceContext, dispatchDate, onDiagnostic: (details) => capture.recordStepDetails("refresh", details) });
      }
      return null;
    } finally {
      capture.finish();
      sourceChangeAcknowledgementInFlightRef.current = false;
      setSourceChangesMutationBusy(false);
    }
  }, [completeSourceChangeMutation,
    commitSourceChangesMutationResponse,
    getSourceChangesDispatchDate,
    getSourceChangesStateForPeriod,
    refreshSourceChangesForPeriod,
    refreshVisibleSourceChangePeriods,
    setSourceChangesMutationBusy,
    showToast,
  ]);

  const resolveEndOfDayLoadIntegrity = useCallback(async (issue, requestedAction, requestedPeriod = "") => {
    const action = normalizeInlineText(requestedAction).toLowerCase();
    const requestedDispatchPeriod = normalizePortalPublicationDispatchPeriod(
      requestedPeriod || activePortalScopeRef.current.dispatchPeriod,
    );
    const target = issue?.target && typeof issue.target === "object" ? issue.target : {};
    if (action === "optimize_target" || action === "optimize_target_dispatch") {
      const navigationCapture = createOperationFeedbackCapture({
        action: "source_changes_resolve_end_of_day_load_integrity",
        dispatchDate: getSourceChangesDispatchDate(), dispatchPeriods: [requestedDispatchPeriod],
        resourceKind: "order", resourceIds: [issue?.order_id || issue?.orderId].filter(Boolean),
        resourceLabel: issue?.marker_key || issue?.markerKey || "Chargé PM",
      });
      const showTargetToast = (toast, status) => {
        navigationCapture.setStatus(status);
        const result = showToast(navigationCapture.recordToast(toast));
        navigationCapture.finish();
        return result;
      };
      if (
        requestedPeriod
        && requestedDispatchPeriod !== normalizePortalPublicationDispatchPeriod(
          activePortalScopeRef.current.dispatchPeriod,
        )
      ) {
        showTargetToast({
          duration: 6000,
          message: "Le dispatch cible est indiqué dans cette section. Ouvrez-le explicitement avant de lancer Optimiser.",
          title: "Dispatch cible",
          type: "info",
        }, "refused");
        return;
      }
      const targetDispatchDate = normalizeInlineText(target.dispatch_date || target.dispatchDate);
      if (!targetDispatchDate) {
        showTargetToast({
          duration: 6000,
          message: "La cible du Chargé PM est incomplète. Réessayez la vérification M-à-J.",
          title: "Dispatch cible introuvable",
          type: "error",
        }, "failed");
        return;
      }
      const targetDispatchPeriod = normalizePortalPublicationDispatchPeriod(
        target.dispatch_period || target.dispatchPeriod,
      );
      writeLastDispatchContext({
        dispatch_date: targetDispatchDate,
        dispatch_month: targetDispatchDate.slice(0, 7),
        dispatch_period: targetDispatchPeriod,
      });
      const nextSearchParams = new URLSearchParams(location.search);
      nextSearchParams.set("dispatch_date", targetDispatchDate);
      nextSearchParams.set("dispatch_month", targetDispatchDate.slice(0, 7));
      nextSearchParams.set("dispatch_period", targetDispatchPeriod);
      nextSearchParams.delete("dispatchView");
      clearResultsDetailViewSearchParam(nextSearchParams);
      detailViewState?.activateView?.("fleet", {
        scroll: false,
        syncLocation: false,
      });
      navigate({
        pathname: location.pathname,
        search: `?${nextSearchParams.toString()}`,
      }, { replace: false });
      showTargetToast({
        duration: 6000,
        message: "Le dispatch cible est ouvert. Lancez Optimiser, puis vérifiez de nouveau M-à-J commandes.",
        title: "Dispatch cible",
        type: "info",
      }, "success");
      return;
    }

    const sourceChanges = getSourceChangesStateForPeriod(requestedDispatchPeriod);
    if (
      !canApplyDispatchSourceChanges
      || !["repair_link", "suspend_target", "repair_target_materialization"].includes(action)
      || dispatchReloadingRef.current || sourceChangesMutationInFlightRef.current
      || sourceChanges.isMutating
    ) {
      return;
    }
    const markerKey = normalizeInlineText(issue?.marker_key || issue?.markerKey);
    const dispatchDate = normalizeInlineText(sourceChanges.dispatchDate) || getSourceChangesDispatchDate();
    const dispatchPeriod = normalizePortalPublicationDispatchPeriod(
      sourceChanges.dispatchPeriod || requestedDispatchPeriod,
    );
    const integrity = sourceChanges.endOfDayLoadIntegrity
      || sourceChanges.end_of_day_load_integrity
      || {};
    const previewRevision = normalizeInlineText(
      integrity.preview_revision || integrity.previewRevision,
    );
    const sourceChangesPreviewIsFresh = isSourceChangesActionPreviewFresh(sourceChanges, {
      dispatchDate,
      dispatchPeriod,
      previewRevision,
    });
    const performanceContext = {
      action: "source_changes_resolve_end_of_day_load_integrity",
      cause: "source_changes_action",
      chainId: createPerformanceCorrelationId(),
      origin: "local",
    };
    let loadingToastId = null;
    const capture = createOperationFeedbackCapture({
      action: performanceContext.action, operationEventId: performanceContext.chainId,
      dispatchDate, dispatchPeriods: [dispatchPeriod], resourceKind: "order",
      resourceIds: [issue?.order_id || issue?.orderId].filter(Boolean), resourceLabel: markerKey,
      onProgress: (nextProgress) => { if (loadingToastId) updateToast(loadingToastId, { progress: nextProgress }); },
    });
    const reportProgress = capture.reportProgress;
    const showOperationToast = (toast) => showToast(capture.recordToast(toast));
    const updateOperationToast = (id, toast) => updateToast(id, capture.recordToast(toast));

    if (!markerKey || !dispatchDate || !sourceChangesPreviewIsFresh) {
      showOperationToast({
        duration: 6000,
        message: "Ouvrez M-à-J commandes et attendez la fin de la vérification avant cette action.",
        title: "Chargé PM",
        type: "warning",
      });
      capture.setStatus("refused");
      capture.finish();
      return;
    }

    const performanceHeaders = buildSourceChangesPerformanceHeaders(performanceContext);

    const isMaterializationRepair = action === "repair_target_materialization";
    const loadingTitle = isMaterializationRepair ? "Rétablissement du Chargé PM" : action === "repair_link" ? "Réparation du lien PM" : "Mise dans Suspendues";
    const loadingMessage = isMaterializationRepair
      ? "Nous reconstruisons la tournée avec le chargement PM d’origine."
      : action === "repair_link"
      ? "Nous validons puis réparons le lien Chargé PM."
      : "Nous validons puis retirons le Chargé PM du camion cible.";
    let integrityRequestAttempted = false;
    if (!setSourceChangesMutationBusy(true)) {
      capture.setStatus("refused");
      capture.finish();
      return null;
    }
    publishSourceChangesStatusNotice({
      message: loadingMessage,
      status: "running",
      title: loadingTitle,
    });
    try {
      loadingToastId = showOperationToast({
        message: loadingMessage,
        title: loadingTitle,
        type: "loading",
      });
      integrityRequestAttempted = true;
      const response = await fetchWithSupabaseAuth(
        buildDispatchDateQueryUrl(
          "/api/planning/source-changes/end-of-day-load-integrity/resolve",
          dispatchDate,
          dispatchPeriod,
        ),
        {
          body: JSON.stringify({
            marker_key: markerKey,
            action,
            preview_revision: previewRevision,
          }),
          headers: {
            "Content-Type": "application/json",
            "X-Planning-Diagnostics": "1",
            ...DEFERRED_SOURCE_CHANGES_HEADERS,
            ...performanceHeaders,
          },
          method: "POST",
        },
      );
      const payload = await parseJsonResponse(response, "Impossible de corriger le Chargé PM.");
      capture.markApplied();
      reportProgress("process", "completed");
      const publicationEffects = await synchronizeSourceChangePortalPublicationEffects([payload]);
      const successToast = await completeSourceChangeMutation({
        dispatchDate, dispatchPeriod, performanceContext, capture, payloads: [payload],
        toast: {
          duration: 5000,
          message: payload?.message || (
            isMaterializationRepair
              ? "Le chargement PM a été rétabli sur son camion."
              : action === "repair_link"
              ? "Le lien Chargé PM a été réparé."
              : "La commande a été retirée du camion cible et placée dans Suspendues."
          ),
          title: isMaterializationRepair ? "Chargé PM rétabli" : action === "repair_link" ? "Lien PM réparé" : "Commande suspendue",
          type: "success",
        },
      });
      if (loadingToastId) {
        updateOperationToast(loadingToastId, successToast);
      } else {
        showOperationToast(successToast);
      }
      publishSourceChangesStatusNotice({
        message: successToast.message,
        planningDiagnostics: payload?._planning_diagnostics || null,
        status: capture.getStatus() === "partial" ? "partial" : "completed",
        title: successToast.title,
      });
      const publicationInvalidationToast = buildPortalPublicationInvalidationToast(
        publicationEffects.invalidatedTruckIds,
      );
      if (publicationInvalidationToast) {
        showToast(publicationInvalidationToast);
      }
    } catch (error) {
      capture.fail(error);
      const errorToast = {
        duration: 7000,
        message: error instanceof Error && error.message
          ? error.message
          : "Impossible de corriger le Chargé PM.",
        title: loadingTitle,
        type: "error",
      };
      if (loadingToastId) {
        updateOperationToast(loadingToastId, errorToast);
      } else {
        showOperationToast(errorToast);
      }
      publishSourceChangesStatusNotice({
        message: errorToast.message,
        planningDiagnostics: error?.payload?._planning_diagnostics || null,
        status: capture.getStatus() === "partial" ? "partial" : "failed",
        title: errorToast.title,
      });
      const errorStatus = Number(error?.status) || 0;
      const shouldRefreshAfterError = [
        "end_of_day_load_integrity_issue_changed",
        "end_of_day_load_integrity_preview_stale",
        "end_of_day_load_integrity_repair_blocked",
      ].includes(normalizeInlineText(error?.code)) || (
        integrityRequestAttempted
        && (errorStatus === 0 || errorStatus >= 500)
      );
      if (shouldRefreshAfterError) {
        await refreshSourceChangesForPeriod(dispatchPeriod, { background: true, ...performanceContext, dispatchDate, onDiagnostic: (details) => capture.recordStepDetails("refresh", details) });
      }
    } finally {
      capture.finish();
      setSourceChangesMutationBusy(false);
    }
  }, [completeSourceChangeMutation,
    canApplyDispatchSourceChanges,
    commitSourceChangesMutationResponse,
    detailViewState,
    getSourceChangesDispatchDate,
    getSourceChangesStateForPeriod,
    location.pathname,
    location.search,
    navigate,
    refreshSourceChangesForPeriod,
    refreshVisibleSourceChangePeriods,
    setSourceChangesMutationBusy,
    showToast,
    synchronizeSourceChangePortalPublicationEffects,
    updateToast,
  ]);

  const resolveNonRenderableTripConflict = useCallback(async (
    conflict,
    requestedAction,
    suspendTarget = null,
    requestedPeriod = "",
  ) => {
    const action = normalizeInlineText(requestedAction).toLowerCase();
    const dispatchPeriod = normalizePortalPublicationDispatchPeriod(
      requestedPeriod || activePortalScopeRef.current.dispatchPeriod,
    );
    const sourceChanges = getSourceChangesStateForPeriod(dispatchPeriod);
    if (
      !canApplyDispatchSourceChanges
      || !canMutatePlanning
      || (action !== "repair" && action !== "suspend")
      || dispatchReloadingRef.current || sourceChangesMutationInFlightRef.current
      || sourceChanges.isMutating
    ) {
      return null;
    }

    const dispatchDate = normalizeInlineText(sourceChanges.dispatchDate) || getSourceChangesDispatchDate();
    const previewRevision = normalizeInlineText(sourceChanges.previewRevision);
    const runtimeRevision = normalizeInlineText(
      sourceChanges.runtimeRevision || sourceChanges.runtime_revision,
    );
    const conflictId = normalizeInlineText(conflict?.conflictId || conflict?.conflict_id);
    const conflictVersion = normalizeInlineText(conflict?.conflictVersion || conflict?.conflict_version);
    const targetId = normalizeInlineText(suspendTarget?.targetId || suspendTarget?.target_id);
    const orderId = normalizeInlineText(suspendTarget?.orderId || suspendTarget?.order_id);
    const previewIsFresh = isSourceChangesActionPreviewFresh(sourceChanges, {
      dispatchDate,
      dispatchPeriod,
      previewRevision,
    });
    const performanceContext = {
      action: action === "repair"
        ? "source_changes_repair_non_renderable_trip"
        : "source_changes_suspend_non_renderable_trip",
      cause: "source_changes_action",
      chainId: createPerformanceCorrelationId(),
      origin: "local",
    };
    let loadingToastId = null;
    const capture = createOperationFeedbackCapture({
      action: performanceContext.action, operationEventId: performanceContext.chainId,
      dispatchDate, dispatchPeriods: [dispatchPeriod], resourceKind: "truck",
      resourceIds: [conflict?.truckId || conflict?.truck_id].filter(Boolean), resourceLabel: conflict?.truckLabel || conflict?.truckName || conflictId,
      onProgress: (nextProgress) => { if (loadingToastId) updateToast(loadingToastId, { progress: nextProgress }); },
    });
    const reportProgress = capture.reportProgress;
    const showOperationToast = (toast) => showToast(capture.recordToast(toast));
    const updateOperationToast = (id, toast) => updateToast(id, capture.recordToast(toast));

    if (
      !dispatchDate
      || !previewRevision
      || !runtimeRevision
      || !conflictId
      || !conflictVersion
      || (action === "suspend" && !targetId)
      || !previewIsFresh
    ) {
      showOperationToast({
        duration: 6000,
        message: "Ouvrez Mise à jour et attendez la fin de la vérification avant cette action.",
        title: "Aperçu requis",
        type: "warning",
      });
      capture.setStatus("refused");
      if (!sourceChangesMutationInFlightRef.current) capture.finish();
      return null;
    }


    const loadingTitle = action === "repair" ? "Réparation de la tournée" : "Mise dans Suspendues";
    const loadingMessage = action === "repair"
      ? "Reconstruction de cette tournée seulement."
      : `Placement de ${orderId || "la commande"} dans Suspendues.`;
    if (!setSourceChangesMutationBusy(true)) {
      capture.setStatus("refused");
      capture.finish();
      return null;
    }
    publishSourceChangesStatusNotice({
      message: loadingMessage,
      status: "running",
      title: loadingTitle,
    });
    try {
      loadingToastId = showOperationToast({
        message: loadingMessage,
        title: loadingTitle,
        type: "loading",
      });
      const requestBody = {
        conflict_id: conflictId,
        conflict_version: conflictVersion,
        expected_runtime_revision: runtimeRevision,
        preview_revision: previewRevision,
        ...(action === "suspend" ? { target_id: targetId } : {}),
      };
      const endpoint = action === "repair"
        ? "/api/planning/source-changes/non-renderable-trips/repair"
        : "/api/planning/source-changes/non-renderable-trips/suspend";
      const response = await fetchWithSupabaseAuth(
        buildDispatchDateQueryUrl(endpoint, dispatchDate, dispatchPeriod),
        {
          body: JSON.stringify(requestBody),
          headers: {
            "Content-Type": "application/json",
            "X-Planning-Diagnostics": "1",
            ...DEFERRED_SOURCE_CHANGES_HEADERS,
            ...buildSourceChangesPerformanceHeaders(performanceContext),
          },
          method: "POST",
        },
      );
      const payload = await parseJsonResponse(
        response,
        action === "repair"
          ? "Réparation automatique impossible. Rien n’a été modifié."
          : "Impossible de placer la commande dans Suspendues. Rien n’a été modifié.",
      );
      capture.markApplied();
      reportProgress("process", "completed");

      const publicationEffects = await synchronizeSourceChangePortalPublicationEffects([payload]);
      const truckLabel = normalizeInlineText(
        conflict?.truckLabel || conflict?.truckName || conflict?.truckId || conflict?.truck_id,
      );
      const successMessage = action === "repair"
        ? `Tournée ${truckLabel || "du camion"} réparée.`
        : `${orderId || "Commande"} placée dans Suspendues.`;
      const successToast = await completeSourceChangeMutation({
        dispatchDate, dispatchPeriod, performanceContext, capture, payloads: [payload],
        periods: [dispatchPeriod],
        toast: {
          duration: 5000,
          message: successMessage,
          title: action === "repair" ? "Tournée réparée" : "Commande suspendue",
          type: "success",
          ...(action === "suspend" ? {
            actionLabel: "Ouvrir Suspendues",
            onAction: () => {
              detailViewState?.activateView?.("suspended", { historyMode: "push" });
            },
          } : {}),
        },
      });
      if (loadingToastId) {
        updateOperationToast(loadingToastId, successToast);
      } else {
        showOperationToast(successToast);
      }
      publishSourceChangesStatusNotice({
        message: successToast.message,
        planningDiagnostics: payload?._planning_diagnostics || null,
        status: capture.getStatus() === "partial" ? "partial" : "completed",
        title: successToast.title,
      });
      const publicationInvalidationToast = buildPortalPublicationInvalidationToast(
        publicationEffects.invalidatedTruckIds,
      );
      if (publicationInvalidationToast) {
        showToast(publicationInvalidationToast);
      }
      return payload;
    } catch (error) {
      capture.fail(error);
      const errorPresentation = resolveNonRenderableTripActionError(error, { action, orderId });
      const errorToast = {
        duration: 7000,
        message: errorPresentation.message,
        title: errorPresentation.title,
        type: errorPresentation.type,
      };
      if (loadingToastId) {
        updateOperationToast(loadingToastId, errorToast);
      } else {
        showOperationToast(errorToast);
      }
      publishSourceChangesStatusNotice({
        message: errorPresentation.message,
        planningDiagnostics: error?.payload?._planning_diagnostics || null,
        status: capture.getStatus() === "partial" ? "partial" : "failed",
        title: errorPresentation.title,
      });
      if (errorPresentation.refresh) {
        reportProgress("refresh");
        await capture.observeRefresh(() => refreshSourceChangesForPeriod(dispatchPeriod, {
          background: true,
          ...performanceContext, dispatchDate,
          onDiagnostic: (details) => capture.recordStepDetails("refresh", details),
        }));
      }
      if (error && typeof error === "object") {
        error.guidedRepairReasonCode = normalizeInlineText(
          error?.code
            || error?.payload?.reason_code
            || error?.payload?.detail?.reason_code,
        ).toLowerCase();
      }
      throw error;
    } finally {
      capture.finish();
      setSourceChangesMutationBusy(false);
    }
  }, [completeSourceChangeMutation,
    canApplyDispatchSourceChanges,
    canMutatePlanning,
    commitSourceChangesMutationResponse,
    detailViewState,
    getSourceChangesDispatchDate,
    getSourceChangesStateForPeriod,
    refreshSourceChangesForPeriod,
    setSourceChangesMutationBusy,
    showToast,
    synchronizeSourceChangePortalPublicationEffects,
    updateToast,
  ]);
  const saveCurrentDispatchSnapshot = useCallback(async () => {
    const dispatchDate = resolveCurrentDispatchDate();
    if (!dispatchDate || dispatchSnapshots.isSaving) {
      return;
    }
    setDispatchSnapshots((currentState) => ({ ...currentState, isSaving: true }));
    try {
      await planningMutation.createDispatchSnapshotSave({
        dispatchDate,
        rethrowOnError: true,
      });
      await refreshDispatchSnapshots();
    } catch {
      return null;
    } finally {
      setDispatchSnapshots((currentState) => ({ ...currentState, isSaving: false }));
    }
    return null;
  }, [dispatchSnapshots.isSaving, planningMutation, refreshDispatchSnapshots, resolveCurrentDispatchDate]);

  const restoreDispatchSnapshot = useCallback(async (snapshotId) => {
    const dispatchDate = resolveCurrentDispatchDate();
    const safeSnapshotId = normalizeInlineText(snapshotId);
    if (!canRestoreDispatchSnapshot || !dispatchDate || !safeSnapshotId || dispatchSnapshots.restoringSnapshotId) {
      return;
    }
    setDispatchSnapshots((currentState) => ({ ...currentState, restoringSnapshotId: safeSnapshotId }));
    try {
      let restorePreview = dispatchSnapshots.restorePreviewById?.[safeSnapshotId];
      const hasLoadedRestorePreview = restorePreview?.previewLoaded === true || restorePreview?.previewError === true;
      if (!hasLoadedRestorePreview) {
        try {
          restorePreview = await fetchDispatchSnapshotRestorePreview(safeSnapshotId, dispatchDate);
        } catch {
          restorePreview = buildDispatchRestorePreviewError();
        }
        const previewForState = restorePreview;
        setDispatchSnapshots((currentState) => ({
          ...currentState,
          restorePreviewById: {
            ...(currentState.restorePreviewById || {}),
            [safeSnapshotId]: previewForState,
          },
        }));
      }
      const relatedDispatchesPreview = restorePreview?.related_dispatches && typeof restorePreview.related_dispatches === "object"
        ? restorePreview.related_dispatches
        : null;
      await planningMutation.restoreDispatchSnapshot({
        confirmAddressRevalidation: restorePreview?.address_revalidation?.confirmation_required === true,
        confirmRelatedDispatches: relatedDispatchesPreview?.requires_confirmation === true,
        confirmRemovedEndOfDayLoads: relatedDispatchesPreview?.requires_removed_end_of_day_load_confirmation === true,
        dispatchDate,
        snapshotId: safeSnapshotId,
        rethrowOnError: true,
      });
      await refreshDispatchSnapshots();
      await refreshSourceChanges();
    } catch {
      return null;
    } finally {
      setDispatchSnapshots((currentState) => ({ ...currentState, restoringSnapshotId: "" }));
    }
    return null;
  }, [
    canRestoreDispatchSnapshot,
    dispatchSnapshots.restoringSnapshotId,
    dispatchSnapshots.restorePreviewById,
    planningMutation,
    refreshDispatchSnapshots,
    refreshSourceChanges,
    resolveCurrentDispatchDate,
  ]);

  const deleteDispatchSnapshot = useCallback(async (snapshotId) => {
    const dispatchDate = resolveCurrentDispatchDate();
    const safeSnapshotId = normalizeInlineText(snapshotId);
    if (!dispatchDate || !safeSnapshotId || dispatchSnapshots.deletingSnapshotId) {
      return;
    }
    setDispatchSnapshots((currentState) => ({ ...currentState, deletingSnapshotId: safeSnapshotId }));
    try {
      await planningMutation.deleteDispatchSnapshot({
        dispatchDate,
        snapshotId: safeSnapshotId,
        rethrowOnError: true,
      });
      await refreshDispatchSnapshots();
    } catch {
      return null;
    } finally {
      setDispatchSnapshots((currentState) => ({ ...currentState, deletingSnapshotId: "" }));
    }
    return null;
  }, [dispatchSnapshots.deletingSnapshotId, planningMutation, refreshDispatchSnapshots, resolveCurrentDispatchDate]);

  const dispatchSnapshotInteractions = useMemo(() => ({
    ...dispatchSnapshots,
    // Une sauvegarde sans période date d'avant le Soir : elle reste dans le Jour.
    items: dispatchSnapshots.items.filter((item) => (item?.dispatch_period || "day") === activePortalDispatchPeriod),
    canMutate: canSaveDispatchSnapshot || canRestoreDispatchSnapshot,
    canRestoreDispatchSnapshot,
    canSaveDispatchSnapshot,
    onRefresh: canSaveDispatchSnapshot || canRestoreDispatchSnapshot ? refreshDispatchSnapshots : undefined,
    onPreviewRestore: canRestoreDispatchSnapshot ? refreshDispatchRestorePreview : undefined,
    onRestore: canRestoreDispatchSnapshot ? restoreDispatchSnapshot : undefined,
    onSave: canSaveDispatchSnapshot ? saveCurrentDispatchSnapshot : undefined,
    onDelete: canSaveDispatchSnapshot ? deleteDispatchSnapshot : undefined,
  }), [
    activePortalDispatchPeriod,
    canRestoreDispatchSnapshot,
    canSaveDispatchSnapshot,
    deleteDispatchSnapshot,
    dispatchSnapshots,
    refreshDispatchSnapshots,
    refreshDispatchRestorePreview,
    restoreDispatchSnapshot,
    saveCurrentDispatchSnapshot,
  ]);

  const openSourceChangesForPeriod = useCallback((requestedPeriod = "day") => {
    const targetDispatchPeriod = normalizePortalPublicationDispatchPeriod(requestedPeriod);
    setSourceChangesOpenRequestedPeriod(targetDispatchPeriod);
    if (targetDispatchPeriod === activePortalDispatchPeriod) {
      return;
    }

    const nextSearchParams = new URLSearchParams(location.search || "");
    nextSearchParams.set("dispatch_period", targetDispatchPeriod);
    if (activePortalDispatchDate) {
      nextSearchParams.set("dispatch_date", activePortalDispatchDate);
      nextSearchParams.set("dispatch_month", activePortalDispatchDate.slice(0, 7));
    }
    writeLastDispatchContext({
      dispatch_date: activePortalDispatchDate,
      dispatch_month: activePortalDispatchDate ? activePortalDispatchDate.slice(0, 7) : "",
      dispatch_period: targetDispatchPeriod,
    });
    navigate({
      pathname: location.pathname,
      search: nextSearchParams.toString() ? `?${nextSearchParams.toString()}` : "",
    }, { replace: false });
  }, [activePortalDispatchDate, activePortalDispatchPeriod, location.pathname, location.search, navigate]);
  const consumeSourceChangesOpenRequest = useCallback((consumedPeriod = "") => {
    const normalizedConsumedPeriod = normalizePortalPublicationDispatchPeriod(consumedPeriod);
    setSourceChangesOpenRequestedPeriod((currentPeriod) => (
      currentPeriod === normalizedConsumedPeriod ? "" : currentPeriod
    ));
  }, []);
  const storeVerifiedDaySourceChangesPendingCount = useCallback((pendingCount) => {
    const normalizedPendingCount = Number(pendingCount);
    if (!Number.isFinite(normalizedPendingCount)) {
      return;
    }
    const safePendingCount = Math.max(0, Math.round(normalizedPendingCount));
    setDaySourceChanges((currentState) => {
      if (
        currentState.hasVerified !== true
        || currentState.needsVerification === true
        || !currentState.dispatchDate
      ) {
        return currentState;
      }
      sourceChangesAggregateCache.write({
        dispatchDate: currentState.dispatchDate,
        dispatchPeriod: "day",
      }, safePendingCount);
      if (currentState.lastVerifiedPendingCount === safePendingCount) {
        return currentState;
      }
      return {
        ...currentState,
        lastVerifiedPendingCount: safePendingCount,
      };
    });
  }, []);
  const storeVerifiedEveningSourceChangesPendingCount = useCallback((pendingCount) => {
    const normalizedPendingCount = Number(pendingCount);
    if (!Number.isFinite(normalizedPendingCount)) {
      return;
    }
    const safePendingCount = Math.max(0, Math.round(normalizedPendingCount));
    setEveningSourceChanges((currentState) => {
      if (
        currentState.hasVerified !== true
        || currentState.needsVerification === true
        || !currentState.dispatchDate
      ) {
        return currentState;
      }
      sourceChangesAggregateCache.write({
        dispatchDate: currentState.dispatchDate,
        dispatchPeriod: "evening",
      }, safePendingCount);
      if (currentState.lastVerifiedPendingCount === safePendingCount) {
        return currentState;
      }
      return {
        ...currentState,
        lastVerifiedPendingCount: safePendingCount,
      };
    });
  }, []);

  const sourceChangesInteractions = useMemo(() => ({
    ...sourceChanges,
    isMutating: isDispatchReloading || sourceChanges.isMutating || Boolean(metadataSyncActivityMessage),
    activeScopeDispatchDate: activePortalDispatchDate,
    activeScopeDispatchPeriod: activePortalDispatchPeriod,
    canMutate: canApplyDispatchSourceChanges,
    openRequestedPeriod: sourceChangesOpenRequestedPeriod,
    onAcknowledgeProtected: canApplyDispatchSourceChanges
      ? acknowledgeProtectedSourceChange
      : undefined,
    onImportNew: canApplyDispatchSourceChanges ? () => applySourceChangeMutation("import-new") : undefined,
    onImportSuspendableErrors: canApplyDispatchSourceChanges ? () => applySourceChangeMutation("import-suspendable-errors") : undefined,
    onOpen: canApplyDispatchSourceChanges ? verifySourceChangesOnOpen : undefined,
    onOpenForPeriod: canApplyDispatchSourceChanges ? openSourceChangesForPeriod : undefined,
    onOpenRequestConsumed: consumeSourceChangesOpenRequest,
    onRemoveDateChanged: canApplyDispatchSourceChanges ? () => applySourceChangeMutation("remove-date-changed") : undefined,
    onRemoveModified: canApplyDispatchSourceChanges ? () => applySourceChangeMutation("remove-modified") : undefined,
    onRepairGhostOrders: canApplyDispatchSourceChanges ? () => applySourceChangeMutation("repair-ghost-orders") : undefined,
    onOpenTruck: openSourceChangeTruck,
    onRecoverTruck: canApplyDispatchSourceChanges && canMutatePlanning
      ? recoverSourceChangeTruck
      : undefined,
    onRejectTruck: canApplyDispatchSourceChanges ? rejectSourceChangeTruck : undefined,
    onRejectLoadingConstraints: canApplyDispatchSourceChanges
      ? rejectLoadingConstraintTruck
      : undefined,
    onRefreshLoadingConstraints: canApplyDispatchSourceChanges
      ? refreshLoadingConstraintTruck
      : undefined,
    onRefreshAllLoadingConstraints: canApplyDispatchSourceChanges ? () => refreshAllLoadingConstraints() : undefined,
    onRefreshTrucks: canApplyDispatchSourceChanges ? refreshSourceChangeTrucks : undefined,
    onRetryTruck: canApplyDispatchSourceChanges ? retrySourceChangeTruck : undefined,
    onResolveNonRenderableTrip: canApplyDispatchSourceChanges && canMutatePlanning
      ? resolveNonRenderableTripConflict
      : undefined,
    onRestoreExecutions: canApplyDispatchSourceChanges ? () => applySourceChangeMutation("restore-executions") : undefined,
    onResolveEndOfDayLoadIntegrity: canApplyDispatchSourceChanges
      ? resolveEndOfDayLoadIntegrity
      : undefined,
    onRefresh: canApplyDispatchSourceChanges ? retrySourceChangesVerification : undefined,
    onVerifiedPendingCount: storeVerifiedSourceChangesPendingCount,
  }), [
    applySourceChangeMutation,
    activePortalDispatchDate,
    activePortalDispatchPeriod,
    acknowledgeProtectedSourceChange,
    canApplyDispatchSourceChanges,
    canMutatePlanning,
    consumeSourceChangesOpenRequest,
    openSourceChangesForPeriod,
    openSourceChangeTruck,
    recoverSourceChangeTruck,
    rejectLoadingConstraintTruck,
    refreshAllLoadingConstraints,
    refreshLoadingConstraintTruck,
    rejectSourceChangeTruck,
    refreshSourceChangeTrucks,
    refreshSourceChanges,
    retrySourceChangesVerification,
    resolveEndOfDayLoadIntegrity,
    resolveNonRenderableTripConflict,
    retrySourceChangeTruck,
    sourceChanges,
    metadataSyncActivityMessage,
    isDispatchReloading,
    sourceChangesOpenRequestedPeriod,
    storeVerifiedSourceChangesPendingCount,
    verifySourceChangesOnOpen,
  ]);
  const daySourceChangesInteractions = useMemo(() => ({
    ...daySourceChanges,
    isMutating: isDispatchReloading || daySourceChanges.isMutating || Boolean(metadataSyncActivityMessage),
    activeScopeDispatchDate: activePortalDispatchDate,
    activeScopeDispatchPeriod: "day",
    canMutate: canApplyDispatchSourceChanges,
    onAcknowledgeProtected: canApplyDispatchSourceChanges
      ? (detail) => acknowledgeProtectedSourceChange(detail, "day")
      : undefined,
    onImportNew: canApplyDispatchSourceChanges
      ? () => applySourceChangeMutation("import-new", "day")
      : undefined,
    onImportSuspendableErrors: canApplyDispatchSourceChanges
      ? () => applySourceChangeMutation("import-suspendable-errors", "day")
      : undefined,
    onOpen: canApplyDispatchSourceChanges ? verifyDaySourceChangesOnOpen : undefined,
    onRemoveDateChanged: canApplyDispatchSourceChanges
      ? () => applySourceChangeMutation("remove-date-changed", "day")
      : undefined,
    onRemoveModified: canApplyDispatchSourceChanges
      ? () => applySourceChangeMutation("remove-modified", "day")
      : undefined,
    onRepairGhostOrders: canApplyDispatchSourceChanges
      ? () => applySourceChangeMutation("repair-ghost-orders", "day")
      : undefined,
    onOpenTruck: openSourceChangeTruck,
    onRecoverTruck: canApplyDispatchSourceChanges && canMutatePlanning
      ? (selection) => recoverSourceChangeTruck(selection, "day")
      : undefined,
    onRejectTruck: canApplyDispatchSourceChanges
      ? (selection) => rejectSourceChangeTruck(selection, "day")
      : undefined,
    onRejectLoadingConstraints: canApplyDispatchSourceChanges
      ? (detail) => rejectLoadingConstraintTruck(detail, "day")
      : undefined,
    onRefreshLoadingConstraints: canApplyDispatchSourceChanges
      ? (detail) => refreshLoadingConstraintTruck(detail, "day")
      : undefined,
    onRefreshAllLoadingConstraints: canApplyDispatchSourceChanges ? () => refreshAllLoadingConstraints("day") : undefined,
    onRefreshTrucks: canApplyDispatchSourceChanges
      ? (selection) => refreshSourceChangeTrucks(selection, "day")
      : undefined,
    onRetryTruck: canApplyDispatchSourceChanges
      ? (detail) => retrySourceChangeTruck(detail, "day")
      : undefined,
    onResolveNonRenderableTrip: canApplyDispatchSourceChanges && canMutatePlanning
      ? (conflict, action, target) => resolveNonRenderableTripConflict(
        conflict,
        action,
        target,
        "day",
      )
      : undefined,
    onRestoreExecutions: canApplyDispatchSourceChanges
      ? () => applySourceChangeMutation("restore-executions", "day")
      : undefined,
    onResolveEndOfDayLoadIntegrity: canApplyDispatchSourceChanges
      ? (issue, action) => resolveEndOfDayLoadIntegrity(issue, action, "day")
      : undefined,
    onRefresh: canApplyDispatchSourceChanges ? retryDaySourceChangesVerification : undefined,
    onVerifiedPendingCount: storeVerifiedDaySourceChangesPendingCount,
  }), [
    acknowledgeProtectedSourceChange,
    activePortalDispatchDate,
    applySourceChangeMutation,
    canApplyDispatchSourceChanges,
    canMutatePlanning,
    daySourceChanges,
    metadataSyncActivityMessage,
    isDispatchReloading,
    openSourceChangeTruck,
    recoverSourceChangeTruck,
    rejectLoadingConstraintTruck,
    refreshAllLoadingConstraints,
    refreshLoadingConstraintTruck,
    rejectSourceChangeTruck,
    refreshSourceChangeTrucks,
    resolveEndOfDayLoadIntegrity,
    resolveNonRenderableTripConflict,
    retrySourceChangeTruck,
    retryDaySourceChangesVerification,
    storeVerifiedDaySourceChangesPendingCount,
    verifyDaySourceChangesOnOpen,
  ]);
  const eveningSourceChangesInteractions = useMemo(() => ({
    ...eveningSourceChanges,
    isMutating: isDispatchReloading || eveningSourceChanges.isMutating || Boolean(metadataSyncActivityMessage),
    activeScopeDispatchDate: activePortalDispatchDate,
    activeScopeDispatchPeriod: "evening",
    canMutate: canApplyDispatchSourceChanges,
    onAcknowledgeProtected: canApplyDispatchSourceChanges
      ? (detail) => acknowledgeProtectedSourceChange(detail, "evening")
      : undefined,
    onEnsure: canApplyDispatchSourceChanges ? ensureEveningSourceChanges : undefined,
    onImportNew: canApplyDispatchSourceChanges
      ? () => applySourceChangeMutation("import-new", "evening")
      : undefined,
    onImportSuspendableErrors: canApplyDispatchSourceChanges
      ? () => applySourceChangeMutation("import-suspendable-errors", "evening")
      : undefined,
    onOpen: canApplyDispatchSourceChanges ? verifyEveningSourceChangesOnOpen : undefined,
    onRemoveDateChanged: canApplyDispatchSourceChanges
      ? () => applySourceChangeMutation("remove-date-changed", "evening")
      : undefined,
    onRemoveModified: canApplyDispatchSourceChanges
      ? () => applySourceChangeMutation("remove-modified", "evening")
      : undefined,
    onRepairGhostOrders: canApplyDispatchSourceChanges
      ? () => applySourceChangeMutation("repair-ghost-orders", "evening")
      : undefined,
    onOpenTruck: openSourceChangeTruck,
    onRecoverTruck: canApplyDispatchSourceChanges && canMutatePlanning
      ? (selection) => recoverSourceChangeTruck(selection, "evening")
      : undefined,
    onRejectTruck: canApplyDispatchSourceChanges
      ? (selection) => rejectSourceChangeTruck(selection, "evening")
      : undefined,
    onRejectLoadingConstraints: canApplyDispatchSourceChanges
      ? (detail) => rejectLoadingConstraintTruck(detail, "evening")
      : undefined,
    onRefreshLoadingConstraints: canApplyDispatchSourceChanges
      ? (detail) => refreshLoadingConstraintTruck(detail, "evening")
      : undefined,
    onRefreshAllLoadingConstraints: canApplyDispatchSourceChanges ? () => refreshAllLoadingConstraints("evening") : undefined,
    onRefreshTrucks: canApplyDispatchSourceChanges
      ? (selection) => refreshSourceChangeTrucks(selection, "evening")
      : undefined,
    onRetryTruck: canApplyDispatchSourceChanges
      ? (detail) => retrySourceChangeTruck(detail, "evening")
      : undefined,
    onResolveNonRenderableTrip: canApplyDispatchSourceChanges && canMutatePlanning
      ? (conflict, action, target) => resolveNonRenderableTripConflict(
        conflict,
        action,
        target,
        "evening",
      )
      : undefined,
    onRestoreExecutions: canApplyDispatchSourceChanges
      ? () => applySourceChangeMutation("restore-executions", "evening")
      : undefined,
    onResolveEndOfDayLoadIntegrity: canApplyDispatchSourceChanges
      ? (issue, action) => resolveEndOfDayLoadIntegrity(issue, action, "evening")
      : undefined,
    onRefresh: canApplyDispatchSourceChanges ? retryEveningSourceChangesVerification : undefined,
    onVerifiedPendingCount: storeVerifiedEveningSourceChangesPendingCount,
  }), [
    acknowledgeProtectedSourceChange,
    activePortalDispatchDate,
    applySourceChangeMutation,
    canApplyDispatchSourceChanges,
    canMutatePlanning,
    eveningSourceChanges,
    metadataSyncActivityMessage,
    isDispatchReloading,
    ensureEveningSourceChanges,
    openSourceChangeTruck,
    recoverSourceChangeTruck,
    rejectLoadingConstraintTruck,
    refreshAllLoadingConstraints,
    refreshLoadingConstraintTruck,
    rejectSourceChangeTruck,
    refreshEveningSourceChanges,
    refreshSourceChangeTrucks,
    resolveEndOfDayLoadIntegrity,
    resolveNonRenderableTripConflict,
    retrySourceChangeTruck,
    retryEveningSourceChangesVerification,
    storeVerifiedEveningSourceChangesPendingCount,
    verifyEveningSourceChangesOnOpen,
  ]);
  const sourceChangesByPeriod = useMemo(() => {
    const activePeriod = normalizePortalPublicationDispatchPeriod(activePortalDispatchPeriod);
    return {
      day: activePeriod === "day"
        ? sourceChangesInteractions
        : daySourceChangesInteractions,
      evening: activePeriod === "evening"
        ? sourceChangesInteractions
        : eveningSourceChangesInteractions,
    };
  }, [activePortalDispatchPeriod, daySourceChangesInteractions, eveningSourceChangesInteractions, sourceChangesInteractions]);

  const workspaceInteractionsWithSourceChanges = useMemo(() => ({
    ...workspaceInteractions,
    chrome: {
      ...(workspaceInteractions.chrome || {}),
      dispatchSnapshots: dispatchSnapshotInteractions,
      sourceChanges: sourceChangesInteractions,
      sourceChangesByPeriod,
    },
    dispatchSnapshots: dispatchSnapshotInteractions,
    sourceChanges: sourceChangesInteractions,
    sourceChangesByPeriod,
  }), [dispatchSnapshotInteractions, sourceChangesByPeriod, sourceChangesInteractions, workspaceInteractions]);

  interactionsRef.current = workspaceInteractionsWithSourceChanges;

  const sourceChangesSyncKey = useMemo(() => JSON.stringify({
    isDispatchReloading,
    counts: sourceChanges.counts,
    changeDomains: sourceChanges.changeDomains,
    dispatchPeriod: sourceChanges.dispatchPeriod,
    endOfDayLoadIntegrity: sourceChanges.endOfDayLoadIntegrity,
    errorMessage: sourceChanges.errorMessage,
    hasVerified: sourceChanges.hasVerified,
    metadataSync: sourceChanges.metadataSync,
    loadingConstraintChanges: sourceChanges.loadingConstraintChanges,
    compartmentFillViolations: sourceChanges.compartmentFillViolations,
    mutationPreviewRevision: sourceChanges.mutationPreviewRevision,
    previewRevision: sourceChanges.previewRevision,
    runtimeRevision: sourceChanges.runtimeRevision,
    resolution: sourceChanges.resolution,
    sourceChangeDetails: sourceChanges.details,
    staleCoverage: sourceChanges.staleCoverage,
    suspendableErrors: sourceChanges.suspendableErrors,
    truckOutputPolicy: sourceChanges.truckOutputPolicy,
    truckChanges: sourceChanges.truckChanges,
    dayResolution: daySourceChanges.resolution,
    dayLoadingConstraintChanges: daySourceChanges.loadingConstraintChanges,
    dayStaleCoverage: daySourceChanges.staleCoverage,
    dayTruckChanges: daySourceChanges.truckChanges,
    eveningResolution: eveningSourceChanges.resolution,
    eveningLoadingConstraintChanges: eveningSourceChanges.loadingConstraintChanges,
    eveningStaleCoverage: eveningSourceChanges.staleCoverage,
    eveningTruckChanges: eveningSourceChanges.truckChanges,
    isLoading: sourceChanges.isLoading,
    isMutating: sourceChanges.isMutating,
    isVerifying: sourceChanges.isVerifying,
    lastVerifiedPendingCount: sourceChanges.lastVerifiedPendingCount,
    needsPlanningContextVerification: sourceChanges.needsPlanningContextVerification,
    needsVerification: sourceChanges.needsVerification,
    planningContextVerificationErrorMessage: sourceChanges.planningContextVerificationErrorMessage,
    planningContextVerificationVersion: sourceChanges.planningContextVerificationVersion,
  }), [
    isDispatchReloading,
    sourceChanges.counts,
    sourceChanges.dispatchPeriod,
    sourceChanges.changeDomains,
    sourceChanges.endOfDayLoadIntegrity,
    sourceChanges.errorMessage,
    sourceChanges.hasVerified,
    sourceChanges.isLoading,
    sourceChanges.metadataSync,
    sourceChanges.mutationPreviewRevision,
    sourceChanges.previewRevision,
    sourceChanges.runtimeRevision,
    sourceChanges.resolution,
    sourceChanges.staleCoverage,
    sourceChanges.details,
    sourceChanges.isMutating,
    sourceChanges.isVerifying,
    sourceChanges.lastVerifiedPendingCount,
    sourceChanges.needsPlanningContextVerification,
    sourceChanges.needsVerification,
    sourceChanges.planningContextVerificationErrorMessage,
    sourceChanges.planningContextVerificationVersion,
    sourceChanges.suspendableErrors,
    sourceChanges.truckOutputPolicy,
    sourceChanges.truckChanges,
    daySourceChanges.resolution,
    daySourceChanges.staleCoverage,
    daySourceChanges.truckChanges,
    eveningSourceChanges.resolution,
    eveningSourceChanges.staleCoverage,
    eveningSourceChanges.truckChanges,
  ]);

  const dispatchSnapshotsSyncKey = useMemo(() => JSON.stringify({
    dispatchDate: dispatchSnapshots.dispatchDate,
    itemIds: dispatchSnapshots.items.map((item) => normalizeInlineText(item?.id)).join("|"),
    isLoading: dispatchSnapshots.isLoading,
    isSaving: dispatchSnapshots.isSaving,
    previewingSnapshotId: dispatchSnapshots.previewingSnapshotId,
    restoringSnapshotId: dispatchSnapshots.restoringSnapshotId,
    restorePreviewKey: Object.entries(dispatchSnapshots.restorePreviewById || {})
      .map(([previewId, preview]) => {
        const counts = preview?.counts && typeof preview.counts === "object" ? preview.counts : {};
        const details = preview?.details && typeof preview.details === "object" ? preview.details : {};
        const relatedDispatches = preview?.related_dispatches && typeof preview.related_dispatches === "object"
          ? preview.related_dispatches
          : {};
        const affectedDates = Array.isArray(relatedDispatches.affected_dispatch_dates)
          ? relatedDispatches.affected_dispatch_dates.map(normalizeInlineText).filter(Boolean).join(",")
          : "";
        const endOfDayLoadKeys = Array.isArray(relatedDispatches.end_of_day_loads)
          ? relatedDispatches.end_of_day_loads.map((load) => normalizeInlineText(load?.marker_key)).filter(Boolean).join(",")
          : "";
        const newIds = Array.isArray(details.new)
          ? details.new.map((detail) => normalizeInlineText(detail?.order_id || detail?.delivery_id)).join(",")
          : "";
        const removedIds = Array.isArray(details.date_changed_remove)
          ? details.date_changed_remove.map((detail) => normalizeInlineText(detail?.order_id || detail?.delivery_id)).join(",")
          : "";
        const addressRevalidation = preview?.address_revalidation && typeof preview.address_revalidation === "object"
          ? preview.address_revalidation
          : {};
        const addressCounts = addressRevalidation.counts && typeof addressRevalidation.counts === "object"
          ? addressRevalidation.counts
          : {};
        const addressDetails = Array.isArray(addressRevalidation.details)
          ? addressRevalidation.details.map((detail) => normalizeInlineText(detail?.order_id || detail?.delivery_id)).join(",")
          : "";
        return [
          previewId,
          Number(counts.new || 0),
          Number(counts.date_changed_remove || 0),
          Number(counts.total || 0),
          preview?.previewLoaded === true ? 1 : 0,
          relatedDispatches.requires_confirmation === true ? 1 : 0,
          affectedDates,
          endOfDayLoadKeys,
          newIds,
          removedIds,
          Number(addressCounts.kept_as_routing_errors || 0),
          Number(addressCounts.changed_valid_confirmation_required || 0),
          addressRevalidation.confirmation_required === true ? 1 : 0,
          addressDetails,
        ].join(":");
      })
      .join("|"),
  }), [
    dispatchSnapshots.dispatchDate,
    dispatchSnapshots.isLoading,
    dispatchSnapshots.isSaving,
    dispatchSnapshots.items,
    dispatchSnapshots.previewingSnapshotId,
    dispatchSnapshots.restoringSnapshotId,
    dispatchSnapshots.restorePreviewById,
  ]);

  useEffect(() => {
    if (sourceChangesSyncKeyRef.current === sourceChangesSyncKey) {
      return;
    }
    sourceChangesSyncKeyRef.current = sourceChangesSyncKey;
    syncWorkspaceUi();
  }, [sourceChangesSyncKey, syncWorkspaceUi]);

  useEffect(() => {
    if (dispatchSnapshotsSyncKeyRef.current === dispatchSnapshotsSyncKey) {
      return;
    }
    dispatchSnapshotsSyncKeyRef.current = dispatchSnapshotsSyncKey;
    syncWorkspaceUi();
  }, [dispatchSnapshotsSyncKey, syncWorkspaceUi]);

  return {
    planningMutation,
    workspaceInteractions: workspaceInteractionsWithSourceChanges,
  };
}
